/**
 * Changelog entries — newest first.
 *
 * Each entry has a version string (matching the API build number or semver)
 * and a list of changes grouped by type.
 *
 * whether the "What's New" badge should be shown.
 */

export interface ChangelogChange {
  type: 'feature' | 'improvement' | 'fix'
  text: string
  detail?: string
  /** Optional translations by language code. German (text/detail) is the fallback. */
  translations?: Record<string, { text: string; detail?: string }>
}

export interface ChangelogEntry {
  version: string
  date: string
  changes: ChangelogChange[]
}

/**
 * The `LATEST_CHANGELOG_VERSION` is compared against localStorage to determine
 * whether the "What's New" badge should be shown.
 */
export const LATEST_CHANGELOG_VERSION = 'v1.14.701'

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: 'v1.14.701',
    date: '3. November 2026',
    changes: [
      {
        type: 'feature',
        text: 'Entwickler-Zugang beantragen: Kunden-IT registriert Firma und Kontaktperson selbst, bestätigt die E-Mail-Adresse und legt danach API-Keys an.',
        translations: {
          en: {
            text: 'Request developer access: customer IT registers company and contact person itself, confirms the e-mail address and then creates API keys.',
          },
          fr: {
            text: "Demander un accès développeur : l'informatique du client enregistre elle-même l'entreprise et la personne de contact, confirme l'adresse e-mail puis crée des clés API.",
          },
          it: {
            text: "Richiedi accesso sviluppatore: l'IT del cliente registra autonomamente azienda e persona di contatto, conferma l'indirizzo e-mail e poi crea le chiavi API.",
          },
        },
      },
      {
        type: 'improvement',
        text: 'Entwickler-Zugang beantragen: Ist die Registrierung auf der Umgebung noch nicht freigeschaltet, erscheint ein verständlicher Hinweis statt einer allgemeinen Fehlermeldung.',
        translations: {
          en: {
            text: 'Request developer access: if sign-up is not yet enabled on the environment, a clear notice appears instead of a generic error message.',
          },
          fr: {
            text: "Demander un accès développeur : si l'inscription n'est pas encore activée sur l'environnement, un message explicite s'affiche au lieu d'une erreur générique.",
          },
          it: {
            text: "Richiedi accesso sviluppatore: se la registrazione non è ancora attiva sull'ambiente, viene mostrato un avviso comprensibile invece di un errore generico.",
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.700',
    date: '3. November 2026',
    changes: [
      {
        type: 'feature',
        text: 'Mandant-Detail › Entwickler-Nutzung: Guthaben der Kundenfirma einsehen und Aufladungen auf Rechnung oder Korrekturen buchen.',
        translations: {
          en: {
            text: 'Tenant detail › Developer usage: view the customer company credit and book top-ups by invoice or corrections.',
          },
          fr: {
            text: 'Détail du mandant › Utilisation développeur: consulter le crédit de l’entreprise cliente et comptabiliser des recharges sur facture ou des corrections.',
          },
          it: {
            text: 'Dettaglio mandante › Utilizzo sviluppatore: consultare il credito dell’azienda cliente e registrare ricariche su fattura o correzioni.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.699',
    date: '3. November 2026',
    changes: [
      {
        type: 'feature',
        text: 'Entwickler › Guthaben: Saldo und Buchungen einsehen, Guthaben per Kreditkarte oder Twint aufladen, Quittung als PDF herunterladen.',
        translations: {
          en: {
            text: 'Developer › Credit: view balance and bookings, top up by credit card or Twint, download the receipt as PDF.',
          },
          fr: {
            text: 'Développeur › Crédit: consulter le solde et les écritures, recharger par carte de crédit ou Twint, télécharger le reçu en PDF.',
          },
          it: {
            text: 'Sviluppatore › Credito: consultare saldo e movimenti, ricaricare con carta di credito o Twint, scaricare la ricevuta in PDF.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.698',
    date: '3. November 2026',
    changes: [
      {
        type: 'feature',
        text: 'Objekt: «Vollanalyse starten» übergibt das Objekt dem Objekt-Agenten; Fortschritt, offene Punkte und Prüfbericht erscheinen direkt auf der Objektseite.',
        translations: {
          en: {
            text: 'Property: «Start full analysis» hands the property to the property agent; progress, open points and the report appear right on the property page.',
          },
          fr: {
            text: 'Objet: «Lancer l\'analyse complète» confie l\'objet à l\'agent objet; progression, points ouverts et rapport apparaissent directement sur la page de l\'objet.',
          },
          it: {
            text: 'Oggetto: «Avvia analisi completa» affida l\'oggetto all\'agente oggetto; avanzamento, punti aperti e rapporto compaiono direttamente nella pagina dell\'oggetto.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.697',
    date: '31. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Entwickler › Nutzung: Abrufe und Kosten je Monat mit CSV-Export; im Mandant-Detail sehen Administratoren die Entwickler-Nutzung je Kundenfirma.',
        translations: {
          en: {
            text: 'Developer › Usage: calls and costs per month with CSV export; administrators see developer usage per customer company in the Mandant detail.',
          },
          fr: {
            text: 'Développeur › Utilisation: appels et coûts par mois avec export CSV; les administrateurs voient l\'utilisation développeur par entreprise cliente dans le détail du mandant.',
          },
          it: {
            text: 'Sviluppatore › Utilizzo: chiamate e costi per mese con esportazione CSV; gli amministratori vedono l\'utilizzo sviluppatore per azienda cliente nel dettaglio del mandante.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.696',
    date: '31. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Neue Rolle «Entwickler»: Kunden-IT erstellt eigene API-Keys und verbindet ihren KI-Assistenten mit Immotron — Seite «Entwickler» mit Anleitung.',
        translations: {
          en: {
            text: 'New role «Developer»: customer IT creates its own API keys and connects its AI assistant to Immotron — «Developer» page with guide.',
          },
          fr: {
            text: 'Nouveau rôle «Développeur»: l\'IT du client crée ses propres clés API et connecte son assistant IA à Immotron — page «Développeur» avec guide.',
          },
          it: {
            text: 'Nuovo ruolo «Sviluppatore»: l\'IT del cliente crea le proprie chiavi API e collega il suo assistente IA a Immotron — pagina «Sviluppatore» con guida.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.695',
    date: '29. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation: Balken und «Kandidaten» der Karte «Immobilien · Eigenbestand» sind anklickbar und öffnen die passenden Objekte im Reiter Immobilien.',
        translations: {
          en: {
            text: 'Organisation: bars and “Candidates” on the “Properties · Own portfolio” card are clickable and open the matching properties in the Properties tab.',
          },
          fr: {
            text: 'Organisation : les barres et « Candidats » de la carte « Immeubles · Portefeuille propre » sont cliquables et ouvrent les objets correspondants dans l’onglet Immeubles.',
          },
          it: {
            text: 'Organizzazione: le barre e «Candidati» della scheda «Immobili · Portafoglio proprio» sono cliccabili e aprono gli oggetti corrispondenti nella scheda Immobili.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.694',
    date: '29. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation, Reiter Immobilien: lässt sich per Link nach Nutzung, Kanton oder Bereinigungsgrund filtern — mit Filter-Chip zum Aufheben.',
        translations: {
          en: {
            text: 'Organisation, Properties tab: can be filtered via link by use, canton or clean-up reason — with a filter chip to clear.',
          },
          fr: {
            text: 'Organisation, onglet Immeubles : filtrable par lien selon usage, canton ou motif d’assainissement — avec puce de filtre.',
          },
          it: {
            text: 'Organizzazione, scheda Immobili: filtrabile tramite link per uso, cantone o motivo di pulizia — con chip di filtro.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.693',
    date: '29. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation: Fonds-Monitor erklärt LTV, Limite, Agio/Disagio, Leerstand und Anlässe per ⓘ — mit Quelle und Stand.',
        translations: {
          en: {
            text: 'Organisation: the fund monitor explains LTV, limit, premium/discount, vacancy and signals via ⓘ — with source and date.',
          },
          fr: {
            text: 'Organisation : le moniteur de fonds explique LTV, limite, agio/disagio, vacance et signaux via ⓘ — avec source et date.',
          },
          it: {
            text: 'Organizzazione: il monitor fondi spiega LTV, limite, agio/disagio, sfitto e segnali tramite ⓘ — con fonte e data.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.692',
    date: '29. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation: Kennzahlen der Karte «Immobilien · Eigenbestand» erklären sich per ⓘ — was die Zahl heisst, wie sie zustande kommt, Quelle und Stand.',
        translations: {
          en: {
            text: 'Organisation: the figures on the “Properties · Own portfolio” card now explain themselves via ⓘ — meaning, calculation, source and date.',
          },
          fr: {
            text: 'Organisation : les chiffres de la carte « Immeubles · Portefeuille propre » s’expliquent via ⓘ — signification, calcul, source et date.',
          },
          it: {
            text: 'Organizzazione: le cifre della scheda «Immobili · Portafoglio proprio» si spiegano tramite ⓘ — significato, calcolo, fonte e data.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.691',
    date: '29. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Firmenseite: Dokumente der Personen einer Firma erscheinen jetzt auch unter Dokumente der Firma.',
      },
    ],
  },
  {
    version: 'v1.14.690',
    date: '29. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Firmenseite: Aktivitäten der Personen einer Firma erscheinen jetzt auch auf der Firma, mit Name und Filter nach Person.',
      },
    ],
  },
  {
    version: 'v1.14.689',
    date: '28. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Was ist neu: interne Einträge zur Testumgebung wurden aus der Liste entfernt — sie zeigt nur noch Änderungen, die für die Nutzung der Plattform relevant sind.',
      },
    ],
  },
  {
    version: 'v1.14.688',
    date: '28. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'CRM › Fondsradar: alle Anlagevehikel mit Verkaufsdruck und Kaufkraft auf einer Seite — Filter Kauft/Verkauft, Klick führt zur Organisation.',
      },
    ],
  },
  {
    version: 'v1.14.686',
    date: '27. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisationsprofil: neuer Chip zeigt, woher die UID stammt — Handelsregister, UID-Register des Bundes oder manuell erfasst.',
      },
    ],
  },
  {
    version: 'v1.14.685',
    date: '27. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Dokumente: Kategorien NDA, NBO, BO und Korrespondenz; eigene Kategorien wie «Fondsberichte» werden lesbar angezeigt und sind filterbar.',
      },
      {
        type: 'feature',
        text: 'Dokumente: Kategorie beim Hochladen wählen und in der Liste nachträglich ändern.',
      },
    ],
  },
  {
    version: 'v1.14.684',
    date: '27. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Dokument-Vorschau: PDFs werden jetzt direkt in der App gezeichnet und sind damit auch auf dem Handy sichtbar; neuer Knopf «Im Browser öffnen» als Ausweichweg.',
      },
    ],
  },
  {
    version: 'v1.14.683',
    date: '26. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Kontaktimport und Deduplizierung sind eigene Seiten unter CRM › Kontakte; die alte Seite «Adressen» samt doppelter Kontaktliste und Recherche-Leads ist aufgelöst, alte Links leiten weiter.',
      },
    ],
  },
  {
    version: 'v1.14.682',
    date: '26. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisationen: Filter «Qualifizierung», Markierung «Unqualifiziert» und Knopf «Qualifizieren» — Zefix bestätigen, Branche setzen, Ansprechperson erfassen in einem Fenster.',
      },
    ],
  },
  {
    version: 'v1.14.681',
    date: '26. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Übersicht: Anlagevehikel mit Vermögen, Fremdfinanzierung, Laufzeit, Zeichnungs-/Rücknahmebedingungen und Kauf-/Verkaufsanlässen direkt in der Übersicht, aufklappbar.',
      },
      {
        type: 'improvement',
        text: 'Organisation › Übersicht: «Objekte ›» eines Anlagevehikels öffnet den Reiter Immobilien und grenzt den Eigenbestand auf dieses Vehikel ein — mit «Filter aufheben» für den ganzen Bestand.',
      },
    ],
  },
  {
    version: 'v1.14.680',
    date: '26. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Übersicht: Die Karte Eigenbestand zeigt Gesamtwert und Anzahl Anlagevehikel wieder korrekt.',
      },
    ],
  },
  {
    version: 'v1.14.679',
    date: '24. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Immobilien: Vehikel mit Leerstand/Rendite aus dem Portrait-Import öffnen wieder ohne Fehler.',
      },
    ],
  },
  {
    version: 'v1.14.678',
    date: '22. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Übersicht: In der Karte «Immobilien · Eigenbestand» sind die Balken «Nach Kanton» und «Portfoliobereinigung» jetzt nach Nutzung farbig aufgeteilt — man sieht auf einen Blick, wie viel davon Wohnen, Gewerbe, Wohn/Gewerbe oder Grundstück ist.',
      },
    ],
  },
  {
    version: 'v1.14.677',
    date: '21. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Immobilien: In den Transaktionen wird «Quelle» nur noch verlinkt, wenn ein echter Beleg (http/https) hinterlegt ist — eine Fundstelle wie «GB 2025 S. 7, 35» steht jetzt als Text da und führt nicht mehr auf die eigene Organisationsseite zurück.',
      },
    ],
  },
  {
    version: 'v1.14.676',
    date: '21. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation › Übersicht: Die Karte «Immobilien · Eigenbestand» zeigt ihre Balken zweispaltig (Nutzung und Portfoliobereinigung links, Kantone rechts) und erklärt unter jedem Grund, wonach beurteilt wurde — «Non-Core» etwa als «passt nicht zum Suchprofil (Nutzung oder Kanton)»; die doppelten Gründe-Chips sind weg.',
      },
    ],
  },
  {
    version: 'v1.14.675',
    date: '21. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Immobilien: Ein Vehikel zeigt seine Kennzahlen — Marktwert, Objekte laut Quelle und bei uns erfasst, Leerstand, Rendite, Status und Stand; ein Vehikel ohne Objekte und Kennzahlen steht als ruhiger Einzeiler «keine Objekte erfasst».',
      },
    ],
  },
  {
    version: 'v1.14.674',
    date: '21. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation › Immobilien: Der Eigenbestand steht als Tabelle mit Spaltenköpfen — Objekt, Ort, Nutzung, Einheiten, Wert, Erwerb, Vehikel, Kandidat und Quelle; Vehikel und Cluster bleiben als Gruppenzeilen mit Anzahl und Summe.',
      },
    ],
  },
  {
    version: 'v1.14.673',
    date: '21. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Übersicht: Die Karte «Immobilien · Eigenbestand» zeigt Objekte, Einheiten, Wert, Kandidaten und Vehikel als Kennzahlen, dazu Balken nach Nutzung und nach Kanton.',
      },
    ],
  },
  {
    version: 'v1.14.672',
    date: '21. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Immobilien: Jedes Objekt des Eigenbestands steht mit seinem Namen und daneben der Adresse in einer kompakten Zeile — statt mit dem Ort als Titel über drei Zeilen.',
      },
    ],
  },
  {
    version: 'v1.14.671',
    date: '21. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation › Übersicht: Die Karte «Portfoliobereinigung» zeigt nur noch Kennzahlen und je Grund einen Balken — die Objektliste steht im Reiter «Immobilien».',
      },
    ],
  },
  {
    version: 'v1.14.670',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Übersicht: Karte «Portfoliobereinigung» — Handelskandidaten mit Gründen, ⚑ setzen und entfernen (mit Notiz), Auswertung als PDF.',
      },
    ],
  },
  {
    version: 'v1.14.669',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Immobilien: Eigenbestand pflegen — Vehikel und Cluster anlegen, umbenennen und löschen, Objekte anhaken und «Verschieben nach», Objekte von Hand erfassen und korrigieren.',
      },
    ],
  },
  {
    version: 'v1.14.668',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Timeline: E-Mails zeigen jetzt das Postfach (geteilt oder persönlich) und wer geantwortet hat — unbeantwortete Anfragen sind markiert, unbekannte Absender lassen sich direkt als Kontakt anlegen.',
      },
    ],
  },
  {
    version: 'v1.14.667',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Immobilien: Im Eigenbestand zeigt jede Objektzeile wieder ihre Quelle — neu auch für Import, KI-Recherche und Amtsblatt.',
      },
    ],
  },
  {
    version: 'v1.14.666',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Immobilien: Abschnitt «Eigenbestand» mit Vehikel, Cluster und Objekten, Summen, Transaktionen — plus Chip «Bestand» im Kopf.',
      },
    ],
  },
  {
    version: 'v1.14.665',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Timeline: E-Mails erscheinen als Konversation mit Betreff, Anzahl Mails und Beteiligten-Zeile «Gegenseite ↔ Postfach (Antwort: User)» — auf der Organisation mit Link auf den Kontakt.',
      },
    ],
  },
  {
    version: 'v1.14.664',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Immobilien: Gruppieren nach Nutzung, Kanton, Status oder Bezug — je Gruppe Anzahl und Preissumme, Gruppen aufklappbar.',
      },
    ],
  },
  {
    version: 'v1.14.663',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Timeline: E-Mails sind anklickbar und öffnen die Konversation mit Betreff, Text und Anhängen — inklusive «Im Postfach öffnen».',
      },
    ],
  },
  {
    version: 'v1.14.662',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Immobilien zeigt jetzt auch die Objekte der Kontakte der Firma — mit Badge «über Kontakt» und Sprung zum Kontakt.',
      },
    ],
  },
  {
    version: 'v1.14.661',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisationsseite nutzt die volle Fensterbreite — die Karten der Übersicht bekommen auf grossen Bildschirmen mehr Platz.',
      },
    ],
  },
  {
    version: 'v1.14.660',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Suchprofil: Mindestgrösse, Fläche, Baujahr, Energie, Lage- und Preisnotizen sowie die harte Preis-Untergrenze lassen sich jetzt direkt im Formular bearbeiten.',
      },
    ],
  },
  {
    version: 'v1.14.659',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Kontaktseite spricht dieselbe Sprache wie die Organisationsseite: Tabs heissen jetzt Organisationen und Suchprofil.',
      },
    ],
  },
  {
    version: 'v1.14.658',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Rechercheergebnis übernehmen funktioniert wieder — der Dialog schickt die Entscheidungen pro Feld im Format, das die API erwartet.',
      },
      {
        type: 'fix',
        text: 'Rechercheergebnis übernehmen: Der Entscheid «beide» führt bestehende und vorgeschlagene Listenwerte wieder zusammen, statt den bestehenden Wert zu überschreiben.',
      },
    ],
  },
  {
    version: 'v1.14.657',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dubletten-Hinweise lassen sich mit «Nicht dieselbe Person» wegklicken — im Kontakte-Tab der Organisation und auf der Kontaktseite; der Entscheid bleibt gespeichert.',
      },
    ],
  },
  {
    version: 'v1.14.656',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation › Übersicht: Die offenen Punkte der Karte «Datenqualität» stehen auf grossen Bildschirmen zweispaltig mit dem Knopf direkt beim Text — die Karte ist nur noch halb so hoch, auf dem Handy bleibt sie einspaltig.',
      },
    ],
  },
  {
    version: 'v1.14.655',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation › Übersicht: Mögliche Dubletten stehen jetzt als oberste Zeile in der Karte «Datenqualität» statt in einem eigenen Hinweis-Banner — «Zusammenführen» gibt es nur noch an einer Stelle.',
      },
    ],
  },
  {
    version: 'v1.14.654',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Kontakte: «Möglicherweise dieselbe Person» erscheint nur noch bei wirklich ähnlichen Vornamen (auch abgekürzt wie «M. Schweizer») — gleiche E-Mail-Domain allein genügt nicht mehr, und zwei Handelsregister-Einträge werden nie als Dublette gemeldet.',
      },
    ],
  },
  {
    version: 'v1.14.653',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisation › Datenqualität: Beim Öffnen einer Organisation hält ein Platzhalter den Platz der Datenqualitäts-Karte — die Karten darunter springen nicht mehr nach unten.',
      },
    ],
  },
  {
    version: 'v1.14.652',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisationen: Die Branche wird aus einem festen Raster von 16 Werten gewählt statt frei getippt — in den Stammdaten und im Listenfilter; bestehende Alt-Werte bleiben sichtbar und entfernbar.',
      },
    ],
  },
  {
    version: 'v1.14.651',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation › Übersicht: Die Karten stehen auf grossen Bildschirmen in zwei Spalten, Kontaktdaten und Zuständigkeit gleich oben — Telefon und Verantwortliche sind ohne Scrollen sichtbar.',
      },
    ],
  },
  {
    version: 'v1.14.650',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › KI-Recherche: Portfolio (Gesamtwert, Anzahl Objekte, Fokus) und die gefundenen Transaktionen (Datum, Kauf/Verkauf, Objekt, Preis, Quelle) werden jetzt lesbar angezeigt — neueste Transaktion zuoberst.',
      },
    ],
  },
  {
    version: 'v1.14.649',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisationsseite: Kopf, Reiter und Karten stehen jetzt in einer zentrierten Inhaltsspalte statt auf der ganzen Fensterbreite — auf breiten Bildschirmen sind Text und Aktionsknöpfe wieder beieinander.',
      },
    ],
  },
  {
    version: 'v1.14.648',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisationsseite: Der Kopf nennt die Datenqualität jetzt als «Daten: n offen» aus derselben Prüfung wie die Karte — statt einer zweiten Prozentzahl — und der Chip springt direkt zu den offenen Punkten.',
      },
    ],
  },
  {
    version: 'v1.14.647',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisationsseite › Datenqualität: Die Karte zeigt jetzt oben die offenen Punkte mit Anzahl, erfüllte Prüfungen liegen eingeklappt darunter — und ohne irreführenden Knopf wie «Logo hochladen» neben «Logo vorhanden».',
      },
    ],
  },
  {
    version: 'v1.14.646',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisationsseite › Datenqualität: Die Prüfungen heissen jetzt überall verständlich (Firmen-Mail, Handelsregister, Recherche-Vorschlag, Kontakt-Dubletten) statt technische Kürzel anzuzeigen.',
      },
    ],
  },
  {
    version: 'v1.14.645',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisationsseite: Der Kopf zeigt jetzt auf einen Blick, was die Organisation macht — Einzeiler aus der Beschreibung sowie Chips für Dealgrösse, Nutzungen, Kantone, Relevanz, Zuständigkeit und Datenqualität, dazu ein Knopf «Recherche prüfen & übernehmen».',
      },
    ],
  },
  {
    version: 'v1.14.644',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'E-Mail-Konflikt: generische Adresse am Kontakt wird mit einem Klick zur Organisation verschoben — «Trotzdem speichern» bleibt möglich.',
      },
      {
        type: 'improvement',
        text: 'Liegt eine Adresse schon bei einem Kontakt oder einer Organisation, führt der Dialog direkt dorthin statt einen Fehlertext zu zeigen.',
      },
    ],
  },
  {
    version: 'v1.14.643',
    date: '20. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Suchprofil: eine Tabelle je Kriterium mit «Gesucht», «Ausgeschlossen» und Quell-Badge; leere Zeilen mit «Ergänzen».',
      },
      {
        type: 'improvement',
        text: 'Der Reiter «Investorengruppen» ist entfallen — die Gruppen stehen jetzt oben im Suchprofil; alte Links landen dort.',
      },
      {
        type: 'feature',
        text: 'Suchkriterien bearbeiten: je Kriterium zwei Chip-Felder (gesucht / ausgeschlossen) und ein Schalter «unter Minimum ausgeschlossen» für die Dealgrösse.',
      },
    ],
  },
  {
    version: 'v1.14.642',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Übernahme-Dialog: Personen mit Revisions-Rolle (z. B. «Leiter Revision», «Partner Treuhand») werden wieder als Kontakt vorgeschlagen — nur firmenähnliche Namen gelten als Revisionsstelle.',
      },
      {
        type: 'fix',
        text: 'Übernahme eines Rechercheergebnisses schlägt nicht mehr fehl — der Dialog sendet wieder alle vom Server verlangten Angaben mit.',
      },
    ],
  },
  {
    version: 'v1.14.641',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation: Dialog «Rechercheergebnis übernehmen» — pro Feld behalten, übernehmen oder beide, Personen verknüpfen, neu anlegen, als Revisionsstelle verknüpfen oder verwerfen.',
      },
      {
        type: 'feature',
        text: 'Quell-Badges an den Feldern der Stammdaten- und Kontaktdaten-Karte zeigen, woher ein Wert kommt (KI mit Datum, Handelsregister, manuell, öffentliche Daten).',
      },
      {
        type: 'fix',
        text: 'Gefundene Personen werden nicht mehr blind als neue Kontakte angelegt — entschieden wird im Übernahme-Dialog.',
      },
    ],
  },
  {
    version: 'v1.14.640',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation › Übersicht: Kennzahlen-Block (Kapital, Gründung, Mitarbeitende, Umsatz, Dealgrösse, Relevanz-Score) mit Quell-Badge; leere Zeilen mit «Ergänzen».',
      },
      {
        type: 'feature',
        text: 'Organisation › Übersicht: Register-Checkliste mit Ampelfarben (Handelsregister, SECO, FINMA, OAK BV, SIX, SRO, Wirtschaftlich Berechtigte, Identifikation, Verbände) inklusive Legende.',
      },
      {
        type: 'improvement',
        text: 'Registerauszug der Organisation ist eingeklappt und zeigt «Stand …»; die Zefix-Aktionen bleiben darunter sichtbar.',
      },
    ],
  },
  {
    version: 'v1.14.639',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation: Karte «Datenqualität» auf der Übersicht — jede Prüfung mit Statuspunkt und einer 1-Klick-Aktion (z. B. «Mir zuweisen»); bei 100 Punkten verschwindet die Karte.',
      },
      {
        type: 'feature',
        text: 'Organisationsliste: neue Spalte «Datenqualität» als Mini-Balken, sortierbar, plus Filter «unvollständig (< 70)».',
      },
    ],
  },
  {
    version: 'v1.14.638',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Organisationsseite: alle Texte sind in Deutsch, Englisch, Französisch und Italienisch gepflegt — keine deutschen Reste mehr in den anderen Sprachen.',
      },
      {
        type: 'improvement',
        text: 'Einheitliche Begriffe auf der Organisationsseite: «Investorprofil» statt «Käuferprofil», «Suchprofil» statt «Suchkriterien», «Verbundene Organisationen» und durchgehend «Organisation» statt «Firma».',
      },
    ],
  },
  {
    version: 'v1.14.637',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Firmenlogos sind jetzt in der Organisationsliste, im Kopf der Detailseite und in den Stammdaten sichtbar — ohne Logo erscheint ein farbiges Monogramm statt des Gebäude-Symbols.',
      },
    ],
  },
  {
    version: 'v1.14.636',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Kontakte-Tab einer Organisation: mögliche Dubletten werden in der Zeile gelb markiert und lassen sich direkt zusammenführen.',
      },
    ],
  },
  {
    version: 'v1.14.635',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Organisation › Übersicht: eine Karte «Kontaktdaten der Organisation» mit Adresse, E-Mails samt Rang-Badge, Telefonnummern, Website und LinkedIn statt vier Karten.',
      },
    ],
  },
  {
    version: 'v1.14.633',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'App bleibt nach einem Release erreichbar: Der Web-Container meldet sich auch bei IPv6-Loopback als betriebsbereit.',
      },
    ],
  },
  {
    version: 'v1.14.632',
    date: '19. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Zefix-Kontakte prüfen: «Zusammenführen» ändert wieder genau die Verknüpfung der gewählten Person statt einer fremden.',
      },
    ],
  },
  {
    version: 'v1.14.631',
    date: '15. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Teaser: Unter der Standort-Karte öffnet «In Google Maps öffnen» die Karte mit gesetzter Markierung auf dem Objekt.',
      },
    ],
  },
  {
    version: 'v1.14.630',
    date: '15. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'KYC per Handy: Das Selfie-Bild ist wieder gross und quadratisch — Gesicht und Hand haben Platz im Rahmen.',
      },
    ],
  },
  {
    version: 'v1.14.629',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Quality Gate: Wird eine Investorengruppe angehakt und am Objekt ist keine Kondition hinterlegt, öffnet sofort «Konditionen für alle festlegen» — sie gilt auch für die Gruppenmitglieder. Der 409-Hinweis bei der Freigabe nennt diesen Weg jetzt ausdrücklich.',
      },
    ],
  },
  {
    version: 'v1.14.628',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Unterschreiben geht sofort los: Signaturseite und Gegenzeichnung warten beim Absenden nicht mehr bis zu 10 Sekunden auf die Standortfreigabe, und eine erteilte Position wird jetzt tatsächlich mitgesendet.',
      },
    ],
  },
  {
    version: 'v1.14.627',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Objektdetail für Investoren: die fünf Abschnitts-Tabs (Timeline, Übersicht, Finanzen, Angebot, Dokumente) sind für Tests und Hilfsmittel wieder eindeutig unterscheidbar, der aktive Tab wird zusätzlich ausgezeichnet.',
      },
    ],
  },
  {
    version: 'v1.14.626',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Schriften: Die Hausschrift Satoshi wird wieder korrekt geladen — statt einer unbrauchbaren Platzhalter-Datei liegt nun die vollständige Schrift bereit, und der Fehler «Failed to decode downloaded font» erscheint nicht mehr in der Browser-Konsole.',
      },
      {
        type: 'improvement',
        text: 'Ein neuer Prüfschritt im Build lehnt unvollständige Schriftdateien ab, damit ein Platzhalter nicht erneut ausgeliefert wird.',
      },
      {
        type: 'fix',
        text: 'Objektdetail für Investoren: beim Scrollen wird wieder der Tab des gerade sichtbaren Abschnitts hervorgehoben.',
      },
    ],
  },
  {
    version: 'v1.14.625',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Navigation: Investoren mit reinem Käufervertrag sehen die Sektion «Verkäufer › Meine Immobilien» nicht mehr — sie erscheint nur noch mit Verkäufer-Rolle im Plattformvertrag.',
      },
    ],
  },
  {
    version: 'v1.14.624',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Dashboard: Eine eingereichte Identifikation zeigt «wird geprüft» statt weiterhin «Jetzt starten»; nach einer nicht erfolgreichen Prüfung «Erneut starten».',
      },
    ],
  },
  {
    version: 'v1.14.623',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Vertragsliste: Die Spalte «Parteien» zeigt bei «Uns» immer die Mandanten-Firma statt der unterzeichnenden Person.',
      },
    ],
  },
  {
    version: 'v1.14.622',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Identifikation: Der Begrüssungsdialog macht keine Aussage mehr zur Speicherung von Ausweisbildern.',
      },
    ],
  },
  {
    version: 'v1.14.621',
    date: '7. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Identifikation: Der Ausweis wird gleich nach der Aufnahme geprüft — die Angaben sind vorausgefüllt, und bei Spiegelung oder Unschärfe bitten wir um eine neue Aufnahme.',
      },
    ],
  },
  {
    version: 'v1.14.620',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Quality Gate: Ohne Kondition für jeden Käufer ist die Freigabe gesperrt; der Dialog führt direkt zur Objektkondition.',
      },
    ],
  },
  {
    version: 'v1.14.619',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Identifikation: Der Ausweis wird auch auf heller Unterlage zuverlässiger erkannt; nach einigen Sekunden hilft ein Tipp weiter.',
      },
    ],
  },
  {
    version: 'v1.14.618',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Timeline: Der Verlauf zeigt jede Phase mit Von–bis-Datum, in der Breite ihrer Dauer und mit der Marke „Heute“',
      },
    ],
  },
  {
    version: 'v1.14.617',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dashboard: Identifikation und Lex-Koller-Einschätzung erscheinen als ein gemeinsamer Hinweis und starten mit einem Klick',
      },
    ],
  },
  {
    version: 'v1.14.616',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Teaser: Die neue Ansicht „Versand & Auswertung“ zeigt, wer welche Mail erhalten, geöffnet und angeklickt hat',
      },
    ],
  },
  {
    version: 'v1.14.615',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Quality Gate: Nach der Freigabe sehen Sie auch, welche neuen Gruppenmitglieder eine Einladung erhalten haben',
      },
    ],
  },
  {
    version: 'v1.14.614',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Investorengruppen: Sie sehen jetzt als aufklappbaren Baum, welche Personen einer Firma die Zugangs-Mail erhalten — im Quality Gate, am Teaser und im Rückfrage-Fenster',
      },
    ],
  },
  {
    version: 'v1.14.613',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Investorengruppen: Bei Firmen sehen Sie jetzt, welche Personen die Zugangs-Mail erhalten, statt der allgemeinen Firmenadresse',
      },
    ],
  },
  {
    version: 'v1.14.612',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Zugeordnete Käufer: Bei einem Zugriff über eine Investorengruppe sehen Sie jetzt die Firma und die Gruppe, über die er zustande kam',
      },
    ],
  },
  {
    version: 'v1.14.611',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Quality Gate: Vor jeder Freigabe, die E-Mails auslöst, sehen Sie alle Empfänger — neue Käufer, Käufer mit Vertrag und Gruppenmitglieder — und bestätigen den Versand',
      },
    ],
  },
  {
    version: 'v1.14.610',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Personen-Suche: Ein entfernter Käufer lässt sich sofort wieder zuordnen; Kontakte ohne E-Mail-Adresse sind nicht mehr zuordenbar.',
      },
    ],
  },
  {
    version: 'v1.14.609',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'fix',
        text: 'Investorengruppen: Im Suchfeld der Gruppe finden Sie jetzt Kontakte und Firmen aus dem CRM und fügen sie direkt als Mitglied hinzu',
      },
    ],
  },
  {
    version: 'v1.14.608',
    date: '6. Oktober 2026',
    changes: [
      {
        type: 'feature',
        text: 'Quality Gate: Nach der Freigabe sehen Sie, welche Mitglieder einer Investorengruppe die Zugangs-Mail erhalten haben',
      },
    ],
  },
  {
    version: 'v1.14.607',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Identifikation: Das Selfie-Livebild wird wie in jeder Selfie-App gespiegelt gezeigt — Gesichts-Oval und Hand-Umriss lassen sich so treffen; gespeichert wird das Bild weiterhin ungespiegelt',
      },
    ],
  },
  {
    version: 'v1.14.606',
    date: '28. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Identifikation: Der Selfie-Ausschnitt ist breiter — das Gesichts-Oval sitzt links, rechts zeigt ein Hand-Umriss mit der verlangten Fingerzahl, wohin die Hand gehört',
      },
    ],
  },
  {
    version: 'v1.14.605',
    date: '28. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Identifikationen prüfen: Die KI-Prüfung zeigt ihre Empfehlung mit allen sechs Prüfpunkten, und bei «Freigabe empfohlen» gibt der Hauptknopf die Identifikation mit einem Klick frei',
      },
    ],
  },
  {
    version: 'v1.14.604',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Identifikation: Nach «Wiederholen» zeigt die Kamera wieder das Livebild statt eines schwarzen Ausschnitts — bei Ausweis und Selfie',
      },
    ],
  },
  {
    version: 'v1.14.603',
    date: '28. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Identifikationen prüfen: Ausweisnummer, Ablaufdatum und Staatsangehörigkeit werden mit Herkunft («von KI») angezeigt und lassen sich vor der Freigabe korrigieren',
      },
      {
        type: 'improvement',
        text: 'Lex Koller: Schweizer Staatsbürgerschaft aus dem Ausweis führt direkt zum Ergebnis «keine Einschränkung» statt zum Fragebogen — andere Nationalitäten sind vorausgefüllt',
      },
    ],
  },
  {
    version: 'v1.14.602',
    date: '28. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Einstellungen: Wer kein Passwort hat (Anmeldung per Link, Passkey oder Handy-Signatur), legt es jetzt per E-Mail-Link fest — statt am Pflichtfeld «Aktuelles Passwort» zu scheitern',
      },
      {
        type: 'improvement',
        text: 'Passwort ändern: neuer Weg «Aktuelles Passwort unbekannt?» sendet einen E-Mail-Link — in den Einstellungen und im Investoren-Profil einheitlich',
      },
    ],
  },
  {
    version: 'v1.14.601',
    date: '28. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'KYC-Ausweisscanner verzeiht mehr: Handzittern beendet die Auto-Auslösung nicht mehr, einzelne Fehlbilder lassen den Umriss stehen, die Karte muss den Rahmen nur noch zu 65 % füllen und der Umriss ist geglättet statt zappelig',
      },
    ],
  },
  {
    version: 'v1.14.600',
    date: '28. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev C&C: Jeder Task zeigt eine Phasenleiste (Umsetzung · MR · Pipeline · Merge-Zug · Deploy · live) mit Wartegrund — inklusive Pipeline-Job und Runner sowie Position im Merge-Zug',
      },
    ],
  },
  {
    version: 'v1.14.599',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'KYC-Selfie: Ist eine Finger-Aufgabe gestellt, zählt die Kamera nach «Gesicht ruhig im Oval» sichtbar von 3 herunter — so bleibt Zeit, die Finger auch wirklich zu zeigen',
      },
    ],
  },
  {
    version: 'v1.14.598',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Ausweis-Aufnahme auf dem Handy: Kamerabild und «Aufnehmen» passen jetzt zusammen auf den Bildschirm — das Bild nimmt nur noch den Platz, der wirklich übrig ist',
      },
    ],
  },
  {
    version: 'v1.14.597',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Ausweis-Aufnahme: der Scanner startete zuletzt gar nicht mehr und blieb stumm — die automatische Erkennung des Ausweises funktioniert wieder',
      },
    ],
  },
  {
    version: 'v1.14.596',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Ausweis-Aufnahme auf dem Handy: der Scanner lädt jetzt im Hintergrund statt die Seite zu blockieren — Vorschau und «Aufnehmen» reagieren sofort, auch auf älteren Geräten',
      },
    ],
  },
  {
    version: 'v1.14.595',
    date: '28. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Entwickler-Cockpit: neue Kachel «Abo-Verbrauch» zeigt pro Token-Slot den Verbrauch im Session- und Wochenfenster mit Ampel, Reset-Zeit, Tempo und dem aktuellen Agenten-Deckel',
      },
    ],
  },
  {
    version: 'v1.14.594',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Identifikations-Dialog: «fortsetzen» oder «wird geprüft» kommt jetzt vom Server statt aus dem Browser-Speicher — eine eingereichte Identifikation zeigt auch auf einem anderen Gerät «wird geprüft» und lässt sich nicht versehentlich zweimal starten',
      },
    ],
  },
  {
    version: 'v1.14.593',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Abgebrochene Identifikation: Statt nur «Identifikation läuft» steht jetzt «Identifikation fortsetzen» im Dialog — am Computer wieder mit QR-Code fürs Handy, auf dem Handy direkt mit der Aufnahme. Ist die Identifikation bereits eingereicht, bleibt es bei «wird geprüft»',
      },
    ],
  },
  {
    version: 'v1.14.592',
    date: '28. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Käufer-Suche: Bereits zugeordnete Käufer erscheinen jetzt als «Zugeordnet» — auch solche mit aktivem Zugriff und Personen, die über ihre zugeordnete Firma dabei sind; sie lassen sich nicht mehr versehentlich ein zweites Mal zuordnen',
      },
    ],
  },
  {
    version: 'v1.14.591',
    date: '27. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Käufer-Suche: Personen, die nur noch über eine beendete Firmenrolle passen, erscheinen grau mit dem Hinweis «inaktiv bei …» und können nicht mehr zugeordnet werden',
      },
    ],
  },
  {
    version: 'v1.14.590',
    date: '27. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Identifikation auf dem Handy: Das Kamerabild ist jetzt an die sichtbare Bildschirmhöhe gekoppelt — Vorschau und «Aufnehmen»-Knopf passen zusammen auf den Schirm, ohne zu scrollen; das Selfie bleibt im Hochformat 3:4',
      },
    ],
  },
  {
    version: 'v1.14.589',
    date: '27. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Identifikation: Der Scanner friert auf dem Handy nicht mehr ein — «Scanner wird geladen …» geht wieder in die Ausweiserkennung über, der Aufnehmen-Knopf und die automatische Auslösung reagieren wieder',
      },
    ],
  },
  {
    version: 'v1.14.588',
    date: '27. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev Cockpit: Der Reiter «Prozess» beginnt mit dem Ablauf in Alltagssprache — eine Zeile pro Schritt mit Symbol, Dauer und kurzem Satz, inklusive gezählter Nachbesserungen bei Tests und Prüfung; Zustandsautomat, Skripte, Prompts und Events liegen eingeklappt unter «Technik»',
      },
    ],
  },
  {
    version: 'v1.14.587',
    date: '27. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Identifikation: das Selfie wird hochkant mit einem Gesichts-Oval aufgenommen und löst von selbst aus, sobald genau ein Gesicht ruhig im Oval sitzt',
      },
    ],
  },
  {
    version: 'v1.14.586',
    date: '27. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev Cockpit: Jeder Task beginnt mit einer Kurz-Erklärung «Was / Warum / Danach» samt Ketten-Übersicht — technische Metadaten, Konfiguration und Stichwort-Analyse liegen eingeklappt unter «Technische Details» am Seitenende',
      },
    ],
  },
  {
    version: 'v1.14.585',
    date: '27. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Identifikationen prüfen: Beim Selfie steht nur noch die vom System geforderte Fingerzahl — die alte, widersprüchliche Aufgabe «Zwei Finger zeigen» erscheint nicht mehr daneben',
      },
    ],
  },
  {
    version: 'v1.14.584',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Käufersuche: Namen in der Trefferliste werden nicht mehr abgeschnitten, sondern umgebrochen — der vollständige Name erscheint zusätzlich als Tooltip' },
    ],
  },
  {
    version: 'v1.14.583',
    date: '27. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Identifikation: ein schräg gehaltener Ausweis wird beim Aufnehmen automatisch gerade gerückt und formatfüllend gespeichert',
      },
    ],
  },
  {
    version: 'v1.14.582',
    date: '27. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Öffentliche Teaser-Seite spricht Alltagssprache: statt «Plattformvertrag» heisst es nun «Konto eröffnen» — in allen vier Sprachen',
      },
    ],
  },
  {
    version: 'v1.14.581',
    date: '27. September 2026',
    changes: [
      { type: 'improvement', text: 'Was ist neu: interne Wartungsmeldungen aus der Liste entfernt — hier stehen nur noch Änderungen, die Sie in der Anwendung sehen' },
    ],
  },
  {
    version: 'v1.14.580',
    date: '27. September 2026',
    changes: [
      { type: 'improvement', text: 'Interne Entwicklungswerkzeuge auf aktuelle Hauptversionen gehoben. Die Anwendung verhält sich unverändert.' },
    ],
  },
  {
    version: 'v1.14.579',
    date: '27. September 2026',
    changes: [
      { type: 'improvement', text: 'Build-Tooling aktualisiert: Vite 7 auf 8.x angehoben (Rolldown ersetzt Rollup/esbuild), @vitejs/plugin-react auf 6.x und vite-plugin-pwa auf 1.3 gehoben. Die Anwendung verhält sich unverändert.' },
    ],
  },
  {
    version: 'v1.14.578',
    date: '27. September 2026',
    changes: [
      { type: 'improvement', text: 'Build-Tooling aktualisiert: Vite 5.4 auf 6.4 angehoben (inkl. @vitejs/plugin-react). Build und Dev-Server laufen unverändert.' },
    ],
  },
  {
    version: 'v1.14.577',
    date: '27. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Onboarding-Seite: die Käuferprovision in den Konditionen wird nicht mehr rot und fett dargestellt und wirkt dadurch nicht länger wie eine Fehlermeldung',
      },
    ],
  },
  {
    version: 'v1.14.576',
    date: '27. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Teaser-Seite auf dem Handy: der Objekttitel wird nicht mehr oben abgeschnitten — Titel, Untertitel und Merkmale stehen jetzt unter dem Titelbild',
      },
    ],
  },
  {
    version: 'v1.14.575',
    date: '27. September 2026',
    changes: [
      { type: 'feature', text: 'Ausweis-Aufnahme im Scanner-Modus: die Karte wird im Livebild erkannt und löst von selbst aus, sobald sie ruhig im Rahmen liegt — «Aufnehmen» bleibt weiterhin möglich' },
    ],
  },
  {
    version: 'v1.14.574',
    date: '27. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Signaturseite: stornierte Verträge werden nicht mehr als unterschreibbar angezeigt — stattdessen der Hinweis «Dieser Vertrag wurde storniert»',
      },
    ],
  },
  {
    version: 'v1.14.573',
    date: '27. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Unterschriftsfeld: «Auf Handy oder Tablet unterschreiben» mit QR-Code — die Unterschrift vom Handy erscheint am Computer',
      },
    ],
  },
  {
    version: 'v1.14.572',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Identifikations-Badge in der Kopfzeile ist auf dem Handy wieder sichtbar — kompakt als Symbol, ab Tablet mit Beschriftung' },
    ],
  },
  {
    version: 'v1.14.571',
    date: '27. September 2026',
    changes: [
      { type: 'feature', text: 'KYC-Ausweisfoto-Erfassung unterstützt 4K-Auflösung und Live-Vorschau' },
      { type: 'improvement', text: 'Kamera-Validierungsschwellen für optimale Ausweiserkennung konfiguriert' },
    ],
  },
  {
    version: 'v1.14.570',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Ausweis erfassen: Aufnahme in voller Kameraauflösung, Live-Ansicht im Format der Kamera — der Hinweis «Die Aufnahme ist klein» erscheint nicht mehr bei jedem Foto' },
    ],
  },
  {
    version: 'v1.14.569',
    date: '27. September 2026',
    changes: [
      { type: 'feature', text: 'Mobile Unterschriftenseite /sign-mobile/:code für Handy-Unterschriften' },
      { type: 'feature', text: 'signatureHandoff-Dienst für sicheren Austausch von Unterschriftsdaten' },
    ],
  },
  {
    version: 'v1.14.568',
    date: '27. September 2026',
    changes: [
      { type: 'feature', text: 'Unterschreiben auf dem Handy: neue Seite, die per QR-Code ohne Anmeldung nur das Unterschriftsfeld zeigt' },
    ],
  },
  {
    version: 'v1.14.567',
    date: '27. September 2026',
    changes: [
      { type: 'improvement', text: 'Signaturseite in Alltagssprache: «Konto eröffnen: Unterschrift», Schritte «Vertrag ansehen · Nutzungsbedingungen akzeptieren · Unterschreiben», Parteien kompakt' },
    ],
  },
  {
    version: 'v1.14.566',
    date: '27. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Identifikationen prüfen: geforderte Fingerzahl gross neben dem Selfie und in der Vergrösserung',
      },
    ],
  },
  {
    version: 'v1.14.565',
    date: '27. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Signaturseite: Mandant wird als «Vermittlerin» beschriftet, «Betreiberin» nur noch für die Plattform-Betreiberin',
      },
    ],
  },
  {
    version: 'v1.14.564',
    date: '27. September 2026',
    changes: [
      { type: 'improvement', text: 'Onboarding-Seite in Alltagssprache: Willkommen, Nutzen und Kosten zuerst, Knopf «Konto eröffnen», vollständiger Vertrag aufklappbar' },
    ],
  },
  {
    version: 'v1.14.563',
    date: '27. September 2026',
    changes: [
      { type: 'improvement', text: 'Identifikation: Vor dem Selfie steht neu die Aufgabe «Zeigen Sie N Finger neben dem Gesicht» mit der vom System gewählten Zahl' },
    ],
  },
  {
    version: 'v1.14.562',
    date: '27. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Signaturseite zeigt die Anbieter-Karte des Mandanten («Angebot von …»)',
      },
    ],
  },
  {
    version: 'v1.14.561',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Identifikation auf dem Handy: Neuladen der Seite setzt die Erfassung fort, statt «Dieser Code wurde bereits verwendet» zu melden' },
    ],
  },
  {
    version: 'v1.14.560',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Identifikation auf dem Handy: Neuladen der Seite setzt die Erfassung fort, statt «Dieser Code wurde bereits verwendet» zu melden' },
    ],
  },
  {
    version: 'v1.14.559',
    date: '27. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Onboarding-Seite zeigt die Anbieter-Karte des Mandanten («Angebot von …»)',
      },
    ],
  },
  {
    version: 'v1.14.558',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Kopf-Badge: Ist die Identifikation wegen einer beaufsichtigten Firma nicht nötig, steht dort neu «Keine Identifikation nötig» statt fälschlich «Identifiziert»' },
    ],
  },
  {
    version: 'v1.14.557',
    date: '27. September 2026',
    changes: [
      { type: 'improvement', text: 'Ausweis erfassen: vor dem Einreichen zeigt der Assistent die tatsächlich gespeicherten Aufnahmen — mit «Neu aufnehmen» pro Bild' },
    ],
  },
  {
    version: 'v1.14.556',
    date: '27. September 2026',
    changes: [
      { type: 'feature', text: 'ProviderCard-Block zeigt Anbieter auf PublicTeaserPage' },
      { type: 'improvement', text: 'KycHandoffQr vereinfacht und Tests konsolidiert' },
    ],
  },
  {
    version: 'v1.14.555',
    date: '27. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Teaser-Seite: Anbieter-Karte «Angebot von» mit Mandanten-Logo und Kontaktperson',
      },
    ],
  },
  {
    version: 'v1.14.554',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Identifikation auf dem Handy: «Neuen Code erzeugen» ist jetzt immer möglich — auch wenn der angezeigte Code noch nicht abgelaufen ist' },
    ],
  },
  {
    version: 'v1.14.553',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Käufer hinzufügen: Die Personensuche zeigt keine inaktiven Kontakte mehr' },
      { type: 'improvement', text: 'Käufer hinzufügen: Nach dem Zuordnen ist das Suchfeld wieder leer und aktiv; der Status (aktiv / Onboarding nötig) erscheint als Meldung' },
    ],
  },
  {
    version: 'v1.14.552',
    date: '27. September 2026',
    changes: [
      { type: 'feature', text: 'Identifikationen prüfen: Die vom Ausweis ausgelesenen Daten stehen direkt unter den Bildern, Abweichungen zum Kontakt sind rot markiert' },
      { type: 'improvement', text: 'Identifikationen prüfen: Läuft das Auslesen noch, aktualisiert sich die Ansicht selbst; nach einem Fehlschlag lässt sich das Auslesen erneut anstossen' },
    ],
  },
  {
    version: 'v1.14.551',
    date: '27. September 2026',
    changes: [
      { type: 'fix', text: 'Identifikation: Die Kamera legt genau den sichtbaren Ausschnitt ab — kein überflüssiger Hintergrund mehr' },
      { type: 'improvement', text: 'Identifikation: Rahmen im Kamerabild für die Ausweiskarte, höhere Kamera-Auflösung und Hinweis bei zu kleiner Aufnahme' },
      { type: 'fix', text: 'Identifikation: Die Abdunkelung rund um den Ausweis-Rahmen bleibt auf das Kamerabild begrenzt und verdunkelt nicht mehr den restlichen Dialog' },
    ],
  },
  {
    version: 'v1.14.550',
    date: '25. September 2026',
    changes: [
      { type: 'feature', text: 'Quality Gate: Freigabe fragt vor dem Versand der Käufer-Einladungen nach — «Freigeben und senden» oder «Nur freigeben»' },
      { type: 'improvement', text: 'Nach der Freigabe nennt ein Hinweis die Anzahl versendeter Einladungen und übersprungene Empfänger' },
    ],
  },
  {
    version: 'v1.14.549',
    date: '25. September 2026',
    changes: [
      { type: 'fix', text: 'Neuigkeiten-Dialog nur noch für interne Rollen — Käufer sehen das interne Changelog nicht mehr' },
      { type: 'fix', text: 'Neuigkeiten öffnen sich nicht mehr automatisch über dem Identifikations-Dialog' },
    ],
  },
  {
    version: 'v1.14.548',
    date: '25. September 2026',
    changes: [
      { type: 'feature', text: 'DossierUploadGrid unterstützt jetzt Untergruppen für bessere Dokumentorganisation' },
      { type: 'improvement', text: 'Veraltete Komponenten entfernt und Dokumentlogik konsolidiert' },
    ],
  },
  {
    version: 'v1.14.547',
    date: '25. September 2026',
    changes: [
      { type: 'feature', text: 'Objekt-Dokumente: Untergruppe je Dokument direkt in der Dokumentliste pflegen — mit Vorschlägen aus demselben Ordner' },
    ],
  },
  {
    version: 'v1.14.546',
    date: '25. September 2026',
    changes: [
      { type: 'feature', text: 'Objektseite: Zugriffsprotokoll zeigt, welcher Käufer welches Dokument wann angesehen oder heruntergeladen hat — mit Käufer-Filter' },
      { type: 'feature', text: 'Objektseite: «Leck zuordnen» ordnet eine weitergegebene Datei per unsichtbarem Fingerabdruck dem Käufer zu (nur Admin)' },
    ],
  },
  {
    version: 'v1.14.545',
    date: '25. September 2026',
    changes: [
      { type: 'improvement', text: 'Käufer-Datenraum: Ordner lückenlos nummeriert, lange Ordner nach Untergruppen gegliedert, «Neu»-Marken seit dem letzten Besuch und neutrale Dokumentzähler' },
    ],
  },
  {
    version: 'v1.14.544',
    date: '25. September 2026',
    changes: [
      { type: 'improvement', text: 'Käufer-Datenraum: Exposé als eigene Karte oben mit «Exposé lesen» im Vollbild statt als Ordnerzeile' },
    ],
  },
  {
    version: 'v1.14.543',
    date: '25. September 2026',
    changes: [
      { type: 'improvement', text: 'Käufer-Datenraum: Dokumente zeigen die gepflegte Beschreibung als Titel, mobil ohne abgeschnittene Namen und ohne Überdeckung durch den Chat-Knopf' },
    ],
  },
  {
    version: 'v1.14.542',
    date: '25. September 2026',
    changes: [
      { type: 'improvement', text: 'Käufer-Datenraum: Dokumentvorschau zeigt Seitenbilder, lädt nur sichtbare Seiten und bietet Vollbild' },
    ],
  },
  {
    version: 'v1.14.541',
    date: '25. September 2026',
    changes: [
      { type: 'fix', text: 'Käufer-Objektseite: Investment Highlights erscheinen wieder als Kacheln statt als Absatz' },
    ],
  },
  {
    version: 'v1.14.540',
    date: '25. September 2026',
    changes: [
      { type: 'fix', text: 'Käufer-Ansicht: fehlende Umlaute in Timeline, Dashboard und Dokumenten korrigiert' },
      { type: 'fix', text: 'Timeline: «Exklusiv» erscheint nur noch bei Objekten mit Exklusivmandat' },
    ],
  },
  {
    version: 'v1.14.539',
    date: '25. September 2026',
    changes: [
      { type: 'feature', text: 'Dossier-Links navigieren jetzt korrekt zum Dashboard statt zu anderen Seiten' },
      { type: 'feature', text: 'Kürzlich besuchte Immobilien laden aus Server-Daten statt aus Client-Cache' },
      { type: 'improvement', text: 'Test-Coverage für Routing und Dashboard-Komponenten erweitert' },
    ],
  },
  {
    version: 'v1.14.538',
    date: '25. September 2026',
    changes: [
      { type: 'fix', text: 'Alte Dossier- und Detail-Links führen wieder auf die Objektseite statt aufs Dashboard' },
      { type: 'improvement', text: '«Zuletzt besucht» im Käufer-Dashboard nutzt die serverseitigen Besuche und funktioniert damit geräteübergreifend' },
    ],
  },
  {
    version: 'v1.14.537',
    date: '25. September 2026',
    changes: [
      { type: 'improvement', text: 'EN/FR/IT: deutsche Beschriftungen im Objekt-Editor übersetzen' },
    ],
  },
  {
    version: 'v1.14.536',
    date: '25. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Objekt-Editor: deutsche Beschriftungen in Englisch, Französisch und Italienisch übersetzt',
      },
    ],
  },
  {
    version: 'v1.14.535',
    date: '25. September 2026',
    changes: [
      { type: 'improvement', text: 'Käufer-Objektseite: Preis vor NDA sichtbar wie in der Suchliste (Regel R1)' },
    ],
  },
  {
    version: 'v1.14.534',
    date: '25. September 2026',
    changes: [
      { type: 'fix', text: 'Objektseite: Preis ist für Käufer schon vor der Vertraulichkeitserklärung sichtbar — wie in Suchliste und Dashboard' },
    ],
  },
  {
    version: 'v1.14.533',
    date: '25. September 2026',
    changes: [
      { type: 'fix', text: 'Bildfilter zeigt jetzt Bilder ohne Bildart-Zuweisung an' },
      { type: 'improvement', text: 'Mehrsprachige Unterstützung für alle Bildfilter-Optionen' },
    ],
  },
  {
    version: 'v1.14.532',
    date: '25. September 2026',
    changes: [
      { type: 'fix', text: 'Bildfilter: Neuer Filter «Weitere» zeigt Bilder ohne erkannte Bildart — die Einzel-Zähler ergeben nun wieder die Gesamtzahl' },
    ],
  },
  {
    version: 'v1.14.531',
    date: '25. September 2026',
    changes: [
      { type: 'feature', text: 'Erkennung von Firmen-Rahmenverträgen bei zugeordneten Käufern' },
      { type: 'feature', text: 'Rahmenvertrag-Kondition wird angezeigt und als Anhang B gespeichert' },
    ],
  },
  {
    version: 'v1.14.530',
    date: '25. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Zugeordnete Käufer: Rahmenvertrag über die Firma einer Person wird erkannt — Kondition wird angezeigt und als Anhang B gespeichert',
      },
    ],
  },
  {
    version: 'v1.14.529',
    date: '24. September 2026',
    changes: [
      { type: 'feature', text: 'Dossier-Zähler unterscheidet nun zwischen Pflichtunterlagen und optionalen Kategorien' },
      { type: 'improvement', text: 'Umfassende Tests für DossierUploadGrid hinzugefügt und Teaser-Utilities konsolidiert' },
    ],
  },
  {
    version: 'v1.14.528',
    date: '24. September 2026',
    changes: [
      { type: 'improvement', text: 'Dossier-Dokumente: Zähler sind nun beschriftet — Pflichtunterlagen und belegte Kategorien sind klar unterscheidbar' },
    ],
  },
  {
    version: 'v1.14.527',
    date: '24. September 2026',
    changes: [
      { type: 'feature', text: 'Teaser-Preishinweis wird bei Vertraulichkeitserklärung nicht angezeigt' },
      { type: 'improvement', text: 'Teaser-Sichtbarkeitsprüfungen in separate Utilities ausgelagert' },
    ],
  },
  {
    version: 'v1.14.526',
    date: '24. September 2026',
    changes: [
      { type: 'fix', text: 'Teaser mit unterzeichneter Vertraulichkeitserklärung blendet die Hinweise «… nach Vertraulichkeitserklärung» aus' },
    ],
  },
  {
    version: 'v1.14.525',
    date: '24. September 2026',
    changes: [
      { type: 'fix', text: 'Preise & Miete: Preis pro m² wird mit Tausendertrennzeichen und ohne Rappen angezeigt' },
      { type: 'improvement', text: 'Mieterspiegel: Spalte «Etage» ist breiter, der volle Text erscheint beim Überfahren' },
    ],
  },
  {
    version: 'v1.14.524',
    date: '24. September 2026',
    changes: [
      { type: 'fix', text: 'Neuigkeiten-Dialog zeigt jede Änderung nur noch einmal statt doppelt' },
    ],
  },
  {
    version: 'v1.14.523',
    date: '24. September 2026',
    changes: [
      { type: 'fix', text: 'Investor sieht beim NBO/BO Details als Read-Only statt Bearbeits-Maske' },
      { type: 'improvement', text: 'Veraltete KYC-Mobile-Komponenten entfernt und codebase bereinigt' },
    ],
  },
  {
    version: 'v1.14.522',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Angebotsverfahren: «Details» beim eingereichten NBO/BO zeigt neu die eingereichten Angaben statt der Angebots-Maske.',
        detail:
          'Nach der Abgabe eines indikativen (NBO) oder verbindlichen Angebots (BO) öffnete «Details» wieder das Formular für ein neues Angebot. Neu erscheint eine Nur-Lese-Ansicht mit Betrag, Status, Bedingungen und Vorbehalten, Einreichungszeitpunkt und Frist der Runde — beim BO zusätzlich LOI-Unterzeichnung und Finanzierungsnachweis. Die Knöpfe «NBO abgeben» und «BO abgeben» öffnen unverändert die Angebots-Maske.',
      },
    ],
  },
  {
    version: 'v1.14.521',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Quality Gate: Gesperrter «Freigeben»-Knopf ist grau, die offenen Pflichtpunkte stehen direkt darüber.',
        detail:
          'Solange die Freigabe nicht möglich ist, sah der Knopf «Freigeben» grün und damit klickbar aus, und der Grund stand nur als allgemeiner Satz weit oben bei der Checkliste. Neu ist der Knopf im gesperrten Zustand grau, und direkt darüber steht der Block «Freigabe noch nicht möglich» mit allen offenen Pflichtpunkten — je Punkt mit dem konkreten Grund der Schnittstelle, sofern vorhanden, sonst nur mit seiner Bezeichnung. Sind alle Pflichtpunkte erfüllt, bleibt der Knopf grün wie bisher.',
      },
    ],
  },
  {
    version: 'v1.14.520',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Quality Gate: Der Block «Verkäufer-Vertrag» ist neu eine kompakte Info-Zeile mit aufklappbaren Details.',
        detail:
          'Statt einer grossen Karte zeigt die Freigabe-Prüfung jetzt eine einzelne Zeile: «Auftraggeber: <Firma>», daneben der Status als Badge und die Textlinks «Vertrag öffnen» und «Vertrag ändern». Hält der Mandant das Verkaufsmandat selbst ausserhalb von Immotron, steht dort «Eigenes Mandat (ausserhalb Immotron)» — es wird kein fehlender Vertrag mehr angemahnt. Vertragsnummer, Ablaufdatum, Kondition und Unterzeichner erscheinen erst über «Details anzeigen». Fehlt ein nötiger Vertrag, bleibt die Zeile rot hervorgehoben, inklusive Hinweis und Knopf «Vertrag suchen».',
      },
    ],
  },
  {
    version: 'v1.14.519',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Identifikation: QR-Schritt, Handy-Seite und automatische Aktualisierung sind jetzt durch Tests abgesichert.',
        detail:
          'Neue Tests decken den QR-Schritt am Computer (Code erzeugen, Restzeit, Ablauf, «Neuen Code erzeugen», «Hier am Computer weitermachen»), die Handy-Seite /kyc/mobile/:token (Code einlösen, abgelaufener oder bereits verwendeter Code, unterbrochene Sitzung, «Eingereicht») sowie die automatische Aktualisierung des Dialogs am Computer ab. Auf Handy und Tablet bleibt geprüft, dass der QR-Schritt entfällt.',
      },
    ],
  },
  {
    version: 'v1.14.518',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Identifikation: QR-Code «auf dem Handy weitermachen» inklusive Handy-Seite und automatischer Aktualisierung.',
        detail:
          'Wählt man am Computer «Ausweis + Selfie», erscheint neu sofort die Frage «Möchten Sie die Identifikation auf Ihrem Handy machen?» mit einem fertigen QR-Code. Das Handy öffnet damit ohne erneutes Login die Erfassungsseite, nimmt Ausweis und Selfie auf und reicht ein; der Dialog am Computer merkt das von selbst und zeigt «Eingereicht, wird geprüft». Der Code ist 15 Minuten gültig und einmal verwendbar; nach Ablauf lässt sich ein neuer erzeugen — alternativ bleibt «Hier am Computer weitermachen». Auf Handy und Tablet entfällt der QR-Schritt.',
      },
    ],
  },
  {
    version: 'v1.14.517',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Vertragsunterzeichnung: Zusätzlicher Schritt «Bestätigungscode» für die fortgeschrittene Signatur.',
        detail:
          'Verlangt die Schnittstelle bei geprüfter Identität einen 6-stelligen Code, zeigt die öffentliche Signaturseite neu einen eigenen Schritt: «Code senden», Hinweis an welche (maskierte) Adresse der Code ging, Eingabefeld für die 6 Ziffern und «Code erneut senden» (60 Sekunden gesperrt). Unterschrieben werden kann erst mit vollständigem Code. Falscher, abgelaufener oder gesperrter Code sowie eine fehlende Identifikation werden jetzt als verständliche Meldung angezeigt, statt die Seite abstürzen zu lassen. Ohne Code-Pflicht bleibt die Seite unverändert.',
      },
    ],
  },
  {
    version: 'v1.14.516',
    date: '24. September 2026',
    changes: [
      { type: 'feature', text: 'OTP-Verifikation auf der öffentlichen Signaturseite aktiviert' },
      { type: 'improvement', text: 'Veraltete KYC-Capture-Komponenten und Wizard aus Codebase entfernt' },
      { type: 'improvement', text: 'Tests für öffentliche Signaturseite mit OTP-Flow erweitert' },
    ],
  },
  {
    version: 'v1.14.515',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Vertragsunterzeichnung: Zusätzlicher Schritt «Bestätigungscode» für die fortgeschrittene Signatur.',
        detail:
          'Verlangt die Schnittstelle bei geprüfter Identität einen 6-stelligen Code, zeigt die öffentliche Signaturseite neu einen eigenen Schritt: «Code senden», Hinweis an welche (maskierte) Adresse der Code ging, Eingabefeld für die 6 Ziffern und «Code erneut senden» (60 Sekunden gesperrt). Unterschrieben werden kann erst mit vollständigem Code. Falscher, abgelaufener oder gesperrter Code sowie eine fehlende Identifikation werden jetzt als verständliche Meldung angezeigt, statt die Seite abstürzen zu lassen. Ohne Code-Pflicht bleibt die Seite unverändert.',
      },
    ],
  },
  {
    version: 'v1.14.514',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Käufer mit Rahmenvertrag: Statt der Warnung «Keine Kondition vereinbart» öffnet direkt der Dialog «Kondition für <Name> festlegen».',
        detail:
          'Fehlt bei einer Zuordnung nur die Kondition eines einzigen Käufers, der einen Rahmenvertrag hat, ist genau eine Handlung fällig — seine Kondition. Darum kommt jetzt dasselbe schlanke Fenster wie bei einem Käufer ohne Vertrag: Honorarmodell, Sonderkonditionen und der Hinweis, welche Objektkondition an diesem Objekt gilt. Speichern legt die Kondition als Anhang B an seinem Käufer-Rahmenvertrag an und ordnet ihn zu, Abbrechen ordnet nicht zu. Fehlt die Kondition der Verkäuferseite, fehlen mehrere Konditionen oder ist kein Käufer-Rahmenvertrag bekannt, bleibt es beim bisherigen Warn-Dialog mit Begründungspflicht.',
      },
    ],
  },
  {
    version: 'v1.14.513',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Identifikation: Ausweis und Selfie werden neu direkt bei Immotron erfasst — mit Kamera oder als Datei-Upload.',
        detail:
          'Die Karte «Ausweis + Selfie» leitet nicht mehr zu einem Ident-Anbieter weiter, sondern öffnet einen Erfassungs-Assistenten im selben Dialog: Ausweisart wählen, Vorder- und Rückseite sowie ein Selfie mit der Handy-Kamera oder der Webcam aufnehmen (mit Kamerawahl, Vorschau und «Wiederholen») — oder Fotos (JPG, PNG, HEIC) beziehungsweise eine PDF-Kopie per Drag & Drop hochladen. Beim Live-Selfie stellt der Server eine Zufallsaufgabe («Drehen Sie den Kopf leicht nach links»), die der Prüfer auf dem Foto kontrolliert. Am Schluss stehen Ausstellungsland, Ausweisnummer und Ablaufdatum; nach dem Einreichen meldet der Dialog «Eingereicht, wird geprüft» — die Freigabe erfolgt durch einen Menschen.',
      },
    ],
  },
  {
    version: 'v1.14.512',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Identifikation: Ausweis und Selfie werden neu direkt bei Immotron erfasst — mit Kamera oder als Datei-Upload.',
        detail:
          'Die Karte «Ausweis + Selfie» leitet nicht mehr zu einem Ident-Anbieter weiter, sondern öffnet einen Erfassungs-Assistenten im selben Dialog: Ausweisart wählen, Vorder- und Rückseite sowie ein Selfie mit der Handy-Kamera oder der Webcam aufnehmen (mit Kamerawahl, Vorschau und «Wiederholen») — oder Fotos (JPG, PNG, HEIC) beziehungsweise eine PDF-Kopie per Drag & Drop hochladen. Beim Live-Selfie stellt der Server eine Zufallsaufgabe («Drehen Sie den Kopf leicht nach links»), die der Prüfer auf dem Foto kontrolliert. Am Schluss stehen Ausstellungsland, Ausweisnummer und Ablaufdatum; nach dem Einreichen meldet der Dialog «Eingereicht, wird geprüft» — die Freigabe erfolgt durch einen Menschen.',
      },
    ],
  },
  {
    version: 'v1.14.511',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Zugeordnete Käufer: Das Mail-Symbol erscheint nur noch bei Käufern mit «Onboarding ausstehend» und erst nach der Freigabe des Objekts.',
        detail:
          'Käufer mit unterschriebenem Käufervertrag erhalten den Zugriff mit der Freigabe («Zugriff bei Freigabe») — sie brauchen keine Onboarding-Mail. Bisher liess sich das Symbol trotzdem klicken und endete in «E-Mail konnte nicht gesendet werden»; auch der Gruppen-Knopf zählte diese Käufer mit. Solange das Objekt nicht freigegeben ist (Quality Gate), sind Einzel- und Gruppen-Symbol deaktiviert und nennen im Tooltip «Versand nach der Freigabe». Schlägt ein Versand fehl, zeigt die Meldung jetzt den Grund der Schnittstelle statt nur «Versand fehlgeschlagen».',
      },
    ],
  },
  {
    version: 'v1.14.510',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Zugeordnete Käufer: Das Mail-Symbol erscheint nur noch bei Käufern mit «Onboarding ausstehend» und erst nach der Freigabe des Objekts.',
        detail:
          'Käufer mit unterschriebenem Käufervertrag erhalten den Zugriff mit der Freigabe («Zugriff bei Freigabe») — sie brauchen keine Onboarding-Mail. Bisher liess sich das Symbol trotzdem klicken und endete in «E-Mail konnte nicht gesendet werden»; auch der Gruppen-Knopf zählte diese Käufer mit. Solange das Objekt nicht freigegeben ist (Quality Gate), sind Einzel- und Gruppen-Symbol deaktiviert und nennen im Tooltip «Versand nach der Freigabe». Schlägt ein Versand fehl, zeigt die Meldung jetzt den Grund der Schnittstelle statt nur «Versand fehlgeschlagen».',
      },
    ],
  },
  {
    version: 'v1.14.509',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Quality Gate: manuell zugeordnete Käufer stehen nicht mehr als «KI-Käufervorschlag» mit 0 % Eignung in der Liste.',
        detail:
          'Die API liefert alle gespeicherten Käufer-Zuordnungen in einer Liste — auch die von Hand über die Personen-Suche oder eine Investorengruppe zugeordneten. Die Liste «KI-Käufervorschläge» zeigt jetzt nur noch die Vorschläge der Matching-Engine. Manuelle Zuordnungen bleiben ausgewählt, erscheinen weiterhin unter «Zugeordnete Käufer» und erhalten bei der Freigabe wie bisher Zugriff; die Zusammenfassung «x Käufer erhalten bei Freigabe Zugriff» zählt beide.',
      },
    ],
  },
  {
    version: 'v1.14.508',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Zugeordnete Käufer: Käufer mit Vertrag stehen auf einem noch nicht freigegebenen Objekt als «Zugriff bei Freigabe» statt als «Onboarding ausstehend».',
        detail:
          'Bisher galt jede noch nicht erteilte Zuordnung als ausstehendes Onboarding — samt dem falschen Hinweis «Bei der Freigabe übersprungen: kein aktiver Käufer-Vertrag». Neu werden drei Zustände unterschieden: «Zugriff aktiv», «Onboarding ausstehend» (kein aktiver Käufer-Vertrag) und «Zugriff bei Freigabe» (Vertrag vorhanden, Objekt noch in Prüfung). Der Hinweis zum übersprungenen Grant erscheint nur noch, wenn wirklich jemand ohne Vertrag zugeordnet ist.',
      },
      {
        type: 'fix',
        text: 'Zugeordnete Käufer: kein «weicht vom Rahmenvertrag ab», wenn der Käufer-Rahmenvertrag auf «Je Objekt (Anhang B)» steht.',
        detail:
          'Ein variabler Rahmenvertrag trägt gar keinen eigenen Satz — es gibt also nichts, wovon die Objekt- oder Anhang-B-Kondition abweichen könnte. Statt der Abweichungs-Warnung nennt die Zeile die eigene Kondition neu als «eigene Kondition für dieses Objekt».',
      },
      {
        type: 'improvement',
        text: 'Zugeordnete Käufer: Badges brechen in schmalen Spalten unter Name und E-Mail um, statt den Text zu verdrängen.',
        detail:
          'Name und E-Mail stehen je auf einer Zeile und zeigen den vollen Wert als Tooltip. Die Gruppenzeile ohne Firma trägt neu den Namen der Person, statt «Einzelpersonen» auf einzelne Buchstaben zu kürzen.',
      },
    ],
  },
  {
    version: 'v1.14.507',
    date: '24. September 2026',
    changes: [
      { type: 'feature', text: 'Zugeordnete Käufer zeigen Zugriff bei Freigabe statt Onboarding-Status' },
      { type: 'fix', text: 'Variablkondition zeigt nicht mehr «weicht ab» an' },
      { type: 'improvement', text: 'Veraltete KYC-Review-Komponenten und Dialoge entfernt' },
    ],
  },
  {
    version: 'v1.14.506',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Zugeordnete Käufer: Käufer mit Vertrag stehen auf einem noch nicht freigegebenen Objekt als «Zugriff bei Freigabe» statt als «Onboarding ausstehend».',
        detail:
          'Bisher galt jede noch nicht erteilte Zuordnung als ausstehendes Onboarding — samt dem falschen Hinweis «Bei der Freigabe übersprungen: kein aktiver Käufer-Vertrag». Neu werden drei Zustände unterschieden: «Zugriff aktiv», «Onboarding ausstehend» (kein aktiver Käufer-Vertrag) und «Zugriff bei Freigabe» (Vertrag vorhanden, Objekt noch in Prüfung). Der Hinweis zum übersprungenen Grant erscheint nur noch, wenn wirklich jemand ohne Vertrag zugeordnet ist.',
      },
      {
        type: 'fix',
        text: 'Zugeordnete Käufer: kein «weicht vom Rahmenvertrag ab», wenn der Käufer-Rahmenvertrag auf «Je Objekt (Anhang B)» steht.',
        detail:
          'Ein variabler Rahmenvertrag trägt gar keinen eigenen Satz — es gibt also nichts, wovon die Objekt- oder Anhang-B-Kondition abweichen könnte. Statt der Abweichungs-Warnung nennt die Zeile die eigene Kondition neu als «eigene Kondition für dieses Objekt».',
      },
      {
        type: 'improvement',
        text: 'Zugeordnete Käufer: Badges brechen in schmalen Spalten unter Name und E-Mail um, statt den Text zu verdrängen.',
        detail:
          'Name und E-Mail stehen je auf einer Zeile und zeigen den vollen Wert als Tooltip. Die Gruppenzeile ohne Firma trägt neu den Namen der Person, statt «Einzelpersonen» auf einzelne Buchstaben zu kürzen.',
      },
    ],
  },
  {
    version: 'v1.14.505',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Neue Admin-Seite «Identifikationen prüfen»: eingereichte Selbst-Identifikationen mit Bildern bzw. PDF nebeneinander, Erfassungsart und Entscheid «Freigeben»/«Ablehnen».',
        detail:
          'Unter Einstellungen → Identifikationen prüfen sieht der GwG-Verantwortliche alle eingereichten Identifikationen, älteste zuerst. Je Fall stehen Ausweis vorne/hinten und Selfie (bzw. das PDF) nebeneinander, dazu die Erfassungsart (live aufgenommen oder hochgeladen), die Lebendigkeits-Aufgabe, die Ausweis- und Kontaktdaten sowie das Ergebnis des automatischen Prüflaufs als Hinweis. Eine Ablehnung verlangt eine Begründung; bei Vier-Augen zeigt die Seite, dass eine erste Freigabe vorliegt und eine zweite Person gegenzeichnen muss.',
        translations: {
          en: {
            text: 'New admin page “Review identifications”: submitted self-identifications with images or PDF side by side, capture mode and the approve/reject decision.',
            detail:
              'Under Settings → Review identifications the AMLA officer sees all submitted identifications, oldest first. Each case shows the ID document front/back and the selfie (or the PDF) side by side, plus the capture mode (captured live or uploaded), the liveness task, the document and contact data and the result of the automatic check run as a hint. A rejection requires a reason; with four-eyes the page shows that a first approval exists and a second person has to countersign.',
          },
          fr: {
            text: 'Nouvelle page d’administration « Vérifier les identifications » : auto-identifications soumises avec images ou PDF côte à côte, mode de saisie et décision d’approbation ou de refus.',
            detail:
              'Sous Paramètres → Vérifier les identifications, le responsable LBA voit toutes les identifications soumises, les plus anciennes en premier. Chaque cas affiche le recto/verso de la pièce d’identité et le selfie (ou le PDF) côte à côte, ainsi que le mode de saisie (prise en direct ou téléversée), la tâche de vivacité, les données du document et du contact et le résultat du contrôle automatique à titre indicatif. Un refus exige un motif ; avec le double contrôle, la page indique qu’une première approbation existe et qu’une deuxième personne doit contresigner.',
          },
          it: {
            text: 'Nuova pagina di amministrazione «Verificare le identificazioni»: auto-identificazioni inoltrate con immagini o PDF affiancati, modalità di acquisizione e decisione di approvazione o rifiuto.',
            detail:
              'In Impostazioni → Verificare le identificazioni il responsabile LRD vede tutte le identificazioni inoltrate, dalla più vecchia. Ogni caso mostra fronte/retro del documento e il selfie (oppure il PDF) affiancati, oltre alla modalità di acquisizione (ripresa dal vivo o caricata), al compito di vivacità, ai dati del documento e del contatto e al risultato del controllo automatico come indicazione. Un rifiuto richiede una motivazione; con il doppio controllo la pagina indica che esiste una prima approvazione e che serve la controfirma di una seconda persona.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.504',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Dialog «Keine Kondition vereinbart»: Knopf «Kondition für diesen Käufer festlegen» erscheint jetzt auch dann, wenn der Käufer-Rahmenvertrag nur der API bekannt ist.',
        detail:
          'Bisher suchte die Oberfläche den Käufer-Rahmenvertrag selbst über die Vertragsliste. Fand sie ihn nicht, blieb der Knopf stumm, obwohl der Hinweistext den Weg «… oder für diesen Käufer festlegen» nannte — es blieben nur «Kondition hinterlegen» und «Trotzdem zuordnen». Neu nennt die 409-Antwort den Vertrag je Käufer-Eintrag; dieser Wert gilt vorrangig, im Quality Gate wie im Inserat. Er trägt auch das Speichern: der Anhang B wird an genau diesem Vertrag angelegt. Meldet die API «kein aktiver Vertrag», steht wie bisher der Grund statt eines toten Knopfs.',
      },
    ],
  },
  {
    version: 'v1.14.503',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Dialog «Geplante Kondition festlegen»: nennt neu die tatsächliche Objektkondition des Objekts und warnt, wenn keine hinterlegt ist.',
        detail:
          'Bisher hiess es dort «es bleibt bei der Objektkondition aus dem Rahmenvertrag (Flat)» — fachlich falsch, denn die Objektkondition hängt am Objekt, nicht am Rahmenvertrag. Neu steht der Name des tatsächlich hinterlegten Honorarmodells in der Auswahl «Keine Angabe». Ist am Objekt nur eine Sonderkondition ohne Honorarmodell hinterlegt, heisst sie dort «Sonderkondition». Hat das Objekt gar keine Objektkondition, warnt der Dialog, dass für diesen Käufer nach seiner Unterschrift keine Kondition gilt, und verlinkt direkt auf die Konditionen-Karte des Objekts.',
      },
    ],
  },
  {
    version: 'v1.14.502',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dialog «Keine Kondition vereinbart»: Käufer-Lücken nennen neu «Anhang B für diesen Käufer», und bei einem Rahmenvertrag «variabel» erscheint ein Hinweis.',
        detail:
          'Bisher hiess die Stufe auch beim Käufer «Anhang B (Objektfreigabe)» — das ist die Verkäuferseite. Neu wird die Stufe in der Rolle der jeweiligen Lücke benannt. Steht der Rahmenvertrag auf «variabel», sagt der Dialog ausserdem, dass Konditionen je Objekt vereinbart werden und das Standardmodell hier nicht gilt.',
        translations: {
          en: {
            text: 'Dialog «No terms agreed»: buyer gaps now say «Appendix B for this buyer», and a framework agreement set to «variable» shows a hint.',
            detail:
              'Previously the stage was labelled «Appendix B (property release)» for buyers too — that is the seller side. The stage is now named according to the role of each gap. If the framework agreement is «variable», the dialog also states that terms are agreed per property and the default model does not apply.',
          },
          fr: {
            text: 'Dialogue «Aucune condition convenue» : les lacunes acheteur indiquent désormais «Annexe B pour cet acheteur», et un contrat-cadre «variable» affiche une note.',
            detail:
              'Jusqu’ici, l’étape s’appelait «Annexe B (validation de l’objet)» aussi pour l’acheteur — c’est le côté vendeur. L’étape est désormais nommée selon le rôle de chaque lacune. Si le contrat-cadre est «variable», le dialogue précise en outre que les conditions sont convenues par objet et que le modèle standard ne s’applique pas.',
          },
          it: {
            text: 'Dialogo «Nessuna condizione concordata»: le lacune dell’acquirente indicano ora «Allegato B per questo acquirente» e un contratto quadro «variabile» mostra una nota.',
            detail:
              'Finora la fase si chiamava «Allegato B (rilascio immobile)» anche per l’acquirente — ma è il lato venditore. Ora la fase viene denominata in base al ruolo di ogni lacuna. Se il contratto quadro è «variabile», il dialogo precisa inoltre che le condizioni sono concordate per immobile e che il modello standard non si applica.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.501',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Hält der Mandant das Verkaufsmandat ausserhalb von Immotron, zeigen Kopf, Auftraggeber-Panel und «Auftrag & Vermarktung» neu «Eigenes Mandat (ausserhalb Immotron)».',
        detail:
          'Bisher erschien in diesem Fall «Aktiver Vermarktungsauftrag», obwohl kein Vertrag auf der Plattform vorliegt. Jetzt nennt das Auftraggeber-Panel den Fall beim Namen, blendet Vertrags- und Honorarblock aus und weist darauf hin, dass der Verkaufsauftrag ausserhalb der Plattform vorliegt. Der Status bleibt grün, weil der Auftrag gültig ist.',
        translations: {
          en: {
            text: 'When the tenant holds the sales mandate outside Immotron, the header, principal panel and «Mandate & marketing» now show «Own mandate (outside Immotron)».',
            detail:
              'Previously this showed «Active marketing mandate» even though no contract exists on the platform. The principal panel now names the case, hides the contract and commission blocks and states that the sales mandate exists outside the platform. The status stays green because the mandate is valid.',
          },
          fr: {
            text: 'Lorsque le mandant détient le mandat de vente hors Immotron, l’en-tête, le panneau du mandant et «Mandat & commercialisation» affichent désormais «Mandat propre (hors Immotron)».',
            detail:
              'Auparavant, «Mandat de commercialisation actif» s’affichait bien qu’aucun contrat n’existe sur la plateforme. Le panneau du mandant nomme désormais le cas, masque les blocs contrat et honoraires et précise que le mandat de vente existe en dehors de la plateforme. Le statut reste vert, car le mandat est valable.',
          },
          it: {
            text: 'Se il mandante detiene l’incarico di vendita fuori da Immotron, intestazione, pannello del mandante e «Incarico e commercializzazione» mostrano ora «Mandato proprio (fuori da Immotron)».',
            detail:
              'Finora compariva «Incarico di commercializzazione attivo» pur non esistendo alcun contratto sulla piattaforma. Il pannello del mandante indica ora il caso, nasconde i blocchi contratto e onorario e precisa che l’incarico di vendita esiste al di fuori della piattaforma. Lo stato resta verde perché l’incarico è valido.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.500',
    date: '24. September 2026',
    changes: [
      { type: 'feature', text: 'Auftraggeber-Kopf zeigt neuen Status «Eigenes Mandat ausserhalb»' },
      { type: 'improvement', text: 'Auftraggeber-Komponenten konsolidiert für bessere Wartbarkeit' },
    ],
  },
  {
    version: 'v1.14.499',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Hält der Mandant das Verkaufsmandat ausserhalb von Immotron, zeigen Kopf, Auftraggeber-Panel und «Auftrag & Vermarktung» neu «Eigenes Mandat (ausserhalb Immotron)».',
        detail:
          'Bisher erschien in diesem Fall «Aktiver Vermarktungsauftrag», obwohl kein Vertrag auf der Plattform vorliegt. Jetzt nennt das Auftraggeber-Panel den Fall beim Namen, blendet Vertrags- und Honorarblock aus und weist darauf hin, dass der Verkaufsauftrag ausserhalb der Plattform vorliegt. Der Status bleibt grün, weil der Auftrag gültig ist.',
        translations: {
          en: {
            text: 'When the tenant holds the sales mandate outside Immotron, the header, principal panel and «Mandate & marketing» now show «Own mandate (outside Immotron)».',
            detail:
              'Previously this showed «Active marketing mandate» even though no contract exists on the platform. The principal panel now names the case, hides the contract and commission blocks and states that the sales mandate exists outside the platform. The status stays green because the mandate is valid.',
          },
          fr: {
            text: 'Lorsque le mandant détient le mandat de vente hors Immotron, l’en-tête, le panneau du mandant et «Mandat & commercialisation» affichent désormais «Mandat propre (hors Immotron)».',
            detail:
              'Auparavant, «Mandat de commercialisation actif» s’affichait bien qu’aucun contrat n’existe sur la plateforme. Le panneau du mandant nomme désormais le cas, masque les blocs contrat et honoraires et précise que le mandat de vente existe en dehors de la plateforme. Le statut reste vert, car le mandat est valable.',
          },
          it: {
            text: 'Se il mandante detiene l’incarico di vendita fuori da Immotron, intestazione, pannello del mandante e «Incarico e commercializzazione» mostrano ora «Mandato proprio (fuori da Immotron)».',
            detail:
              'Finora compariva «Incarico di commercializzazione attivo» pur non esistendo alcun contratto sulla piattaforma. Il pannello del mandante indica ora il caso, nasconde i blocchi contratto e onorario e precisa che l’incarico di vendita esiste al di fuori della piattaforma. Lo stato resta verde perché l’incarico è valido.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.498',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation: verknüpfte Firmen zeigen die Art der Beziehung, die Revisionsstelle steht im Registerauszug.',
        detail:
          'Jede verknüpfte Organisation trägt neu ein kleines Etikett mit der Art der Beziehung (Mutter/Tochter, Schwesterfirma, Partner, Lieferant, Kunde, Vorgänger, Revisionsstelle, Sonstige). Ist eine Revisionsstelle verknüpft, erscheint sie zusätzlich als eigene Zeile «Revisionsstelle: <Firma> (UID)» im Registerauszug der Organisation — ein Klick öffnet die Revisionsfirma.',
      },
    ],
  },
  {
    version: 'v1.14.497',
    date: '24. September 2026',
    changes: [
      { type: 'improvement', text: 'Organisation: Art der Firmen-Beziehung anzeigen, Revisionsstelle in den Stammdaten' },
    ],
  },
  {
    version: 'v1.14.496',
    date: '24. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisation: verknüpfte Firmen zeigen die Art der Beziehung, die Revisionsstelle steht im Registerauszug.',
        detail:
          'Jede verknüpfte Organisation trägt neu ein kleines Etikett mit der Art der Beziehung (Mutter/Tochter, Schwesterfirma, Partner, Lieferant, Kunde, Vorgänger, Revisionsstelle, Sonstige). Ist eine Revisionsstelle verknüpft, erscheint sie zusätzlich als eigene Zeile «Revisionsstelle: <Firma> (UID)» im Registerauszug der Organisation — ein Klick öffnet die Revisionsfirma.',
      },
    ],
  },
  {
    version: 'v1.14.495',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Karte «Konditionen» zeigt nur noch die Kondition des Objekts — keine Werte mehr aus dem Vertrag des Auftraggebers und kein vorausgewähltes Standardmodell.',
        detail:
          'Bisher las die Karte den Vertrag des Auftraggebers mit: beim eigenen Mandat erschien dessen Gebührentext als «Sonderkondition» des Objekts und konnte versehentlich gespeichert werden. Ohne gespeicherte Kondition war zudem das Standardmodell vorausgewählt, sodass «Speichern» es dauerhaft übernahm. Jetzt startet das Formular leer, ein Hinweis nennt, was stattdessen je Käufer gilt, und der Eintrag «Kein Honorarmodell» entfernt eine gesetzte Objektkondition wieder. «Für alle festlegen» im Quality Gate und auf der Inserat-Seite ist neu auch ohne Vertrag nutzbar.',
        translations: {
          en: {
            text: 'The «Conditions» card now shows only the property’s own condition — no values from the principal’s contract and no pre-selected standard model.',
            detail:
              'Previously the card also read the principal’s contract: for our own mandate its fee wording showed up as a «special condition» of the property and could be saved by mistake. Without a stored condition the standard model was pre-selected, so «Save» adopted it permanently. The form now starts empty, a note states what applies per buyer instead, and the entry «No fee model» clears a stored property condition again. «Set for all» in the quality gate and on the listing page works without a contract.',
          },
          fr: {
            text: 'La carte « Conditions » n’affiche plus que la condition de l’objet — aucune valeur du contrat du mandant et aucun modèle standard présélectionné.',
            detail:
              'Jusqu’ici, la carte lisait aussi le contrat du mandant : pour notre propre mandat, son texte d’honoraires apparaissait comme « condition particulière » de l’objet et pouvait être enregistré par erreur. Sans condition enregistrée, le modèle standard était présélectionné et « Enregistrer » le reprenait durablement. Le formulaire démarre désormais vide, un avis indique ce qui s’applique par acheteur et l’entrée « Aucun modèle d’honoraires » supprime à nouveau une condition d’objet enregistrée. « Définir pour tous » fonctionne aussi sans contrat.',
          },
          it: {
            text: 'La scheda «Condizioni» mostra solo la condizione dell’oggetto — nessun valore dal contratto del mandante e nessun modello standard preselezionato.',
            detail:
              'Finora la scheda leggeva anche il contratto del mandante: per il mandato proprio il suo testo sugli onorari compariva come «condizione particolare» dell’oggetto e poteva essere salvato per errore. Senza una condizione salvata era preselezionato il modello standard, così «Salva» lo rendeva definitivo. Ora il modulo parte vuoto, un avviso indica che cosa vale per ogni acquirente e la voce «Nessun modello di onorario» rimuove di nuovo una condizione dell’oggetto salvata. «Definisci per tutti» funziona anche senza contratto.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.494',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'AGB § 1.2 definiert den Mandanten neu als Vermittlerin; neuer § 1.5 zum Lizenzverhältnis zwischen Betreiberin und Mandant.',
        detail:
          'Bisher beschrieb § 1.2 den Mandanten als Partnerfirma «z. B. Verkäufer-Auftraggeberin» — Mandant ist aber die auf der Plattform tätige Vermittlerin, die im eigenen Namen mit Käufern und Verkäufern abschliesst. Der neue § 1.5 stellt klar, dass die Lizenz- bzw. Plattformgebühr allein zwischen Betreiberin und Mandant gilt und das Honorar des Nutzers nicht berührt.',
      },
    ],
  },
  {
    version: 'v1.14.493',
    date: '24. September 2026',
    changes: [
      { type: 'feature', text: 'AGB erweitert: neue Sektion 1.5 zum Lizenzverhältnis Betreiberin–Mandant' },
      { type: 'improvement', text: 'Preisgestaltung und Property-Review-Tab an aktualisierte AGB-Definition angepasst' },
    ],
  },
  {
    version: 'v1.14.492',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'AGB § 1.2 definiert den Mandanten neu als Vermittlerin; neuer § 1.5 zum Lizenzverhältnis zwischen Betreiberin und Mandant.',
        detail:
          'Bisher beschrieb § 1.2 den Mandanten als Partnerfirma «z. B. Verkäufer-Auftraggeberin» — Mandant ist aber die auf der Plattform tätige Vermittlerin, die im eigenen Namen mit Käufern und Verkäufern abschliesst. Der neue § 1.5 stellt klar, dass die Lizenz- bzw. Plattformgebühr allein zwischen Betreiberin und Mandant gilt und das Honorar des Nutzers nicht berührt.',
      },
    ],
  },
  {
    version: 'v1.14.491',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Die Karte «Konditionen» steht neu im Tab «Auftrag & Vermarktung» statt in «Preise & Miete».',
        detail:
          'Die Objektkondition (Honorarmodell und Sonderkonditionen) gehört zum Auftrag und steht darum direkt unter dem Auftraggeber. Die Verweise aus dem Hinweis «Kondition nicht vereinbart» im Quality Gate und auf der Inserat-Seite springen direkt dorthin. An der Karte selbst ändert sich nichts.',
        translations: {
          en: {
            text: 'The «Conditions» card has moved to the «Mandate & marketing» tab, out of «Prices & rent».',
            detail:
              'The property condition (commission model and special conditions) belongs to the mandate and now sits right below the principal. The links from the «No condition agreed» notice in the quality gate and on the listing page jump straight to it. The card itself is unchanged.',
          },
          fr: {
            text: 'La carte « Conditions » se trouve désormais dans l’onglet « Mandat et commercialisation » au lieu de « Prix et loyers ».',
            detail:
              'La condition de l’objet (modèle d’honoraires et conditions spéciales) fait partie du mandat et figure donc juste sous le mandant. Les liens de l’avis « Condition non convenue » dans le quality gate et sur la page de l’annonce y mènent directement. La carte elle-même reste inchangée.',
          },
          it: {
            text: 'La scheda «Condizioni» si trova ora nella scheda «Mandato e commercializzazione» invece che in «Prezzi e affitto».',
            detail:
              'La condizione dell’oggetto (modello di onorario e condizioni speciali) fa parte del mandato ed è quindi collocata subito sotto il mandante. I collegamenti dall’avviso «Condizione non concordata» nel quality gate e nella pagina dell’annuncio portano direttamente lì. La scheda stessa resta invariata.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.490',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Im Quality Gate erscheinen bereits gespeicherte Käufer-Zuordnungen beim Öffnen eines Objekts sofort wieder.',
        detail:
          'Bisher war die Liste nach einem Objektwechsel leer, bis die Käufer-Vorschläge neu berechnet wurden — manuell zugeordnete Interessenten schienen dadurch verschwunden. Die gespeicherten Zuordnungen werden jetzt beim Öffnen geladen, mit ihrer Auswahl. Eine Neuberechnung startet weiterhin nur auf Knopfdruck.',
        translations: {
          en: {
            text: 'In the quality gate, already saved buyer assignments reappear immediately when a property is opened.',
            detail:
              'Previously the list was empty after switching properties until the buyer suggestions were recomputed, which made manually assigned prospects look lost. The saved assignments are now loaded when the property is opened, including their selection. A recomputation still only starts on demand.',
          },
          fr: {
            text: 'Dans le quality gate, les acheteurs déjà attribués réapparaissent immédiatement à l’ouverture d’un objet.',
            detail:
              'Jusqu’ici, la liste restait vide après un changement d’objet tant que les propositions d’acheteurs n’étaient pas recalculées ; les prospects attribués manuellement semblaient perdus. Les attributions enregistrées sont désormais chargées à l’ouverture, avec leur sélection. Un recalcul ne démarre toujours que sur demande.',
          },
          it: {
            text: 'Nel quality gate le assegnazioni di acquirenti già salvate ricompaiono subito all’apertura di un oggetto.',
            detail:
              'Finora l’elenco restava vuoto dopo il cambio di oggetto finché le proposte di acquirenti non venivano ricalcolate, così gli interessati assegnati manualmente sembravano spariti. Le assegnazioni salvate vengono ora caricate all’apertura, con la loro selezione. Il ricalcolo parte ancora solo su richiesta.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.489',
    date: '24. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Karte «Konditionen» am Objekt speichert die Objektkondition jetzt direkt am Objekt — auch ohne verknüpften Rahmenvertrag.',
        detail:
          'Honorarmodell und Sonderkonditionen werden am Objekt gespeichert statt als Anhang B an einem Verkäufer-Rahmenvertrag. Die Karte ist damit immer bedienbar; nach dem Neuladen erscheinen die Werte mit der Quelle «Objekt». Sie gelten für alle Käufer dieses Objekts, sofern kein individueller Anhang B besteht.',
        translations: {
          en: {
            text: 'The «Terms» card on a property now stores the property terms on the property itself — even without a linked framework agreement.',
            detail:
              'The commission model and special terms are stored on the property instead of as Appendix B on a seller framework agreement. The card is therefore always editable; after a reload the values appear with the source «Property». They apply to all buyers of this property unless an individual Appendix B exists.',
          },
          fr: {
            text: 'La carte « Conditions » de l’objet enregistre désormais les conditions directement sur l’objet — même sans contrat-cadre lié.',
            detail:
              'Le modèle d’honoraires et les conditions particulières sont enregistrés sur l’objet au lieu d’une annexe B au contrat-cadre du vendeur. La carte est donc toujours utilisable ; après rechargement, les valeurs s’affichent avec la source « Objet ». Elles s’appliquent à tous les acheteurs de cet objet, sauf annexe B individuelle.',
          },
          it: {
            text: 'La scheda «Condizioni» dell’oggetto salva ora la condizione direttamente sull’oggetto — anche senza contratto quadro collegato.',
            detail:
              'Il modello di onorario e le condizioni particolari vengono salvati sull’oggetto invece che come allegato B a un contratto quadro del venditore. La scheda è quindi sempre utilizzabile; dopo il ricaricamento i valori compaiono con la fonte «Oggetto». Valgono per tutti gli acquirenti di questo oggetto, salvo un allegato B individuale.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.488',
    date: '24. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Im Objekt-Tab «Review» heisst das Feld im Abschnitt Auftraggeber jetzt schlicht «Auftraggeber».',
        detail:
          'Der Zusatz «(CRM-Kontakt)» ist entfallen. Er wurde bei leerem Feld als vorhandener Kontakt missverstanden, und der Auftraggeber ist meist eine Firma.',
        translations: {
          en: {
            text: 'In the property «Review» tab the field in the principal section is now simply labelled «Principal».',
            detail:
              'The «(CRM contact)» suffix has been dropped. It was mistaken for an existing contact when the field was empty, and the principal is usually a company.',
          },
          fr: {
            text: 'Dans l’onglet « Review » de l’objet, le champ de la section mandant s’appelle désormais simplement « Mandant ».',
            detail:
              'La mention « (contact CRM) » a été supprimée. Elle était comprise comme un contact existant lorsque le champ était vide, et le mandant est le plus souvent une société.',
          },
          it: {
            text: 'Nella scheda «Review» dell’oggetto il campo nella sezione mandante si chiama ora semplicemente «Mandante».',
            detail:
              'L’aggiunta «(contatto CRM)» è stata rimossa. Con il campo vuoto veniva intesa come un contatto esistente e il mandante è di solito un’azienda.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.487',
    date: '23. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Eine bereits vergebene E-Mail-Adresse lässt sich jetzt direkt auflösen statt nur als Fehler zu erscheinen.',
        detail:
          'Meldet die Speicherung, dass die Adresse schon bei einem Kontakt oder einer Organisation liegt, öffnet sich ein Dialog mit dem bestehenden Eintrag: «Zu Kontakt wechseln» führt auf dessen Detailseite, «Zusammenführen» öffnet den Zusammenführen-Dialog mit beiden Kontakten vorbelegt. Erkennt die Prüfung eine generische Adresse wie info@, bietet der Dialog an, sie an die Organisation zu übernehmen oder bewusst trotzdem beim Kontakt zu speichern.',
        translations: {
          en: {
            text: 'An email address that is already in use can now be resolved right away instead of only showing an error.',
            detail:
              'If saving reports that the address already belongs to a contact or an organisation, a dialog shows the existing entry: «Switch to contact» opens its detail page, «Merge» opens the merge dialog with both contacts preselected. If the check flags a generic address such as info@, the dialog offers to move it to the organisation or to save it on the contact anyway.',
          },
          fr: {
            text: 'Une adresse e-mail déjà utilisée peut désormais être traitée directement au lieu de n’afficher qu’une erreur.',
            detail:
              'Si l’enregistrement signale que l’adresse appartient déjà à un contact ou à une organisation, une boîte de dialogue affiche l’entrée existante : « Aller au contact » ouvre sa page de détail, « Fusionner » ouvre la fusion avec les deux contacts préremplis. Pour une adresse générique comme info@, la boîte de dialogue propose de la transférer à l’organisation ou de l’enregistrer quand même sur le contact.',
          },
          it: {
            text: 'Un indirizzo e-mail già in uso può ora essere risolto subito, invece di mostrare solo un errore.',
            detail:
              'Se il salvataggio segnala che l’indirizzo appartiene già a un contatto o a un’organizzazione, una finestra mostra la voce esistente: «Vai al contatto» apre la sua pagina di dettaglio, «Unisci» apre l’unione con entrambi i contatti preimpostati. Per un indirizzo generico come info@, la finestra propone di trasferirlo all’organizzazione o di salvarlo comunque sul contatto.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.486',
    date: '23. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Die Kurzvorschauen bei Kontakten, Organisationen und Auftraggebern verhalten sich jetzt überall gleich.',
        detail:
          'Alle drei Vorschau-Fenster nutzen dieselbe Hülle: Escape schliesst, ein Klick auf den Hintergrund schliesst, die von Hand eingestellte Breite bleibt gemerkt, und die Tastatur-Navigation bleibt im geöffneten Fenster statt dahinter auf der Seite zu landen.',
        translations: {
          en: {
            text: 'Preview panels for contacts, organisations and principals now behave identically.',
            detail:
              'All three previews share one shell: Escape closes them, a click on the backdrop closes them, the width you drag is remembered, and keyboard navigation stays inside the open panel instead of drifting onto the page behind it.',
          },
          fr: {
            text: 'Les aperçus des contacts, organisations et mandants se comportent désormais de façon identique.',
            detail:
              'Les trois aperçus partagent la même enveloppe : Échap les ferme, un clic sur l’arrière-plan les ferme, la largeur réglée à la main est mémorisée et la navigation au clavier reste dans le panneau ouvert.',
          },
          it: {
            text: 'Le anteprime di contatti, organizzazioni e committenti si comportano ora allo stesso modo.',
            detail:
              'Le tre anteprime usano la stessa struttura: Esc le chiude, un clic sullo sfondo le chiude, la larghezza impostata manualmente viene ricordata e la navigazione da tastiera resta nel pannello aperto.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.485',
    date: '23. September 2026',
    changes: [
      {
        type: 'improvement',
        text: '«Neue Organisation» und «Neuer Kontakt» öffnen neu einen kurzen Dialog statt des vollen Bearbeiten-Fensters.',
        detail:
          'Bei Organisationen sucht Schritt 1 gleichzeitig im CRM und im Handelsregister: ein bestehender Treffer führt mit «Zu bestehendem Eintrag» direkt zur Organisation, ein Zefix-Treffer belegt Schritt 2 vor. Schritt 2 fragt nur noch Name, Typ und Sitz. Beim Kontakt genügen Vor- und Nachname; die E-Mail ist freiwillig und wird während der Eingabe auf Dubletten geprüft, die Organisation lässt sich direkt mitverknüpfen. Nach dem Anlegen landet man jeweils auf der Detailseite; das alte Organisations-Fenster mit Auto-Entwurf wurde entfernt.',
        translations: {
          en: {
            text: '"New organisation" and "New contact" now open a short dialog instead of the full edit window.',
            detail:
              'For organisations, step 1 searches the CRM and the commercial register at the same time: an existing hit leads straight to the organisation via "Go to existing entry", a Zefix hit pre-fills step 2. Step 2 only asks for name, type and registered office. For a contact, first and last name are enough; the e-mail is optional and checked for duplicates while typing, and the organisation can be linked right away. After creating, you land on the detail page; the old organisation window with its auto-draft has been removed.',
          },
          fr: {
            text: '« Nouvelle organisation » et « Nouveau contact » ouvrent désormais un dialogue court au lieu de la fenêtre de modification complète.',
            detail:
              'Pour les organisations, l’étape 1 cherche simultanément dans le CRM et au registre du commerce : une entrée existante mène directement à l’organisation via « Vers l’entrée existante », un résultat Zefix préremplit l’étape 2. L’étape 2 ne demande que le nom, le type et le siège. Pour un contact, le prénom et le nom suffisent ; l’e-mail est facultatif et contrôlé en doublon pendant la saisie, et l’organisation peut être liée directement. Après la création, on arrive sur la page de détail ; l’ancienne fenêtre d’organisation avec brouillon automatique a été supprimée.',
          },
          it: {
            text: '«Nuova organizzazione» e «Nuovo contatto» aprono ora un breve dialogo al posto della finestra di modifica completa.',
            detail:
              'Per le organizzazioni il passo 1 cerca contemporaneamente nel CRM e nel registro di commercio: una voce esistente porta direttamente all’organizzazione con «Vai alla voce esistente», un risultato Zefix precompila il passo 2. Il passo 2 chiede solo nome, tipo e sede. Per un contatto bastano nome e cognome; l’e-mail è facoltativa e viene controllata per doppioni durante la digitazione, e l’organizzazione può essere collegata subito. Dopo la creazione si arriva sulla pagina di dettaglio; la vecchia finestra delle organizzazioni con bozza automatica è stata rimossa.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.484',
    date: '23. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Das Kontakt-Bearbeiten-Fenster entfällt — «Bearbeiten» führt überall direkt auf die Kontakt-Detailseite.',
        detail:
          'In der CRM-Kontaktliste, der Admin-Kontaktliste und im Reiter «Kontakte» einer Organisation öffnet ein Klick auf eine Person neu direkt die Detailseite; ein frisch angelegter Kontakt landet dort ebenfalls sofort. In der Pendenz-Werkstatt öffnet der Kontaktname in der Übersicht einen neuen Tab, damit der Entwurf erhalten bleibt. Das alte Fenster wurde entfernt.',
        translations: {
          en: {
            text: 'The contact edit dialog is gone — “Edit” now always opens the contact detail page.',
            detail:
              'In the CRM contact list, the admin contact list and an organisation’s “Contacts” tab, clicking a person now opens the detail page directly; a newly created contact lands there straight away. In the task workbench the contact name opens in a new tab so the draft is preserved. The old dialog has been removed.',
          },
          fr: {
            text: 'La fenêtre de modification des contacts disparaît — « Modifier » ouvre désormais la page de détail du contact.',
            detail:
              'Dans la liste des contacts CRM, la liste admin et l’onglet « Contacts » d’une organisation, un clic sur une personne ouvre directement la page de détail ; un contact fraîchement créé y arrive immédiatement. Dans l’atelier des tâches, le nom du contact s’ouvre dans un nouvel onglet afin de conserver le brouillon. L’ancienne fenêtre a été supprimée.',
          },
          it: {
            text: 'La finestra di modifica dei contatti scompare: «Modifica» apre ora la pagina di dettaglio del contatto.',
            detail:
              'Nell’elenco contatti CRM, nell’elenco admin e nella scheda «Contatti» di un’organizzazione un clic su una persona apre direttamente la pagina di dettaglio; un contatto appena creato vi arriva subito. Nell’officina delle pendenze il nome del contatto si apre in una nuova scheda, così la bozza resta. La vecchia finestra è stata rimossa.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.483',
    date: '23. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Kontakt-Detailseite: Verträge, LinkedIn, Zuständigkeit, Notizen und Wissen laufen neu direkt auf der Seite — das Bearbeiten-Fenster entfällt.',
        detail:
          'Die Übersicht zeigt LinkedIn-Recherche, Zuständigkeit und Notizen als eigene Karten: Haupteigner und Info-Eigner werden inline gesetzt und mit einem «Speichern» übernommen, Notizen speichern beim Tippen automatisch mit Status im Kartenkopf. Der Reiter «Verträge» zeigt die Verträge der Person wie bisher inkl. Sprung auf den Vertrag; «Neuer Vertrag» und «Suchmandat» stehen als einzige Aktionen im Kartenkopf. Neu gibt es den Reiter «Wissen» mit den angehefteten Wissens-Einträgen. Im Kontakt-Fenster entfallen die Reiter «Verträge», «LinkedIn», «Zuständigkeit», «Notizen» und «Wissen»; es verweist nur noch auf die Detailseite.',
        translations: {
          en: {
            text: 'Contact detail page: contracts, LinkedIn, responsibility, notes and knowledge now live directly on the page — no edit dialog needed.',
            detail:
              'The overview shows LinkedIn research, responsibility and notes as their own cards: primary and info owners are set inline and applied with a single "Save", notes save automatically while typing with the status in the card header. The "Contracts" tab lists the person\'s contracts as before, including the jump to the contract; "New contract" and "Search mandate" are the only actions and sit in the card header. A new "Knowledge" tab shows the pinned knowledge entries. The contact dialog loses its "Contracts", "LinkedIn", "Responsibility", "Notes" and "Knowledge" tabs and only points to the detail page.',
          },
          fr: {
            text: 'Fiche contact : contrats, LinkedIn, responsabilité, notes et connaissances se gèrent désormais directement sur la page — sans fenêtre d\'édition.',
            detail:
              'L\'aperçu présente la recherche LinkedIn, la responsabilité et les notes sous forme de cartes : le propriétaire principal et les propriétaires d\'info se définissent sur place et s\'appliquent avec un seul « Enregistrer », les notes s\'enregistrent automatiquement pendant la saisie avec le statut en tête de carte. L\'onglet « Contrats » affiche les contrats de la personne comme auparavant, avec accès direct au contrat ; « Nouveau contrat » et « Mandat de recherche » sont les seules actions et figurent en tête de carte. Un nouvel onglet « Connaissances » montre les entrées épinglées. La fenêtre de contact perd ses onglets « Contrats », « LinkedIn », « Responsabilité », « Notes » et « Connaissances » et renvoie uniquement vers la fiche.',
          },
          it: {
            text: 'Pagina di dettaglio del contatto: contratti, LinkedIn, responsabilità, note e conoscenze si gestiscono ora direttamente nella pagina — senza finestra di modifica.',
            detail:
              'La panoramica mostra ricerca LinkedIn, responsabilità e note come schede proprie: proprietario principale e proprietari info si impostano sul posto e si applicano con un unico «Salva», le note si salvano automaticamente durante la digitazione con lo stato nell\'intestazione della scheda. La scheda «Contratti» elenca i contratti della persona come prima, incluso il salto al contratto; «Nuovo contratto» e «Mandato di ricerca» sono le uniche azioni e stanno nell\'intestazione della scheda. La nuova scheda «Conoscenze» mostra le voci di conoscenza fissate. La finestra del contatto perde le schede «Contratti», «LinkedIn», «Responsabilità», «Note» e «Conoscenze» e rimanda solo alla pagina di dettaglio.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.482',
    date: '23. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Kontakt-Detailseite: Stammdaten, Adresse & Kontaktwege sowie Firmenrollen werden neu direkt auf der Seite bearbeitet — ohne Bearbeiten-Fenster.',
        detail:
          'Die Übersicht besteht aus Karten mit eigenem «Bearbeiten»: «Stammdaten» (Vorname, Nachname, Geschlecht, Sprache, Geburtstag, Tags) und «Adresse & Kontaktwege» (E-Mails, Telefone, Website, Adressen). Ist eine E-Mail bereits bei einem anderen Eintrag hinterlegt, bleibt der Hinweis in der Karte stehen und verlinkt den bestehenden Kontakt, statt nur kurz als Meldung aufzutauchen. Im Reiter «Firmen» lassen sich Rollen ohne Zwischenfenster hinzufügen, ändern und entfernen; die Firmensuche findet CRM-Organisationen und Zefix-Treffer in einem Feld und legt einen reinen Zefix-Treffer beim Hinzufügen an. Im Kontakt-Fenster entfallen die Reiter «Stammdaten», «Adresse & Kontakt» und «Firmen».',
        translations: {
          en: {
            text: 'Contact detail page: master data, address & contact details and company roles are now edited directly on the page — no edit dialog needed.',
            detail:
              'The overview is built from cards with their own "Edit": "Master data" (first name, last name, gender, language, birthday, tags) and "Address & contact details" (e-mails, phones, website, addresses). If an e-mail is already on file elsewhere, the notice stays in the card and links to the existing contact instead of only flashing up briefly. In the "Companies" tab, roles can be added, changed and removed without a dialog; the company search finds CRM organisations and Zefix matches in a single field and creates a Zefix-only match when it is added. The contact dialog loses its "Master data", "Address & contact" and "Companies" tabs.',
          },
          fr: {
            text: 'Fiche contact : les données de base, l\'adresse et les coordonnées ainsi que les rôles en entreprise se modifient désormais directement sur la page — sans fenêtre d\'édition.',
            detail:
              'L\'aperçu se compose de cartes avec leur propre bouton « Modifier » : « Données de base » (prénom, nom, sexe, langue, date de naissance, tags) et « Adresse et coordonnées » (e-mails, téléphones, site web, adresses). Si une adresse e-mail existe déjà ailleurs, le message reste affiché dans la carte et renvoie au contact existant au lieu de n\'apparaître que brièvement. Dans l\'onglet « Entreprises », les rôles s\'ajoutent, se modifient et se suppriment sans fenêtre intermédiaire ; la recherche d\'entreprise trouve les organisations du CRM et les résultats Zefix dans un seul champ et crée un résultat purement Zefix lors de l\'ajout. La fenêtre de contact perd ses onglets « Données de base », « Adresse et contact » et « Entreprises ».',
          },
          it: {
            text: 'Pagina di dettaglio del contatto: dati anagrafici, indirizzo e recapiti nonché ruoli aziendali si modificano ora direttamente nella pagina — senza finestra di modifica.',
            detail:
              'La panoramica è composta da schede con un proprio «Modifica»: «Dati anagrafici» (nome, cognome, sesso, lingua, data di nascita, tag) e «Indirizzo e recapiti» (e-mail, telefoni, sito web, indirizzi). Se un\'e-mail è già registrata altrove, l\'avviso resta nella scheda e rimanda al contatto esistente invece di comparire solo brevemente. Nella scheda «Aziende» i ruoli si aggiungono, modificano e rimuovono senza finestre intermedie; la ricerca azienda trova organizzazioni CRM e risultati Zefix in un unico campo e crea un risultato solo Zefix al momento dell\'aggiunta. La finestra del contatto perde le schede «Dati anagrafici», «Indirizzo e contatto» e «Aziende».',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.481',
    date: '23. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Kontakte haben neu eine eigene Detailseite unter «/crm/contacts/:id» — der Bereich rechts in der Liste ist nur noch eine Kurzvorschau mit «Öffnen».',
        detail:
          'Die Detailseite zeigt alle Reiter (Übersicht, Firmen, Suchkriterien, Verträge, Dokumente, KI-Recherche, Investorengruppen, Pendenzen, Aktivitäten), lässt sich als Link teilen und merkt sich den Reiter im «?tab=»-Parameter. «Zurück zur Liste» führt mit den gesetzten Filtern zurück. Der Duplikat-Hinweis steht oberhalb der Reiter und öffnet den Zusammenführen-Dialog wie bisher vorbelegt. Alte Links mit «?selected=» leiten automatisch auf die neue Seite um; das Stift-Symbol in der Liste öffnet ebenfalls die Detailseite.',
        translations: {
          en: {
            text: 'Contacts now have their own detail page at «/crm/contacts/:id» — the right-hand area of the list is only a quick preview with «Open».',
            detail:
              'The detail page shows all tabs (overview, companies, search criteria, contracts, documents, AI research, investor groups, tasks, activities), can be shared as a link and keeps the tab in the «?tab=» parameter. «Back to list» returns with the filters that were set. The duplicate notice sits above the tabs and opens the merge dialog pre-filled as before. Old «?selected=» links redirect to the new page automatically; the pencil icon in the list also opens the detail page.',
          },
          fr: {
            text: 'Les contacts ont désormais leur propre page de détail sous «/crm/contacts/:id» — la zone de droite de la liste n’est plus qu’un aperçu rapide avec «Ouvrir».',
            detail:
              'La page de détail affiche tous les onglets (aperçu, entreprises, critères de recherche, contrats, documents, recherche IA, groupes d’investisseurs, tâches, activités), peut être partagée comme lien et conserve l’onglet dans le paramètre «?tab=». «Retour à la liste» ramène aux filtres définis. L’avis de doublon se trouve au-dessus des onglets et ouvre le dialogue de fusion prérempli comme auparavant. Les anciens liens «?selected=» redirigent automatiquement vers la nouvelle page; l’icône crayon de la liste ouvre également la page de détail.',
          },
          it: {
            text: 'I contatti hanno ora una pagina di dettaglio propria su «/crm/contacts/:id» — l’area a destra dell’elenco è solo un’anteprima rapida con «Apri».',
            detail:
              'La pagina di dettaglio mostra tutte le schede (panoramica, aziende, criteri di ricerca, contratti, documenti, ricerca IA, gruppi di investitori, attività da svolgere, attività), può essere condivisa come link e memorizza la scheda nel parametro «?tab=». «Torna all’elenco» riporta ai filtri impostati. L’avviso di duplicato si trova sopra le schede e apre la finestra di unione precompilata come prima. I vecchi link «?selected=» reindirizzano automaticamente alla nuova pagina; anche l’icona della matita nell’elenco apre la pagina di dettaglio.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.480',
    date: '23. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisations-Detailseite: Zuständigkeit, Investorengruppen und Notizen werden direkt in der Karte bearbeitet — das Bearbeiten-Fenster gibt es nur noch für «Neue Organisation».',
        detail:
          'Die Übersicht hat neu Karten für «Zuständigkeit» (Verantwortlich und Info-Owner) und «Notizen»; die Notizen speichern beim Tippen automatisch und zeigen den Speicher-Status in der Karte, «Abbrechen» stellt den Ausgangsstand wieder her. Der Reiter «Investorengruppen» ist ebenfalls eine Karte: Gruppen werden zugeordnet oder entfernt und erst mit «Speichern» übernommen. Die Handelsregister-Aktionen (Zefix aktualisieren, Zefix zuordnen, Zefix entfernen) stehen jetzt beim Registerauszug der Übersicht. Das Stift-Symbol in der Liste und in der Kurzvorschau führt auf die Detailseite statt in ein Fenster.',
        translations: {
          en: {
            text: 'Organisation detail page: responsibility, investor groups and notes are edited directly in the card — the dialog is only used for «New organisation».',
            detail:
              'The overview now has cards for «Responsibility» (owner and info owners) and «Notes»; notes are saved automatically while typing and show the save status in the card, «Cancel» restores the original text. The «Investor groups» tab is a card as well: groups are assigned or removed and applied with «Save». The commercial register actions (refresh Zefix, link Zefix, remove Zefix) now sit next to the register extract in the overview. The pencil icon in the list and in the preview opens the detail page instead of a dialog.',
          },
          fr: {
            text: "Fiche organisation: responsabilité, groupes d'investisseurs et notes se modifient directement dans la carte — la fenêtre ne sert plus qu'à «Nouvelle organisation».",
            detail:
              "L'aperçu comporte désormais des cartes «Responsabilité» (responsable et responsables info) et «Notes»; les notes sont enregistrées automatiquement pendant la saisie et affichent l'état d'enregistrement dans la carte, «Annuler» rétablit le texte initial. L'onglet «Groupes d'investisseurs» est également une carte: les groupes sont attribués ou retirés et validés avec «Enregistrer». Les actions du registre du commerce (actualiser Zefix, associer Zefix, retirer Zefix) se trouvent maintenant près de l'extrait du registre dans l'aperçu. L'icône crayon de la liste et de l'aperçu ouvre la fiche au lieu d'une fenêtre.",
          },
          it: {
            text: 'Scheda organizzazione: responsabilità, gruppi di investitori e note si modificano direttamente nella scheda — la finestra serve solo per «Nuova organizzazione».',
            detail:
              "La panoramica ha nuove schede «Responsabilità» (responsabile e responsabili info) e «Note»; le note vengono salvate automaticamente durante la digitazione e mostrano lo stato di salvataggio nella scheda, «Annulla» ripristina il testo iniziale. Anche la scheda «Gruppi di investitori» è una card: i gruppi vengono assegnati o rimossi e applicati con «Salva». Le azioni del registro di commercio (aggiorna Zefix, associa Zefix, rimuovi Zefix) si trovano ora accanto all'estratto del registro nella panoramica. L'icona della matita nell'elenco e nell'anteprima apre la scheda anziché una finestra.",
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.479',
    date: '23. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisations-Detailseite: Stammdaten, Adressen, E-Mails und Telefone werden direkt in der Karte bearbeitet.',
        detail:
          'Die Übersicht einer Organisation besteht neu aus vier Karten mit «Bearbeiten»-Schaltfläche. Die Felder werden in derselben Karte editierbar, «Speichern/Abbrechen» und der Speicher-Status stehen unten in der Karte; «Abbrechen» stellt den Stand wieder her und ungespeicherte Änderungen werden beim Verlassen der Seite gemeldet. Eine bereits vergebene E-Mail-Adresse wird direkt in der E-Mail-Karte gemeldet, mit Link auf den bestehenden Eintrag. Das Bearbeiten-Fenster zeigt für bestehende Organisationen nur noch Handelsregister, Zuständigkeit, Signatur und Notizen; für neue Organisationen bleibt es unverändert.',
        translations: {
          en: {
            text: 'Organisation detail page: master data, addresses, emails and phone numbers are edited directly in the card.',
            detail:
              'The overview of an organisation now consists of four cards with an «Edit» button. The fields become editable inside the same card, «Save/Cancel» and the save status sit at the bottom of the card; «Cancel» restores the previous state and unsaved changes are flagged when leaving the page. An email address that already exists is reported inside the email card, with a link to the existing entry. For existing organisations the edit dialog now only shows commercial register, responsibility, signature and notes; for new organisations it is unchanged.',
          },
          fr: {
            text: "Fiche organisation: données de base, adresses, e-mails et téléphones se modifient directement dans la carte.",
            detail:
              "L'aperçu d'une organisation se compose désormais de quatre cartes avec un bouton «Modifier». Les champs deviennent modifiables dans la même carte, «Enregistrer/Annuler» et l'état d'enregistrement se trouvent en bas de la carte; «Annuler» rétablit l'état précédent et les modifications non enregistrées sont signalées en quittant la page. Une adresse e-mail déjà utilisée est signalée dans la carte e-mail, avec un lien vers l'entrée existante. Pour les organisations existantes, la fenêtre de modification n'affiche plus que registre du commerce, responsabilité, signature et notes; pour les nouvelles organisations, elle reste inchangée.",
          },
          it: {
            text: 'Scheda organizzazione: dati di base, indirizzi, e-mail e telefoni si modificano direttamente nella scheda.',
            detail:
              "La panoramica di un'organizzazione è composta da quattro schede con un pulsante «Modifica». I campi diventano modificabili nella stessa scheda, «Salva/Annulla» e lo stato di salvataggio si trovano in fondo alla scheda; «Annulla» ripristina lo stato precedente e le modifiche non salvate vengono segnalate quando si lascia la pagina. Un indirizzo e-mail già presente viene segnalato nella scheda e-mail, con un collegamento alla voce esistente. Per le organizzazioni esistenti la finestra di modifica mostra solo registro di commercio, responsabilità, firma e note; per le nuove organizzazioni resta invariata.",
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.478',
    date: '23. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Kontakt-Detailansicht zeigt einen Duplikat-Hinweis mit Zähler und Sprung in den Merge-Dialog.',
        detail:
          'Hat ein Kontakt mögliche Duplikate, erscheint oben in der Detailansicht ein Banner «n mögliche Duplikate» — sichtbar in jedem Register, nicht mehr nur in der Übersicht. Pro Treffer zeigt der Banner Name und Übereinstimmung in Prozent; «Prüfen» öffnet den Merge-Dialog vorbelegt mit dem aktuellen Kontakt und dem gewählten Duplikat. Ohne Duplikate bleibt der Banner aus.',
        translations: {
          en: {
            text: 'Contact detail view shows a duplicate hint with counter and a jump into the merge dialog.',
            detail:
              'If a contact has possible duplicates, a banner «n possible duplicates» appears at the top of the detail view — visible on every tab, no longer only on the overview. Each match shows name and match score; «Check» opens the merge dialog pre-filled with the current contact and the selected duplicate. Without duplicates no banner is shown.',
          },
          fr: {
            text: 'La fiche contact affiche un avertissement de doublons avec compteur et accès au dialogue de fusion.',
            detail:
              'Si un contact a des doublons possibles, une bannière «n doublons possibles» apparaît en haut de la fiche — visible dans tous les onglets, plus seulement dans l’aperçu. Chaque correspondance affiche le nom et le score; «Vérifier» ouvre le dialogue de fusion prérempli avec le contact actuel et le doublon choisi. Sans doublons, pas de bannière.',
          },
          it: {
            text: 'La scheda contatto mostra un avviso duplicati con contatore e accesso alla finestra di unione.',
            detail:
              'Se un contatto ha possibili duplicati, in cima alla scheda appare un banner «n possibili duplicati» — visibile in ogni scheda, non più solo nella panoramica. Per ogni corrispondenza vengono mostrati nome e percentuale; «Verifica» apre la finestra di unione precompilata con il contatto attuale e il duplicato scelto. Senza duplicati il banner non compare.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.477',
    date: '23. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Die Merge-Vorschau für Kontakte zeigt alle Bereiche, die auf den Hauptkontakt umgehängt werden.',
        detail:
          'Der Abschnitt «Wird übernommen» listet neben den bisherigen Bereichen neu auch Aktivitäten, synchronisierte E-Mails, Benutzerkonto, Zugangs-Links, Wissens-Abschnitte, Investoren-Gruppen, Info-Berechtigungen und Objekt-Verknüpfungen mit Zähler. Haben beide Kontakte ein Benutzerkonto, erklärt ein Hinweis, warum das Zusammenführen abgelehnt wird und wie man es löst.',
      },
    ],
  },
  {
    version: 'v1.14.476',
    date: '23. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisationen haben neu eine eigene Detailseite mit verlinkbarer Adresse und Browser-Zurück.',
        detail:
          'Unter /crm/organizations/<ID> öffnet sich die Organisation als vollwertige Seite mit allen Reitern (Übersicht, Kontakte, Verträge, Immobilien, Suchkriterien, Dokumente, KI-Recherche, Investorengruppen, Wissen, Aktivitäten). Der gewählte Reiter steht im Link, der Browser-Zurück-Knopf führt zur Liste samt Filtern. Der Klick in der Liste öffnet weiterhin eine Kurzvorschau — dort führt «Öffnen» auf die Seite. Alte Links mit ?selected= leiten automatisch um.',
      },
    ],
  },
  {
    version: 'v1.14.475',
    date: '23. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Im Kontakte-Reiter einer Organisation werden die Funktionen einer Person als Liste angezeigt und als Chips bearbeitet.',
        detail:
          'Die Funktionen kommen neu als Liste aus der API statt aus einem einzelnen Textfeld. Beim Bearbeiten einer Rolle erscheint jede Funktion als eigener Chip: einzelne Funktionen lassen sich per «x» entfernen und über das Eingabefeld (Enter oder «+») ergänzen. Gespeichert wird die vollständige Liste.',
      },
    ],
  },
  {
    version: 'v1.14.474',
    date: '23. September 2026',
    changes: [
      { type: 'feature', text: 'Funktionen in Kontakte-Tab und Rollenbearbeitung anzeigen' },
      { type: 'feature', text: 'RoleFunctionsEditor für die Verwaltung von Funktionen' },
      { type: 'improvement', text: 'Kontakttabelle refaktoriert und Translations in vier Sprachen aktualisiert' },
    ],
  },
  {
    version: 'v1.14.473',
    date: '23. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Im Kontakte-Reiter einer Organisation werden die Funktionen einer Person als Liste angezeigt und als Chips bearbeitet.',
        detail:
          'Die Funktionen kommen neu als Liste aus der API statt aus einem einzelnen Textfeld. Beim Bearbeiten einer Rolle erscheint jede Funktion als eigener Chip: einzelne Funktionen lassen sich per «x» entfernen und über das Eingabefeld (Enter oder «+») ergänzen. Gespeichert wird die vollständige Liste.',
      },
    ],
  },
  {
    version: 'v1.14.472',
    date: '23. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Der Firmen-Reiter eines Kontakts zeigt neu dieselbe Tabelle wie der Kontakte-Reiter einer Organisation.',
        detail:
          'Die Firmenzuordnungen einer Person stehen jetzt als Tabelle statt als eigene Liste: eine Zeile pro Organisation, gruppiert nach «Zeichnungsberechtigt (Handelsregister)», «Weitere Organisationen» und «Inaktiv (n)». Die Spalte Organisation sowie Funktion(en), Zeichnung, Quelle, Status und Verifiziert lassen sich per Klick auf den Spaltenkopf sortieren, Filter-Chips und Suche grenzen die Liste ein. Neu sichtbar sind das Verifiziert-Abzeichen mit Datum sowie die Aktionen Anpinnen und Ansprechpartner; Funktion und Zeichnung bearbeiten, Entfernen (neu mit Rückfrage) und «Firma hinzufügen» bleiben erhalten.',
      },
    ],
  },
  {
    version: 'v1.14.471',
    date: '23. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Der Kontakte-Reiter einer Organisation ist neu eine Tabelle mit einer Zeile pro Person, Gruppen, sortierbaren Spalten und Filter-Chips.',
        detail:
          'Mehrere Rollen derselben Person (z.B. aus dem Handelsregister und manuell erfasst) stehen jetzt in einer einzigen Zeile, die Funktionen erscheinen als Liste. Die Kontakte sind gruppiert nach «Zeichnungsberechtigte (Handelsregister)», «Weitere Kontakte» und «Inaktiv (n)» — die inaktive Gruppe ist eingeklappt und zeigt den Zähler. Die Spalten Name, Funktion(en), Zeichnung, Quelle, E-Mail, Status und Verifiziert lassen sich per Klick auf den Spaltenkopf sortieren, Filter-Chips schalten aktiv/inaktiv, Handelsregister/Manuell/Import sowie «Ohne E-Mail». Die Registerverifizierung ist neu als Abzeichen mit Datum sichtbar. Alle bisherigen Aktionen — Anpinnen, Ansprechpartner, Funktion und Zeichnung bearbeiten, Entfernen — bleiben erhalten.',
      },
      {
        type: 'fix',
        text: 'Anpinnen, Ansprechpartner und Entfernen wirken bei Personen mit mehreren Rollen jetzt auf die ganze Zeile.',
        detail:
          'Hat eine Person in derselben Organisation mehrere Rollen (z.B. eine aus dem Handelsregister und eine manuell erfasste), betrifft «Entfernen» neu alle Rollen dieser Person — die Zeile verschwand bisher nicht, weil die zweite Rolle stehen blieb. Ebenso löst ein Klick auf das Pin-Symbol die Markierung wirklich, statt zusätzlich eine zweite Rolle anzupinnen; dasselbe gilt für den Ansprechpartner.',
      },
    ],
  },
  {
    version: 'v1.14.470',
    date: '22. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Käufer ohne E-Mail-Adresse: Die Zeile weist «E-Mail fehlt» aus und die Adresse lässt sich direkt dort sowie im Konditions-Dialog nachtragen.',
        detail:
          'Bisher liess sich ein Käufer ohne hinterlegte E-Mail-Adresse zuordnen, danach passierte still nichts — die Einladung wurde übersprungen. Neu zeigt die Karte «Zugeordnete Käufer» (Objekt-Freigabe und Inserat) bei solchen Personen das Kennzeichen «E-Mail fehlt» samt Eingabefeld: nach dem Speichern steht die Adresse in der Zeile und «E-Mail erneut senden» steht wieder zur Verfügung. Der Konditions-Dialog, der nach einer Zuordnung ohne Rahmenvertrag sofort aufgeht, verlangt die Adresse als Pflichtfeld und speichert zuerst den Kontakt, dann die Kondition; ist die Adresse bereits vergeben, nennt der Dialog den Grund und die Kondition bleibt ungespeichert. Der Hinweis nach einem übersprungenen Versand nennt jetzt «Keine E-Mail hinterlegt» statt des Codes «no_email».',
      },
    ],
  },
  {
    version: 'v1.14.469',
    date: '22. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Die Käufer-Suche findet neu auch Firmen aus dem Handelsregister und legt sie mit einem Klick im CRM an.',
        detail:
          'Die Personensuche im Quality Gate und auf der Objektseite durchsucht weiterhin zuerst das eigene CRM: Kontakte, dann Firmen. Bleiben dort weniger als fünf Treffer, werden Handelsregister-Treffer mit dem Kennzeichen «Zefix» angehängt. Firmen, die über die UID bereits im CRM liegen, erscheinen nicht doppelt, sondern als gewohnte CRM-Zeile mit «Zuordnen». Mit «Firma anlegen» entsteht die Organisation samt UID, Rechtsform, Sitz und Zeichnungsberechtigten direkt im CRM; danach läuft die Suche neu und Firma wie Personen lassen sich zuordnen. Die Zuordnung bleibt ein bewusster zweiter Klick. Ist das Handelsregister nicht erreichbar, bleiben die CRM-Treffer ohne Fehlermeldung sichtbar; ohne Treffer führt ein Hinweis direkt zur Organisationsanlage im CRM.',
      },
    ],
  },
  {
    version: 'v1.14.468',
    date: '22. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Der Auftraggeber eines Objekts lässt sich jetzt direkt im Reiter «Auftrag und Vermarktung» entfernen.',
        detail:
          'Bei gesetztem Auftraggeber steht neben «Vertrag ändern» neu ein «Entfernen»-Knopf. Nach einer Rückfrage wird die Verknüpfung zu Firma und Kontakt am Objekt gelöst, der Abschnitt zeigt danach wieder «Noch nicht festgelegt». Bisher klebte ein einmal gesetzter Auftraggeber am Objekt und konnte nur noch über die Schnittstelle korrigiert werden. Bestehende Verträge bleiben erhalten, ein neuer Auftraggeber kann jederzeit verknüpft werden. Für Verkäufer bleibt der Abschnitt unverändert schreibgeschützt.',
      },
    ],
  },
  {
    version: 'v1.14.467',
    date: '22. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Quality Gate: Die zugeordneten Käufer erscheinen jetzt in derselben Karte wie im Teaser — inklusive Firmengruppen, Versandstand und Aktionen.',
        detail:
          'Die Karte «Konditionen der zugeordneten Käufer» entfällt; an ihrer Stelle steht die Karte «Zugeordnete Käufer» aus der Inserats-Ansicht. Sie gruppiert nach Firma, zeigt je Person Name, E-Mail, Status (aktiv / Onboarding ausstehend) und den Versandstand der Einladung und bietet «Erneut einladen», «Entfernen» sowie «Kondition für diesen Käufer festlegen» an. «Für alle festlegen» (Objektkondition) steht neu in beiden Ansichten als Fussleiste der Karte. Vor der Freigabe gibt es noch kein Inserat — dort erscheinen nur die objektbezogenen Aktionen. Der Konditions-Dialog öffnet nach einer manuellen Zuordnung ohne Rahmenvertrag weiterhin sofort.',
      },
    ],
  },
  {
    version: 'v1.14.466',
    date: '22. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Objektseite für Käufer: Die Karte «Externe Links» (Sonneneinstrahlung, Lärmbelastung, Topografie, Nachbarschaftspreise) entfällt.',
        detail:
          'Die vier Kacheln führten auf einen Drittanbieter, der nicht erreichbar ist — der Klick ging ins Leere. Zudem gaben die Links die exakten Koordinaten des Objekts an einen Dritten weiter, auf einer Seite, die sonst bewusst nur freigegebene Objektdaten zeigt. Die Sektionsnavigation zeigt den Eintrag «Links» nicht mehr; Timeline, Übersicht, Finanzen, Angebot und Dokumente bleiben unverändert. Die Standortdaten des Objekts selbst sind nicht betroffen.',
      },
    ],
  },
  {
    version: 'v1.14.465',
    date: '22. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Vertragsübersicht: Anhänge sind als solche erkennbar, Firmennamen werden nicht mehr abgeschnitten, pro Zeile gibt es eine Hauptaktion.',
        detail:
          'Ein Anhang zeigt jetzt seine eigene Bezeichnung («Anhang B: Objekt #275 (Region AG)») statt des Vorlagennamens, der bei Haupt- und Anhangsvertrag identisch ist. Anhänge stehen eingerückt direkt unter ihrem Rahmenvertrag und bieten einen Textlink «zum Rahmenvertrag»; fehlt der Rahmenvertrag wegen eines Filters, bleibt der Anhang als eigene Zeile mit demselben Link stehen. In den Spalten «Parteien» und «Unterzeichner» entfallen die festen Breiten — lange Firmennamen brechen um statt abgeschnitten zu werden. Der Klick auf die Zeile führt zur Detailseite; der zusätzliche Knopf «Anzeigen» entfällt, das PDF bleibt als Symbol «Herunterladen».',
      },
    ],
  },
  {
    version: 'v1.14.464',
    date: '22. September 2026',
    changes: [
      { type: 'feature', text: 'Anhänge in Vertragsübersicht optisch kenntlich gemacht und zählbar' },
      { type: 'fix', text: 'Vertragstitel und Dateinamen werden vollständig angezeigt, nicht mehr abgeschnitten' },
      { type: 'feature', text: 'Hauptaktion in Angebots-/Vertrags-Dialog prominenter hervorgehoben' },
    ],
  },
  {
    version: 'v1.14.463',
    date: '22. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Vertragsübersicht: Anhänge sind als solche erkennbar, Firmennamen werden nicht mehr abgeschnitten, pro Zeile gibt es eine Hauptaktion.',
        detail:
          'Ein Anhang zeigt jetzt seine eigene Bezeichnung («Anhang B: Objekt #275 (Region AG)») statt des Vorlagennamens, der bei Haupt- und Anhangsvertrag identisch ist. Anhänge stehen eingerückt direkt unter ihrem Rahmenvertrag und bieten einen Textlink «zum Rahmenvertrag»; fehlt der Rahmenvertrag wegen eines Filters, bleibt der Anhang als eigene Zeile mit demselben Link stehen. In den Spalten «Parteien» und «Unterzeichner» entfallen die festen Breiten — lange Firmennamen brechen um statt abgeschnitten zu werden. Der Klick auf die Zeile führt zur Detailseite; der zusätzliche Knopf «Anzeigen» entfällt, das PDF bleibt als Symbol «Herunterladen».',
      },
    ],
  },
  {
    version: 'v1.14.462',
    date: '22. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Angebot abgeben: nur noch drei Schritte, Vorbehalte zum Ankreuzen, keine Erwerbsform mehr.',
        detail:
          'Der Dialog führt jetzt über «Ihr Angebot» (nur der Betrag), «Ihr Dokument» und «Prüfen & senden». Die Wahl zwischen Standard-Vorlage und eigenem Dokument steht am Anfang von Schritt 2; ohne Wahl bleibt «Weiter» gesperrt. Im Standardweg erscheinen die Vorbehalte der jeweiligen Angebotsart als Auswahlliste — angekreuzt stehen sie im Wortlaut in der Vorschau und im erzeugten Dokument, nicht angekreuzte erscheinen gar nicht. Beim eigenen Dokument entfällt die Liste. Bemerkungen und der Finanzierungsnachweis liegen neu im letzten Schritt, der Nachweis bleibt optional. Erwerbsform und Übernahmezeitpunkt werden nicht mehr abgefragt: die Erwerbsform gibt die Plattform vor, eine echte Einschränkung beim Zeitpunkt gehört in die Bemerkungen.',
      },
    ],
  },
  {
    version: 'v1.14.461',
    date: '22. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Abgelaufener Anmelde-Link: ein Klick auf «Neuen Link senden» schickt einen neuen Link — ohne die E-Mail-Adresse erneut einzutippen.',
        detail:
          'Die Seite heisst jetzt «Anmelde-Link abgelaufen» und bietet genau eine Schaltfläche an: «Neuen Link senden». Sie reicht das Kennzeichen des abgelaufenen Links an den Server weiter, der daraus den Empfänger ermittelt und einen neuen Link verschickt; die Bestätigung nennt die Adresse bewusst nicht. Ist der Link dem Server gar nicht mehr bekannt, erscheint ersatzweise ein Feld für die E-Mail-Adresse. Wer noch angemeldet ist, sieht den Hinweis «Sie sind bereits angemeldet» als dezente Zeile mit Textlink zur Plattform — «Neuen Link senden» bleibt der Hauptweg und beendet dabei die bestehende Sitzung.',
      },
    ],
  },
  {
    version: 'v1.14.460',
    date: '22. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Angebot abgeben: der vorgeschlagene Betrag entspricht wieder dem Richt- bzw. Festpreis und ist nicht mehr hundertfach zu hoch.',
        detail:
          'Preise kommen mit zwei Nachkommastellen aus dem System (z. B. 31\'900\'000.00). Beim Vorbelegen des Angebotsfelds wurden die Nachkommastellen bisher wie Ziffern behandelt — aus 31.9 Millionen wurden 3.19 Milliarden. Der Betrag wird jetzt auf ganze Franken gerundet übernommen; angezeigter und abgesendeter Betrag stimmen überein. Fehlt ein Preis oder ist er 0, bleibt das Feld leer statt «0» anzuzeigen. Auch der Hinweis «Festpreis»/«Richtpreis» unter dem Eingabefeld nennt jetzt denselben gerundeten Betrag und verschwindet, wenn kein Preis hinterlegt ist. Die Tastatureingabe bleibt unverändert mit Tausender-Trennzeichen.',
      },
    ],
  },
  {
    version: 'v1.14.459',
    date: '21. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Angebot abgeben: eine einzige Maske für alle Verkaufsmodi — mit Standard-Vorlage oder eigenem Dokument, Bemerkungen und Finanzierungsnachweis.',
        detail:
          'Ob Festpreis, indikatives Angebot (NBO) oder verbindliches Angebot (BO): Käuferinnen und Käufer durchlaufen jetzt überall dieselben vier Schritte — Betrag, Angebotsdokument, Bemerkungen, Finanzierungsnachweis — und sehen danach eine Übersicht. Im Festpreis war bisher nur ein Unterschriftsfeld zu sehen, ohne jede Möglichkeit, das Dokument zu lesen; jetzt wird die Standard-Vorlage vor dem Unterzeichnen vollständig angezeigt. Wer lieber ein eigenes Angebotsdokument einreicht, lädt es als PDF hoch — es wird neu tatsächlich übertragen und am eigenen Angebot abgelegt, statt stillschweigend verworfen zu werden. Dasselbe gilt für den Finanzierungsnachweis; beim verbindlichen Angebot geht er wie bisher an die laufende Angebotsrunde. Lässt sich eine Datei keinem Angebot zuordnen, wird sie gemeldet statt irgendwo abgelegt. Bemerkungen und Vorbehalte landen unverändert am Angebot; der frühere Systemtext «LOI signed digitally» entfällt. Schlägt die Übertragung einer Datei fehl, bleibt das Angebot bestehen und die Maske benennt die fehlende Datei zum erneuten Übertragen.',
      },
    ],
  },
  {
    version: 'v1.14.458',
    date: '21. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Abgelaufener Anmelde-Link: «Neuen Link anfordern» meldet nicht mehr stillschweigend wieder an.',
        detail:
          'Wer einen Anmelde-Link ein zweites Mal öffnet, sieht jetzt, ob die Anmeldung trotz abgelaufenem Link noch besteht. In dem Fall bietet die Seite zwei benannte Wege an: «Weiter zur Plattform» führt aufs Dashboard, «Abmelden und neuen Link anfordern» beendet die Sitzung und führt auf die Anmeldeseite — vorher landete der Klick wegen der weiterlaufenden Sitzung sofort wieder im Dashboard, ohne dass je ein neuer Link angefordert wurde. Ohne bestehende Sitzung führt «Neuen Link anfordern» wie bisher direkt auf die Anmeldeseite. Ein abgelaufener Link beendet weiterhin von sich aus keine laufende Sitzung.',
      },
    ],
  },
  {
    version: 'v1.14.457',
    date: '21. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Zugeordnete Käufer: Versandstatus je Person sichtbar, Firmen-Versand fragt vor dem erneuten Anschreiben nach.',
        detail:
          'Jede Person mit ausstehendem Onboarding zeigt jetzt, ob sie noch nie angeschrieben wurde oder wann die jüngste Einladung verschickt wurde — samt Anzahl der Einladungen und dem Zeitpunkt, an dem der Link geöffnet wurde. Der Versand-Knopf auf Firmenebene nennt die Zahl der Empfänger, die er wirklich trifft, und ist deaktiviert, wenn er niemanden erreicht. Trifft ein Versand jemanden, der bereits eine Einladung hat, kommt vorher eine Rückfrage: die zuvor verschickten Links werden dadurch ungültig. Aktive Käufer bleiben unverändert ohne Versandstatus und ohne Knopf.',
      },
    ],
  },
  {
    version: 'v1.14.456',
    date: '21. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Teaser bearbeiten: Stammdaten und Objektliste liegen jetzt direkt auf der Teaser-Hauptseite — die separate Bearbeiten-Seite entfällt.',
        detail:
          'Titel, Status, privat/öffentlich und Gesamtpreis werden auf der Teaser-Hauptseite geändert und gespeichert, Objekte lassen sich dort hinzufügen und entfernen (mit Sicherheitsabfrage). Die doppelte Verkaufskonfiguration ist weg — Verkaufsmodus, Preis und NBO-/BO-Termine kommen wie vorgesehen aus dem Quality Gate; das Speichern auf der alten Seite scheiterte ohnehin, weil sie abgeschaffte Preisfelder sendete. Der frühere Investorenzugang im Bearbeiten entfällt ebenfalls: Käufer werden ausschliesslich über die Käufer-Verwaltung zugeordnet, die Rahmenvertrag und Kondition prüft. Alte Lesezeichen auf die Bearbeiten-Adresse landen automatisch auf der Teaser-Hauptseite.',
      },
    ],
  },
  {
    version: 'v1.14.455',
    date: '21. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Mandanten-Umschalter wirkt jetzt überall — Quality Gate zeigt die Objekte des gewählten Mandanten.',
        detail:
          'Die Wahl in der Titelleiste wurde bisher nur im Browser gemerkt und nie an den Server gemeldet; tenant-abhängige Ansichten wie Quality Gate oder KI-Erfassung blieben deshalb beim zuerst zugeordneten Mandanten. Ein Wechsel wird nun serverseitig gespeichert, die betroffenen Ansichten laden ohne Neuladen der Seite neu und nach einem Reload ist derselbe Mandant aktiv. Lässt sich der Wechsel nicht speichern, bleibt die bisherige Wahl stehen und eine Fehlermeldung erscheint — angezeigt wird nur, was der Server auch kennt.',
      },
    ],
  },
  {
    version: 'v1.14.454',
    date: '21. September 2026',
    changes: [
      { type: 'fix', text: 'Mandanten-Umschalter funktioniert jetzt auch via API korrekt' },
      { type: 'fix', text: 'Quality Gate zeigt richtige Objekte nach Mandanten-Wechsel an' },
    ],
  },
  {
    version: 'v1.14.453',
    date: '21. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Mandanten-Umschalter wirkt jetzt überall — Quality Gate zeigt die Objekte des gewählten Mandanten.',
        detail:
          'Die Wahl in der Titelleiste wurde bisher nur im Browser gemerkt und nie an den Server gemeldet; tenant-abhängige Ansichten wie Quality Gate oder KI-Erfassung blieben deshalb beim zuerst zugeordneten Mandanten. Ein Wechsel wird nun serverseitig gespeichert, die betroffenen Ansichten laden ohne Neuladen der Seite neu und nach einem Reload ist derselbe Mandant aktiv. Lässt sich der Wechsel nicht speichern, bleibt die bisherige Wahl stehen und eine Fehlermeldung erscheint — angezeigt wird nur, was der Server auch kennt.',
      },
    ],
  },
  {
    version: 'v1.14.452',
    date: '21. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Käufer können wieder ein Angebot abgeben — der Festpreis-Einstieg prüfte ein abgeschafftes Preisfeld.',
        detail:
          'Die Objektseite im Käuferbereich fragte für den Knopf «Angebot abgeben» das alte Feld `minimum_price` ab, das die API seit der Preis-Vereinheitlichung (#360) nicht mehr liefert — der Knopf erschien deshalb nie, auch nicht bei einem Festpreis-Inserat mit hinterlegtem Preis. Der gesamte Käuferbereich (Objektseite, Suche inkl. Sortierung und Karte, Inserats-Teaser, Exposé, Dashboard-Kacheln) liest jetzt das kanonische Feld `price`; der Angebots-Dialog übernimmt den Preis des Inserats. Neu bietet auch die Exposé-Seite eines Inserats den Einstieg an: bei einem Einzelobjekt ein Knopf am Seitenende, bei einem Bundle je Objekt einer im jeweiligen Abschnitt — damit bleibt das Angebot eindeutig einem Objekt zugeordnet. Läuft bereits ein Angebot, erscheint unverändert dessen Status; bei Angebotssammlung bleibt der Festpreis-Einstieg aus.',
      },
    ],
  },
  {
    version: 'v1.14.451',
    date: '21. September 2026',
    changes: [
      { type: 'fix', text: 'Gebots-Einstieg überprüft nicht mehr abgeschafftes Feld minimum_price' },
      { type: 'fix', text: 'Investor kann wieder Gebote abgeben — Validierung korrigiert' },
    ],
  },
  {
    version: 'v1.14.450',
    date: '21. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Käufer können wieder ein Angebot abgeben — der Festpreis-Einstieg prüfte ein abgeschafftes Preisfeld.',
        detail:
          'Die Objektseite im Käuferbereich fragte für den Knopf «Angebot abgeben» das alte Feld `minimum_price` ab, das die API seit der Preis-Vereinheitlichung (#360) nicht mehr liefert — der Knopf erschien deshalb nie, auch nicht bei einem Festpreis-Inserat mit hinterlegtem Preis. Der gesamte Käuferbereich (Objektseite, Suche inkl. Sortierung und Karte, Inserats-Teaser, Exposé, Dashboard-Kacheln) liest jetzt das kanonische Feld `price`; der Angebots-Dialog übernimmt den Preis des Inserats. Neu bietet auch die Exposé-Seite eines Inserats den Einstieg an: bei einem Einzelobjekt ein Knopf am Seitenende, bei einem Bundle je Objekt einer im jeweiligen Abschnitt — damit bleibt das Angebot eindeutig einem Objekt zugeordnet. Läuft bereits ein Angebot, erscheint unverändert dessen Status; bei Angebotssammlung bleibt der Festpreis-Einstieg aus.',
      },
    ],
  },
  {
    version: 'v1.14.449',
    date: '21. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Signaturseite: bereits unterschrieben wird bestätigt statt als Fehler gezeigt.',
        detail:
          'Öffnet ein Unterzeichner seinen Signaturlink erneut — nach einem Reload oder einer Unterbrechung —, zeigt die Seite «Sie haben bereits unterschrieben» mit dem Unterschriftszeitpunkt im Format TT.MM.JJJJ um HH:MM, einem Knopf «Vertrag als PDF herunterladen» (derselbe Link, kein Login) und dem Hinweis, dass keine weitere Handlung nötig ist. Abgelaufene Links («Link abgelaufen») und ungültige Links («Ungültiger Unterzeichnungslink») behalten ihre eigenen, klar unterscheidbaren Meldungen; eine noch offene Signatur zeigt unverändert das Unterschriftsformular.',
      },
    ],
  },
  {
    version: 'v1.14.448',
    date: '18. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Zuordnung ohne Rahmenvertrag: Konditions-Dialog öffnet sofort.',
        detail:
          'Wird am Teaser ein Käufer zugeordnet, der danach auf «Onboarding ausstehend» steht, öffnet der Konditions-Dialog automatisch für genau diesen Käufer — die Entscheidung «Objektkondition (Flat) oder eigene Kondition für dieses Objekt» ist damit sofort sichtbar und muss nicht mehr weiter unten in der Liste gesucht werden. Der Dialogtext nennt jetzt beide Wege: ohne Honorarmodell gilt die Objektkondition aus dem Rahmenvertrag, mit Honorarmodell entsteht eine objektspezifische Kondition, die nach der Unterschrift als Anhang B übernommen wird. «Abbrechen» lässt die Zuordnung bestehen; die Zeile bietet «Geplante Kondition festlegen» weiterhin an. Käufer mit Rahmenvertrag laufen unverändert über den bisherigen Weg.',
      },
    ],
  },
  {
    version: 'v1.14.447',
    date: '17. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Teaser: Kondition auch für Käufer mit «Onboarding ausstehend» festlegen.',
        detail:
          'In den zugeordneten Käufern bietet jetzt auch eine Zeile ohne Rahmenvertrag die Aktion «Geplante Kondition festlegen». Die Kondition wird an der Zuordnung vorgemerkt und nach der Unterschrift des Käufers automatisch als Anhang B übernommen; bis dahin weist die Zeile sie als «geplant: <Modell>» aus. Eine leere Auswahl entfernt die Vormerkung — dann gilt wie bisher die Objektkondition. Käufer mit aktivem Rahmenvertrag bekommen ihre Kondition unverändert direkt als Anhang B. Bei einem Bundle gilt die Vormerkung genau für das Objekt der Vorschau — der Wechsel auf ein anderes Objekt zeigt dessen eigene Kondition.',
      },
    ],
  },
  {
    version: 'v1.14.446',
    date: '17. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Meine Verträge: Anhänge des Rahmenvertrags werden im Investorenbereich aufgelistet.',
        detail:
          'Auf dem eigenen Rahmenvertrag erscheint ein Block «Anhänge» mit Anhang A/B, Vertragsnummer, Objektnummer, Status und Link zum jeweiligen Anhang. So ist ersichtlich, welche Kondition für ein einzelnes Objekt gilt. Gibt es noch keine Anhänge, erscheint ein Hinweistext. Ein Anhang selbst verweist wie bisher nur auf seinen Rahmenvertrag.',
      },
    ],
  },
  {
    version: 'v1.14.445',
    date: '17. September 2026',
    changes: [
      { type: 'feature', text: 'Investor-Vertragsseite zeigt pro Objekt die Anhänge (Anhang B) an' },
      { type: 'improvement', text: 'OnboardingPage mit besserer Fehlerbehandlung refaktoriert' },
    ],
  },
  {
    version: 'v1.14.444',
    date: '17. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Meine Verträge: Anhänge des Rahmenvertrags werden im Investorenbereich aufgelistet.',
        detail:
          'Auf dem eigenen Rahmenvertrag erscheint ein Block «Anhänge» mit Anhang A/B, Vertragsnummer, Objektnummer, Status und Link zum jeweiligen Anhang. So ist ersichtlich, welche Kondition für ein einzelnes Objekt gilt. Gibt es noch keine Anhänge, erscheint ein Hinweistext. Ein Anhang selbst verweist wie bisher nur auf seinen Rahmenvertrag.',
      },
    ],
  },
  {
    version: 'v1.14.443',
    date: '17. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Onboarding «Ihre Konditionen»: nur noch Vertragspartner und Kosten.',
        detail:
          'Wer ein Konto erstellt, sieht unter «Ihre Konditionen» nur noch den Objekt-Hinweis, die Vertragsart mit dem Vertragspartner und den Honorar-Block. Laufzeit, Verlängerung und Kündigungsfrist sowie der ganze Block «Kontaktschutz» sind entfallen — diese Regelungen stehen in den Plattformbedingungen, die mit der Unterschrift bestätigt werden.',
      },
    ],
  },
  {
    version: 'v1.14.442',
    date: '17. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Persönlicher Teaser-Link: ein Knopf «Konto erstellen» statt E-Mail-Abfrage.',
        detail:
          'Wer den Teaser über eine persönliche Einladung öffnet, muss seine E-Mail-Adresse nicht mehr eingeben — der Link ist bereits an den Empfänger gebunden. Ein Klick auf «Konto erstellen» führt direkt zur Seite mit dem Plattformvertrag zur Unterzeichnung. Bei einem allgemeinen Teaser-Link bleibt das E-Mail-Formular wie bisher.',
      },
    ],
  },
  {
    version: 'v1.14.441',
    date: '17. September 2026',
    changes: [
      {
        type: 'fix',
        text: '«E-Mail erneut senden» verschickt die Onboarding-Einladung wieder.',
        detail:
          'Der Knopf bei einem Käufer mit «Onboarding ausstehend» rief eine Adresse auf, die es nicht gibt — die Einladung ging nie raus und es erschien «Versand fehlgeschlagen». Der Versand läuft jetzt über das Objekt der Zuordnung; bei einem Bündel wird je Objekt einmal gesendet. Wird kein Empfänger angeschrieben, nennt die Meldung neu den Grund.',
      },
    ],
  },
  {
    version: 'v1.14.440',
    date: '17. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Anmelde-Link aus der Zugangs-Mail führt wieder auf die verlinkte Seite statt aufs Dashboard.',
        detail:
          'Der Knopf in der Zugangs-Mail trägt neu eine Zielseite (z. B. den Teaser). Diese wird jetzt übernommen — auch dann, wenn vorher noch die Firma gewählt werden muss. Ohne Zielseite oder bei einer externen Adresse bleibt es beim Dashboard. War im Browser ein anderer Benutzer angemeldet, benennt die Meldung den Wechsel neu ausdrücklich.',
      },
    ],
  },
  {
    version: 'v1.14.439',
    date: '17. September 2026',
    changes: [
      { type: 'feature', text: 'Magic-Link respektiert Zielseite (next_url) aus Verify-Antwort' },
      { type: 'feature', text: 'Benutzerwechsel auf Magic-Link-Seite wird sichtbar angezeigt' },
      { type: 'improvement', text: 'Neue safeRedirect-Utility für sichere URL-Redirect-Validierung' },
    ],
  },
  {
    version: 'v1.14.438',
    date: '17. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Anmelde-Link aus der Zugangs-Mail führt wieder auf die verlinkte Seite statt aufs Dashboard.',
        detail:
          'Der Knopf in der Zugangs-Mail trägt neu eine Zielseite (z. B. den Teaser). Diese wird jetzt übernommen — auch dann, wenn vorher noch die Firma gewählt werden muss. Ohne Zielseite oder bei einer externen Adresse bleibt es beim Dashboard. War im Browser ein anderer Benutzer angemeldet, benennt die Meldung den Wechsel neu ausdrücklich.',
      },
    ],
  },
  {
    version: 'v1.14.437',
    date: '17. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Käufer-Suche: Treffer zeigen jetzt auch die E-Mail-Adresse.',
        detail:
          'In der Käufer-Suche (Teaser-Detail und Quality Gate) war bei einem Treffer nur Name, Typ und Firma bzw. Ort sichtbar. Vor dem Zuordnen liess sich damit nicht erkennen, an welche Adresse das Onboarding geht. Neu steht die hinterlegte E-Mail als eigene Zeile unter dem Treffer — bei Kontakten zusätzlich zur Firma, bei Firmen sobald die API sie liefert. Ist keine E-Mail hinterlegt, bleibt die Zeile weg.',
      },
    ],
  },
  {
    version: 'v1.14.436',
    date: '17. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Zugeordnete Käufer entfernen: keine Fehlermeldung mehr, wenn eine Firma mehrere Personen hat.',
        detail:
          'Teilen sich mehrere Personen derselben Firma eine Zuordnung, wurde diese beim Entfernen einmal pro Person widerrufen — der zweite Aufruf lief ins Leere und meldete «Entfernen fehlgeschlagen», obwohl der Widerruf bereits vollständig war; die Zeilen blieben bis zum Neuladen stehen. Neu wird jede Zuordnung genau einmal widerrufen, und eine bereits entfernte Zuordnung (Doppelklick oder parallele Bearbeitung) gilt als erledigt statt als Fehler.',
      },
    ],
  },
  {
    version: 'v1.14.435',
    date: '17. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Rahmenverträge zeigen ihre Anhänge — der Anhang B eines Käufers ist wieder auffindbar.',
        detail:
          'Ein gespeicherter Anhang B (käuferindividuelle Kondition je Objekt) war in der Oberfläche nirgends erreichbar: Die Vertragsliste zeigt bewusst nur Hauptverträge, und die Detailseite des Rahmenvertrags verlinkte bisher nur die Gegenrichtung. Neu listet jeder Rahmenvertrag unter «Anhänge» seine Anhänge mit Bezeichnung, Vertragsnummer, betroffenem Objekt und Status — jede Zeile führt direkt zum Anhang. Rahmenverträge ohne Anhänge zeigen einen Hinweistext; auf einem Anhang selbst bleibt es beim Link «Rahmenvertrag anzeigen».',
      },
    ],
  },
  {
    version: 'v1.14.434',
    date: '17. September 2026',
    changes: [
      {
        type: 'fix',
        text: '«Entfernen» einer einzelnen Käufer-Zuordnung auf der Teaser-Seite funktioniert wieder.',
        detail:
          'Der Knopf rief bisher eine Adresse auf, die es serverseitig nicht gibt — der Klick lief ins Leere und die Zeile blieb stehen. Der Widerruf einer Zuordnung gehört zum Objekt, nicht zum Inserat: er geht jetzt an das Objekt der jeweiligen Zuordnung. Gehört eine Person bei einem Bundle zu mehreren Objekten, wird jede ihrer Zuordnungen mit ihrem eigenen Objekt widerrufen. «Entfernen» für eine ganze Firmengruppe und für aktive Zugriffe verhält sich unverändert.',
      },
    ],
  },
  {
    version: 'v1.14.433',
    date: '14. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Kondition je Käufer jetzt auch bei bereits freigegebenen Objekten erreichbar.',
        detail:
          'Bisher liess sich eine käuferindividuelle Kondition nur im Quality Gate festlegen — und das listet ausschliesslich Objekte in Prüfung. Auf der Teaser-Seite hat nun jede Zeile unter «Zugeordnete Käufer» die Aktion «Kondition für diesen Käufer festlegen», sofern der Käufer einen aktiven Käufer-Rahmenvertrag hat; danach zeigt die Zeile sofort das Quellen-Badge «Anhang B». Zweiter Weg: der Dialog «Keine Kondition vereinbart» bietet den Käufer-Weg direkt in der betroffenen Zeile an und wiederholt die Zuordnung nach dem Speichern automatisch. Ist der Käufervertrag noch nicht gegengezeichnet, steht statt des Knopfs der Grund. Der bisherige Verweis auf die Objektkondition und «Trotzdem zuordnen» bleiben unverändert.',
      },
    ],
  },
  {
    version: 'v1.14.432',
    date: '14. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Quality Gate: Kondition je Käufer festlegen statt nur für alle.',
        detail:
          'Im Abschnitt «Konditionen der zugeordneten Käufer» hat jede Zeile neu die Aktion «Für diesen Käufer festlegen» — damit zahlt Käufer A z. B. 2 %, während für Käufer B 3 % gelten. Die individuelle Kondition wird als Anhang B an seinem Käufer-Rahmenvertrag gespeichert, die Zeile zeigt sie danach mit dem Quellen-Badge «Anhang B». Der bisherige Knopf heisst jetzt «Für alle festlegen» und setzt unverändert die Objektkondition für alle zugeordneten Käufer ohne eigene Kondition. Lehnt die Schnittstelle ab (z. B. weil der Käufer dem Objekt nicht zugeordnet ist oder die Kondition bereits bestätigt wurde), steht der Grund im Klartext in der betroffenen Zeile.',
      },
    ],
  },
  {
    version: 'v1.14.431',
    date: '11. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'NDA-Konditionen ablehnen: Gegenvorschlag im Investorenbereich, Einwandliste im Quality Gate.',
        detail:
          'Im NDA-Dialog gibt es neben «Bestätigen» neu «Konditionen nicht akzeptieren»: ein kurzes Formular mit gewünschtem Satz (optional) und Begründung (Pflicht) — ohne Begründung bleibt die Schaltfläche gesperrt. Der Einwand wird übermittelt, ohne dass Zugang zu den Objektdaten entsteht; danach erscheint der Hinweis, dass sich die Verkäuferseite meldet. Im Quality Gate listet der neue Abschnitt «Offene Einwände zu Konditionen» Person, Objekt, gewünschten Satz, Begründung und Datum. «Annehmen» hält den vereinbarten Satz als Anhang B zum Objekt fest, «Ablehnen» geht mit Begründung an den Käufer — erledigte Einwände verschwinden aus der Liste.',
      },
    ],
  },
  {
    version: 'v1.14.430',
    date: '11. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Vertrags-Wizard: Konditionen wahlweise fest im Vertrag oder je Objekt (Anhang B).',
        detail:
          'Im Schritt «Konfiguration» des Plattformvertrags gibt es neu die Auswahl «Konditionen»: «Fest im Vertrag» schreibt die Prozentsätze wie bisher in den Vertrag — die Felder sind sichtbar und für jede gewählte Rolle zwingend grösser als 0 %. «Je Objekt (Anhang B)» blendet die Prozentfelder aus und weist stattdessen darauf hin, dass die Konditionen pro Objekt in Anhang B vereinbart und vor dem Zugang zum Objekt bestätigt werden; die Sätze werden dann bewusst nicht in den Vertrag geschrieben. Voreinstellung für neue Plattformverträge ist «Je Objekt (Anhang B)».',
      },
    ],
  },
  {
    version: 'v1.14.429',
    date: '11. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Zuordnung ohne vereinbarte Kondition: Dialog nennt Person und Rolle und lässt den Vorgang bewusst bestätigen.',
        detail:
          'Weist die Zuordnung eines Käufers oder die Freigabe eines Objekts mangels vereinbarter Kondition ab, erscheint statt einer allgemeinen Fehlermeldung ein Dialog: Er nennt jede betroffene Person bzw. Firma mit ihrer Rolle (Käufer/Verkäufer) und zeigt, welche Stufen der Konditions-Hierarchie geprüft wurden (Anhang B, Objektkondition, Rahmenvertragssatz, Standardmodell). Zwei Wege stehen offen: «Kondition hinterlegen» führt direkt zu den Objektkonditionen des Objekts, «Trotzdem zuordnen» bzw. «Trotzdem freigeben» wiederholt den Vorgang mit bewusster Bestätigung — erst nach Eingabe einer Begründung, ohne sie bleibt die Schaltfläche gesperrt. Die Begründung wird als eigenes Feld der Bestätigung übermittelt — getrennt vom Ablehnungsgrund — und serverseitig mit Benutzer und Zeitpunkt im Aktivitätslog vermerkt. Schlägt der bestätigte Vorgang aus einem anderen Grund fehl, erscheint nur die Fehlermeldung und der Dialog meldet keinen Erfolg.',
      },
    ],
  },
  {
    version: 'v1.14.428',
    date: '10. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Testabdeckung wird neu gemessen: «npm run test:coverage» zeigt den Gesamtwert und die zehn schwächsten Dateien.',
        detail:
          'Der Befehl «npm run test:coverage» erzeugt neben dem Cobertura-Bericht unter coverage/ eine Konsolen-Übersicht: Gesamtabdeckung der Zeilen in Prozent sowie die zehn Dateien mit der niedrigsten Abdeckung samt Zeilenzahl. Gemessen wird der gesamte Produktivcode unter src/ — auch Module ohne eigenen Test; ausgeschlossen sind nur Testdateien und generierte Artefakte. Es gilt bewusst keine Mindestschwelle, die Zahlen dienen der Beobachtung. Ausgangswert: 22,15 % der Zeilen (6423 von 29000) über 476 Dateien.',
      },
    ],
  },
  {
    version: 'v1.14.427',
    date: '10. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Zugeordnete Käufer im Teaser-Detail: nach Firma gruppiert, mit Entfernen und erneutem Mailversand.',
        detail:
          'Der Abschnitt «Zugeordnete Käufer» zeigt die Zuordnungen nicht mehr als flache Liste, sondern gruppiert nach Firma — jede Firmenzeile ist aufklappbar und nennt Personenzahl und zusammengefassten Status (aktiv, Onboarding ausstehend oder gemischt); Personen ohne Firma stehen unter «Einzelpersonen». Pro Person lassen sich die Zuordnung entfernen (mit Bestätigung) und bei ausstehendem Onboarding die Einladung erneut senden; auf Firmenebene gelten dieselben Aktionen für alle Personen der Gruppe. Zusätzlich wird pro Person die geltende Kondition mit Quelle («Anhang B», «Rahmenvertrag», «Standard») angezeigt, und beim Zuordnen ist die Kondition des Objekts bereits vor dem Klick sichtbar.',
      },
    ],
  },
  {
    version: 'v1.14.426',
    date: '10. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Konditionen sind neu am Objekt und im Quality Gate sichtbar und einstellbar.',
        detail:
          'Im Reiter «Preise & Finanzen» eines Objekts gibt es die Karte «Konditionen»: Sie zeigt das aktuell geltende Honorarmodell samt Quelle («Anhang B», «Rahmenvertrag» oder «Standard») und lässt Honorarmodell und Sonderkonditionen für dieses Objekt setzen. Gespeichert werden sie als Anhang B (Objektfreigabe) zum Rahmenvertrag des Auftraggebers. Im Quality Gate zeigt der neue Abschnitt «Konditionen der zugeordneten Käufer» pro Käufer die geltende Kondition mit Quellen-Badge; über «Individuell festlegen» lassen sich die Konditionen im Dialog anpassen. Ohne verknüpften Verkäufer-Rahmenvertrag bleibt die Anzeige lesend — der Hinweis erklärt warum und führt direkt in den Vertrags-Assistenten, mit dem Auftraggeber bereits vorbelegt.',
      },
    ],
  },
  {
    version: 'v1.14.425',
    date: '10. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Objekt bearbeiten, Reiter «Standort & Umweltdaten»: die nicht funktionierenden Quellen-Links wurden entfernt.',
        detail:
          'Die Verweise auf map.geo.admin.ch, BFS, ARE Bauzonen, ARE ÖV-Güteklassen, geodienste.ch, ÖREB-Kataster, BAFU, Minergie, sonnendach.ch, housing-stat.ch, cadastre.ch und den ESTV-Steuerrechner führten ins Leere oder nur auf Startseiten ohne Bezug zum Objekt. Die Quellenangabe bleibt als Text unter dem jeweiligen Feld stehen, damit die Herkunft der Daten dokumentiert ist. Anklickbar ist neben der Karte weiterhin nur der objektbezogene Google-Maps-Link.',
      },
    ],
  },
  {
    version: 'v1.14.424',
    date: '10. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Objektliste: gespeicherter Status-Filter versteckt keine Objekte mehr unbemerkt.',
        detail:
          'Der Status-Filter wird nicht mehr über die Sitzung hinaus gespeichert — nach Reload oder Neu-Login steht er wieder auf «Alle», bereits gespeicherte Werte werden ignoriert. Sortierung, Objektart, Kanton und Preis bleiben wie bisher erhalten. Ist die Liste wegen eines Filters leer, nennt die Seite jetzt die aktiven Filter (z. B. «Status = Archiviert · Mandant = Uniplacement GmbH») und bietet «Filter zurücksetzen»; hat der gewählte Mandant keine Objekte, kann man direkt zu einem anderen Mandanten wechseln. Der Filter-Button zählt den Status mit.',
      },
    ],
  },
  {
    version: 'v1.14.423',
    date: '10. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Teaser-Hero: Textkontrast richtet sich nach der Bildhelligkeit.',
        detail:
          'Liefert die API zu einem Bild die Luminanz (is_dark), passen sich Verlauf, Titelfarbe und die glasigen Bedienelemente an: helle Bilder erhalten dunkle Schrift auf hellem Verlauf, dunkle Bilder bleiben wie bisher weiss auf dunklem Verlauf. Das gilt auch für den Hero der öffentlichen Teaser-Seite inklusive Untertitel und Merkmal-Chips. Ohne Wert bleibt die Darstellung unverändert. Die Kampagnen-Vorschau rendert weiterhin die API, dort ändert sich im Block-Editor nichts.',
      },
    ],
  },
  {
    version: 'v1.14.422',
    date: '10. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Käuferprofil (KI): Relevanz-Score in Firmenliste und Firmen-Detail.',
        detail:
          'Die Organisationsliste zeigt eine sortierbare Spalte «Relevanz» mit dem KI-Score (0–100) und lässt sich auf «nur profilierte» bzw. «Score ≥ 40/70» filtern. Im Firmen-Detail zeigt die Karte «Käuferprofil (KI)» Score, Begründung und Profilierungsdatum und erlaubt das Neuerstellen des Profils. In der Investorengruppe lassen sich die Käuferprofile aller Mitglieder auf einmal erstellen; die Mitgliederliste zeigt die Scores.',
      },
    ],
  },
  {
    version: 'v1.14.421',
    date: '10. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Teaser-Kurztext pro Sprache pflegbar — sichtbar schon vor der NDA.',
        detail:
          'Im Medien-/Teaser-Tab gibt es neu das Feld «Teaser-Kurztext» (max. 600 Zeichen, Zeichenzähler, Hinweis «Vor NDA sichtbar — keine Orte, keine Immo-Nr.»). Der Text erscheint im Teaser im Block «Auf einen Blick» — auch in der Vorschau «Käufer ohne NDA», damit Vorschau und Mail dieselben Blöcke zeigen.',
      },
      {
        type: 'improvement',
        text: 'Teaser-Hero zeigt Kurz-Merkmale als Chips.',
        detail:
          'Öffentliche Teaser-Seite und interner Teaser zeigen unter dem Untertitel identitätsfreie Chips (Baubewilligung, Zustand, Objektart) in derselben Reihenfolge wie die API. Ohne Merkmale bleibt die Zeile leer.',
      },
    ],
  },
  {
    version: 'v1.14.420',
    date: '10. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Quality Gate warnt vor Identitätsmerkmalen im Teaser vor NDA.',
        detail:
          'Über der Teaser-Vorschau zeigt das Quality Gate neu den Block «Diskretion vor NDA»: Ortsname, Bezirk, Strasse oder Immo-Nr. in Titel, Highlights oder Beschreibung werden als Warnung gelistet, mit Direktlink auf den Medien-/Teaser-Tab des Objekts. Ohne Fund erscheint ein grüner Einzeiler.',
      },
      {
        type: 'fix',
        text: 'Vorschau «Käufer ohne NDA» zeigt keine Beschreibung und keine Investment-Highlights mehr.',
        detail:
          'Die Vorschau entspricht damit exakt dem, was ein Käufer ohne NDA erhält (Titel, Bilder, Eckdaten) — Fliesstexte, die einen Rückschluss auf das Objekt erlauben, sind erst nach NDA sichtbar.',
      },
    ],
  },
  {
    version: 'v1.14.419',
    date: '9. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Neue Admin-Seite «Aufsichtslisten» für beaufsichtigte Gegenparteien.',
        detail:
          'Unter Einstellungen → Aufsichtslisten sind die importierten Listen (FINMA, SIX, OAK BV) mit Bestand und letztem Import sichtbar. Listenimport und Firmen-Abgleich lassen sich manuell starten; Namens-Treffer werden in einer Prüfliste (Offen · Veraltet · Bestätigt · Abgelehnt) bestätigt oder abgelehnt.',
      },
    ],
  },
  {
    version: 'v1.14.418',
    date: '9. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Signaturseite: übersichtlicheres Layout mit Parteikarten und Schrittführung.',
        detail:
          'Die öffentliche Unterzeichnungsseite zeigt Vertragsart, Vertragsnummer und Gültigkeit in einer neutralen Kopfkarte, «Vertrag anzeigen» als Hauptbutton und die Vertragsparteien als zwei Karten nebeneinander — die eigene Karte hervorgehoben. Eine Schrittleiste (Vertrag · AGB · Unterschrift) zeigt den Fortschritt. Neutrale Hinweise erscheinen nicht mehr in Warnfarben.',
      },
    ],
  },
  {
    version: 'v1.14.417',
    date: '9. September 2026',
    changes: [
      { type: 'feature', text: 'Signaturseite zeigt Vertragsparteien als zwei Kartenlayouts' },
      { type: 'feature', text: 'PDF-Download ist Primäraktion, Schritte 1-2-3 deutlich sichtbar' },
      { type: 'improvement', text: 'Modals refaktoriert und Testsuite für Signaturseite erweitert' },
    ],
  },
  {
    version: 'v1.14.416',
    date: '9. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Signaturseite: übersichtlicheres Layout mit Parteikarten und Schrittführung.',
        detail:
          'Die öffentliche Unterzeichnungsseite zeigt Vertragsart, Vertragsnummer und Gültigkeit in einer neutralen Kopfkarte, «Vertrag anzeigen» als Hauptbutton und die Vertragsparteien als zwei Karten nebeneinander — die eigene Karte hervorgehoben. Eine Schrittleiste (Vertrag · AGB · Unterschrift) zeigt den Fortschritt. Neutrale Hinweise erscheinen nicht mehr in Warnfarben.',
      },
    ],
  },
  {
    version: 'v1.14.415',
    date: '9. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Investoren-Ansichten zeigen vor der NDA sauber «Region AG» statt einer leeren Ortsangabe.',
        detail:
          'Ort und PLZ sind vor der Unterzeichnung der Vertraulichkeitserklärung geschützt. Suche, Karten, Objektseite und Teaser zeigen nun durchgehend die Region — ohne leere Adresszeilen oder Platzhalter wie «undefined».',
      },
    ],
  },
  {
    version: 'v1.14.414',
    date: '9. September 2026',
    changes: [
      { type: 'improvement', text: 'Investor-Ansichten: null-sicherer Fallback city → region_label vor NDA (SearchPage.tsx:401, PropertyTeaserPage.tsx:171, PropertyTeaserContent.tsx:122)' },
    ],
  },
  {
    version: 'v1.14.413',
    date: '9. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Investoren-Ansichten zeigen vor der NDA sauber «Region AG» statt einer leeren Ortsangabe.',
        detail:
          'Ort und PLZ sind vor der Unterzeichnung der Vertraulichkeitserklärung geschützt. Suche, Karten, Objektseite und Teaser zeigen nun durchgehend die Region — ohne leere Adresszeilen oder Platzhalter wie «undefined».',
      },
    ],
  },
  {
    version: 'v1.14.412',
    date: '9. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Der Compliance-Dialog öffnet sich nach dem Schliessen nicht mehr ein zweites Mal.',
        detail:
          'Wer sich identifiziert oder eine beaufsichtigte Firma bestätigt hat und den Dialog danach mit «X» oder «Escape» schliesst, bekam ihn 1,5 Sekunden später erneut auf dem Schritt «2 Erwerbsberechtigung» präsentiert. Der Dialog wird jetzt pro Sitzung genau einmal angeboten; die Lex-Koller-Einschätzung bleibt in den Einstellungen jederzeit startbar.',
      },
    ],
  },
  {
    version: 'v1.14.411',
    date: '9. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Identifikation und Lex Koller sind neu ein einziger Compliance-Schritt.',
        detail:
          'Nach dem ersten Login öffnet sich nur noch EIN Dialog mit zwei Stationen: «1 Identifikation → 2 Erwerbsberechtigung». Was aus der Identifikation oder aus Zefix bereits bekannt ist — Staatsangehörigkeit, Firma, Schweizer Firmensitz — wird nicht mehr gefragt. Bei Schweizer Staatsangehörigkeit entfallen die Fragen zu Bewilligung und Wohnsitz.',
      },
      {
        type: 'feature',
        text: 'Beaufsichtigte Institutionen überspringen die Lex-Koller-Fragen.',
        detail:
          'Ist die Gesellschaft als beaufsichtigte oder registrierte Institution erfasst (FINMA, OAK BV, SIX), genügt eine einzige Bestätigung, dass sie nicht zu mehr als einem Drittel ausländisch beherrscht ist (Art. 5 BewG). Der Status erscheint in den Einstellungen als «Nicht anwendbar (institutionell)».',
      },
    ],
  },
  {
    version: 'v1.14.410',
    date: '9. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Objektseite für Investoren: nur noch ein Button «NDA bestätigen».',
        detail:
          'Bisher erschien der Hinweis «Dokumente nach NDA-Freigabe verfügbar» zweimal — einmal in der Dokumente-Karte und einmal darunter als roter Zweitbutton. Neu gibt es genau einen Aufruf im gewohnten Stil.',
      },
      {
        type: 'improvement',
        text: 'NDA-Dialog: Provisionsinformation nur einmal, Checkboxen einheitlich.',
        detail:
          'Der allgemeine Hinweis zur Vermittlungsgebühr entfällt, wenn für das Objekt bereits Konditionen (Anhang B) angezeigt werden. Beide Zustimmungs-Checkboxen sind jetzt gleich gross und die Texte klarer voneinander abgegrenzt.',
      },
    ],
  },
  {
    version: 'v1.14.409',
    date: '9. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Teaser-Seite: Der Call-to-Action führt neu direkt zum Plattformvertrag.',
        detail:
          'Interessenten ohne Konto geben auf der öffentlichen Teaser-Seite nur ihre E-Mail-Adresse ein und landen sofort im Plattformvertrag. Ist der Link nicht an diese Adresse gebunden, senden wir ihn per E-Mail. Wer bereits einen aktiven Plattformvertrag hat, wird zur Anmeldung geführt. Name, Firma und UID lassen sich unter «Ich bin neu hier» optional ergänzen.',
      },
    ],
  },
  {
    version: 'v1.14.408',
    date: '8. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Bestätigungsseite: Eingaben bleiben erhalten und die Seite springt nicht mehr zurück.',
        detail:
          'Die Onboarding-Daten werden neu nur noch einmal pro Link geladen. Bisher konnte ein erneuter Ladevorgang die Seite mitten im Erfassen wieder auf die Startansicht zurücksetzen — etwa beim Öffnen des Korrektur-Formulars oder beim Erfassen der zweiten Unterschrift.',
      },
    ],
  },
  {
    version: 'v1.14.407',
    date: '8. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Bestätigungsseite: zweite Unterschrift bei Kollektivunterschrift angeben.',
        detail:
          'Zeichnet Ihre Firma kollektiv, fragt die Bestätigungsseite neu die zweite unterschriftsberechtigte Person ab — wahlweise aus den bekannten Personen (Name · Funktion) oder von Hand erfasst, jeweils mit E-Mail-Adresse. Bestätigen ist erst möglich, wenn die Angaben vollständig sind. Danach zeigt die Seite beide Unterzeichnenden; der Plattformvertrag geht an beide zur Unterschrift.',
      },
    ],
  },
  {
    version: 'v1.14.406',
    date: '7. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Test-Seite für die Identifikation ist auf der Testumgebung wieder erreichbar.',
        detail:
          'Die Simulationsseite des Ident-Anbieters wird für Staging und die Review-Apps neu beim Erstellen der Testumgebung freigeschaltet. Der Ablauf «Ausweis + Selfie» lässt sich damit auf der Testumgebung komplett durchklicken. In der Produktion bleibt die Seite unerreichbar.',
      },
    ],
  },
  {
    version: 'v1.14.405',
    date: '7. September 2026',
    changes: [
      { type: 'fix', text: 'KYC-Stub-Seite auf Staging jetzt über Build-Flag erreichbar' },
      { type: 'improvement', text: 'Dockerfile und CI für Staging Build-Flags konfiguriert' },
      { type: 'improvement', text: 'KYC-Komponenten, Services und Typen bereinigt' },
    ],
  },
  {
    version: 'v1.14.404',
    date: '7. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Test-Seite für die Identifikation ist auf der Testumgebung wieder erreichbar.',
        detail:
          'Die Simulationsseite des Ident-Anbieters wird für Staging und die Review-Apps neu beim Erstellen der Testumgebung freigeschaltet. Der Ablauf «Ausweis + Selfie» lässt sich damit auf der Testumgebung komplett durchklicken. In der Produktion bleibt die Seite unerreichbar.',
      },
    ],
  },
  {
    version: 'v1.14.403',
    date: '7. September 2026',
    changes: [
      { type: 'fix', text: 'KYC-Stub-Seite wird jetzt auf Staging und Review-Apps korrekt geroutet' },
      { type: 'improvement', text: 'Build-Flag VITE_KYC_STUB_ENABLED in Dockerfile und CI konfiguriert' },
      { type: 'improvement', text: 'Tests für KYC-Konstanten hinzugefügt' },
    ],
  },
  {
    version: 'v1.14.402',
    date: '7. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Test-Seite für die Identifikation ist auf der Testumgebung wieder erreichbar.',
        detail:
          'Die Simulationsseite des Ident-Anbieters wird für Staging und die Review-Apps neu beim Erstellen der Testumgebung freigeschaltet. Der Ablauf «Ausweis + Selfie» lässt sich damit auf der Testumgebung komplett durchklicken. In der Produktion bleibt die Seite unerreichbar.',
      },
    ],
  },
  {
    version: 'v1.14.401',
    date: '7. September 2026',
    changes: [
      { type: 'fix', text: 'KYC-Stub-Seite auf Staging jetzt über Build-Flag erreichbar' },
      { type: 'improvement', text: 'Dockerfile und CI für Staging Build-Flags konfiguriert' },
      { type: 'improvement', text: 'KYC-Komponenten, Services und Typen bereinigt' },
    ],
  },
  {
    version: 'v1.14.400',
    date: '7. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Test-Seite für die Identifikation ist auf der Testumgebung wieder erreichbar.',
        detail:
          'Die Simulationsseite des Ident-Anbieters wird für Staging und die Review-Apps neu beim Erstellen der Testumgebung freigeschaltet. Der Ablauf «Ausweis + Selfie» lässt sich damit auf der Testumgebung komplett durchklicken. In der Produktion bleibt die Seite unerreichbar.',
      },
    ],
  },
  {
    version: 'v1.14.399',
    date: '7. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Test-Seite für die Identifikation ist auf der Testumgebung wieder erreichbar.',
        detail:
          'Die Simulationsseite des Ident-Anbieters wird für Staging und die Review-Apps neu beim Erstellen der Testumgebung freigeschaltet. Der Ablauf «Ausweis + Selfie» lässt sich damit auf der Testumgebung komplett durchklicken. In der Produktion bleibt die Seite unerreichbar.',
      },
    ],
  },
  {
    version: 'v1.14.398',
    date: '7. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Nach einer Korrekturmeldung auf der Onboarding-Seite gibt es neu eine Erklärung und einen Weg hinaus.',
        detail:
          'Die Abschluss-Seite zeigt, wie es weitergeht, wiederholt die gesendete Meldung und nennt den zuständigen Berater samt E-Mail, sofern verfügbar. Die Schaltflächen «Zur Startseite» und «Fenster schliessen» stehen bereit; «Zur Startseite» erscheint neu auch bei abgelaufenem Link, Fehlern und bereits bestätigten Angaben.',
      },
    ],
  },
  {
    version: 'v1.14.397',
    date: '7. September 2026',
    changes: [
      { type: 'feature', text: 'Onboarding-Seite zeigt Abschlussseite mit Erklärung nach Korrekturmeldung' },
      { type: 'feature', text: 'Beraterkontakt auf Abschlussseite verfügbar für weitere Unterstützung' },
      { type: 'feature', text: '«Zur Startseite»- und «Fenster schließen»-Button auch bei Fehler/Abgelaufen' },
    ],
  },
  {
    version: 'v1.14.396',
    date: '7. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Nach einer Korrekturmeldung auf der Onboarding-Seite gibt es neu eine Erklärung und einen Weg hinaus.',
        detail:
          'Die Abschluss-Seite zeigt, wie es weitergeht, wiederholt die gesendete Meldung und nennt den zuständigen Berater samt E-Mail, sofern verfügbar. Die Schaltflächen «Zur Startseite» und «Fenster schliessen» stehen bereit; «Zur Startseite» erscheint neu auch bei abgelaufenem Link, Fehlern und bereits bestätigten Angaben.',
      },
    ],
  },
  {
    version: 'v1.14.395',
    date: '7. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Onboarding-Seite: «Keine Kosten ohne Transaktion» steht neu zuoberst in der Karte «Honorar».',
        detail:
          'Die Karte «Honorar» zeigt als erste Zeile grün und mit Häkchen, dass der Rahmenvertrag keine Grundgebühr und keine laufenden Kosten verursacht — Kosten entstehen nur bei einer erfolgreichen Transaktion. Danach folgen wie bisher Käufer- und Verkäuferprovision.',
      },
    ],
  },
  {
    version: 'v1.14.394',
    date: '7. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Identitätsprüfung bleibt nicht mehr auf «Läuft» stehen, wenn kein Ergebnis eintrifft.',
        detail:
          'Meldet die Prüfung nach zwei Minuten kein Ergebnis oder liefert sie gar keine Schritte, zeigt der Dialog neu den Hinweis «Fast geschafft — wir prüfen Ihre Angaben manuell und melden uns bei Ihnen». Offene Schritte erscheinen als «In Prüfung» und die Schaltfläche «Weiter» lässt sich wieder benutzen.',
      },
    ],
  },
  {
    version: 'v1.14.393',
    date: '7. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Die Identitätsprüfung läuft jetzt sichtbar Schritt für Schritt ab — mit Live-Status pro Prüfung.',
        detail:
          'Nach der Identifikation sehen Sie statt eines Ladebalkens jeden einzelnen Prüfschritt: Identität, Handelsregister (Zefix), Zeichnungsberechtigung, SECO-Sanktionsliste, Transparenzregister und Beaufsichtigung. Jeder Schritt zeigt seinen Status und — wo vorhanden — die konkrete Fundstelle, etwa den Handelsregister-Eintrag mit UID. Der letzte Prüflauf bleibt in den Einstellungen einsehbar.',
      },
      {
        type: 'improvement',
        text: 'Der Hinweis auf das neue Geldwäschereigesetz spricht bis zum 1. Oktober 2026 korrekt von «ab», danach von «seit».',
      },
    ],
  },
  {
    version: 'v1.14.392',
    date: '7. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Die Test-Seite für die Identifikation wird in der Produktion nicht mehr ausgeliefert und leitet nur noch auf Immotron-Seiten zurück.',
        detail:
          'Die Simulationsseite des Ident-Anbieters existiert jetzt ausschliesslich auf Entwicklungs- und Testumgebungen. Zudem wird die Rücksprung-Adresse geprüft: Verweist sie auf eine fremde Domain, landen Nutzerinnen und Nutzer auf dem Dashboard statt auf der fremden Seite.',
      },
    ],
  },
  {
    version: 'v1.14.391',
    date: '7. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Einmalige Identifikation nach GwG — Erstlogin-Dialog mit Ausweis + Selfie, SwissID oder Bestätigung für beaufsichtigte Firmen.',
        detail:
          'Investoren und Verkäufer sehen beim ersten Login einen erklärten Schritt: warum identifiziert wird, wie lange es dauert und dass kein Ausweisbild gespeichert wird. Zur Wahl stehen Ausweis + Selfie, die Anmeldung mit SwissID oder — für Banken, Versicherungen, Fonds, Pensionskassen und kotierte Gesellschaften — eine kurze Selbstdeklaration mit Aufsichtsbehörde und Referenz. «Später» ist jederzeit erlaubt, der Dialog kommt beim nächsten Login wieder. Der Status ist im Kopfbereich und unter Einstellungen › Compliance sichtbar, wo die Identifikation auch neu gestartet werden kann. Der Lex-Koller-Dialog erscheint erst nach abgeschlossener Identifikation.',
      },
    ],
  },
  {
    version: 'v1.14.390',
    date: '7. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Lex-Koller-Dialog erscheint nur noch einmal pro Investor statt bei jedem Seitenaufruf.',
        detail:
          'Wird der Dialog geschlossen oder auf «Später erledigen» gesetzt, öffnet er sich auch nach Navigation und Reload nicht mehr. Stattdessen weist eine dezente Karte im Dashboard auf die ausstehende Einschätzung hin und verlinkt in die Einstellungen, wo das Assessment jederzeit startbar bleibt. Nach abgeschlossener Einschätzung verschwinden Dialog und Hinweis.',
      },
    ],
  },
  {
    version: 'v1.14.389',
    date: '7. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Onboarding-Seite: Konditionen neu in drei Karten «Vertrag / Honorar / Kontaktschutz» mit klarer Käufer- und Verkäuferprovision.',
        detail:
          'Der Block zeigt zuoberst den Vertragstyp, darunter Laufzeit, Verlängerung und Kündigungsfrist. Im Honorar-Block steht die Käuferprovision hervorgehoben, die Verkäuferprovision erscheint nur wenn vorhanden, dazu der Transparenzhinweis. Konditionen ohne Wert (z.B. Exklusivität) werden nicht mehr als leere Zeile gerendert; ein Hinweis erklärt, dass die Konditionen wortgleich im Vertrag stehen.',
      },
    ],
  },
  {
    version: 'v1.14.388',
    date: '6. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'UI-Texte nennen neu die Marke «Immotron» und als Rechtsträgerin die Uniplacement GmbH statt «Immotron AG».',
        detail:
          'Alle Footer (Login, Dashboard, Hilfe, Onboarding, Teaser, Signaturseiten) zeigen «© Jahr Immotron · Uniplacement GmbH». Die Vertragsvorschau blendet die tatsächlich gewählte Vertragspartei ein (Fallback: Marke «Immotron»), und die KI-Klausel «Behörden-Bevollmächtigung» nennt neutral «die Vermittlerin gemäss Vertragskopf» statt einer fixen Firma.',
      },
    ],
  },
  {
    version: 'v1.14.387',
    date: '6. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Signaturseite: Der AGB-Block weist die Bedingungen neu als Plattform-Nutzungsbedingungen der Betreiberin aus und zeigt allfällige ergänzende Bedingungen des Mandanten.',
        detail:
          'Beschreibung, Link und Checkbox nennen die Betreiberin (Standard: Uniplacement GmbH) sowie die Vertragsgegenpartei und halten fest, dass für Honorar und Konditionen der Einzelvertrag vorgeht. Hat der Mandant eigene ergänzende Bedingungen, erscheint ein zweiter Link und die Bestätigung umfasst beide Regelwerke.',
      },
    ],
  },
  {
    version: 'v1.14.386',
    date: '6. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'AGB v1.1: Die Seite «AGB» enthält neu reine Plattform-Nutzungsbedingungen der Uniplacement GmbH.',
        detail:
          'Alle kommerziellen Klauseln (Honorar, Doppelmäkelei, Kontaktschutz-Sanktion, Nachlaufklausel) sind entfallen — dafür gilt ausschliesslich der Einzelvertrag. Neu geregelt sind Konto und Zugang (Onboarding-/Magic-Link), Teaser/NDA/Dossiers, Angebots- und Verkaufsverfahren, elektronische Signatur, Identifikation und Sorgfaltspflichten, Datenschutz inkl. KI-Verarbeitung und Auftragsverarbeiter im Ausland, Nutzungsrechte, Verfügbarkeit sowie das Änderungsverfahren für die AGB. Die AGB gelten neu für alle Nutzer der Plattform, unabhängig davon, ob der Einzelvertrag mit der Betreiberin oder mit einem Mandanten abgeschlossen wird.',
      },
      {
        type: 'fix',
        text: 'AGB-Seite: korrekte UID der Uniplacement GmbH (CHE-265.053.706), c/o-Adresse sowie Version und Gültigkeitsdatum werden wieder angezeigt.',
        detail:
          'Der Footer nannte die UID der Immotron AG. Zudem überschrieben generische Übersetzungen den Untertitel und die Versionszeile, sodass «Version 1.1 · Gültig ab 1. Oktober 2026» nicht sichtbar war — in allen vier Sprachen korrigiert.',
      },
    ],
  },
  {
    version: 'v1.14.385',
    date: '4. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Signaturseite zeigt, ob die Zeichnungsberechtigung geprüft ist (Handelsregister, manuell oder nicht verifiziert).',
        detail:
          'Auf der öffentlichen Signaturseite steht neu hinter dem Unterzeichner ein Badge zur Verifikationsquelle sowie die Unterschriftsart (Einzel-/Kollektivunterschrift). Ist die Zeichnungsberechtigung nicht gegen das Handelsregister geprüft, erscheint ein oranger Hinweis, dass die Unterschrift die eigene Berechtigung bestätigt — unterschreiben bleibt weiterhin möglich. In der Liste der Vertragsparteien wird der Badge pro Partei angezeigt.',
      },
    ],
  },
  {
    version: 'v1.14.384',
    date: '4. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'Signaturseite zeigt «Sie unterschreiben für …» und alle Vertragsparteien mit Signatur-Status.',
        detail:
          'Auf der öffentlichen Signaturseite steht neu zuoberst hervorgehoben, für welche Partei unterschrieben wird (inkl. Unterzeichner und Funktion). Darunter listet der Abschnitt «Vertragsparteien» alle Unterzeichner mit Partei, Name, Rolle und Status (signiert/ausstehend) — ebenso auf der Ansicht «Bereits unterzeichnet». Zusätzlich werden die Rollen «Betreiberin» und «Benutzer» korrekt übersetzt statt roh angezeigt.',
      },
    ],
  },
  {
    version: 'v1.14.383',
    date: '4. September 2026',
    changes: [
      {
        type: 'feature',
        text: 'NDA-Dialog zeigt die Konditionen des Objekts (Provisionsmodell und Sonderkonditionen) vor der Bestätigung an.',
        detail:
          'Der Abschnitt «Konditionen für dieses Objekt» listet Provisionsmodell mit Einheitssatz bzw. Stufen, Hinweis auf MwSt. und Fälligkeit sowie allfällige Sonderkonditionen — bei Objekten mit Anhang B objektspezifisch, sonst die Standardkonditionen des Mandanten. Die Bestätigung ist erst nach einer zusätzlichen Konditions-Checkbox möglich; danach werden die akzeptierten Konditionen im Erfolgsdialog nochmals angezeigt.',
      },
    ],
  },
  {
    version: 'v1.14.382',
    date: '4. September 2026',
    changes: [
      {
        type: 'fix',
        text: 'Signaturseite: «Vertrag anzeigen» und «AGB anzeigen» öffnen beide einen neuen Tab mit Kopfzeile und Button «Zurück zur Unterzeichnung».',
        detail:
          'Bisher wurde der Vertrag inline als eingebettetes Fenster angezeigt, die AGB dagegen in einem neuen Tab ohne Rückweg. Neu führen beide auf eine eigene Ansicht im öffentlichen Layout (Logo, Vertragsnummer, PDF-Download); der eingegebene Zustand auf der Signaturseite (AGB-Häkchen, Unterschrift) bleibt erhalten.',
      },
    ],
  },
  {
    version: 'v1.14.381',
    date: '4. September 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev C&C: MR-Panel zeigt neu die Abdeckung aller Merge-Train-Projekte (API, Web, Autopilot, Teaser, Infra) mit MR-Zahl pro Projekt.',
      },
      {
        type: 'improvement',
        text: 'Dev C&C: Obsolete Status-Gruppe «Telegram Eingang» entfernt; leere Status-Gruppen sind standardmässig ausgeblendet und per Toggle einblendbar.',
      },
      {
        type: 'fix',
        text: 'Dev C&C: Fehlgeschlagene und abgeschlossene Tasks zeigen wieder ihren Log-Tail statt «Noch keine Log-Daten vorhanden».',
      },
      {
        type: 'fix',
        text: 'Dev C&C: Phasen-Anzeige weist Fehlschläge im Implementierungs-Retry-Loop korrekt Phase 7 (Implementierung) statt Phase 9 (Tests) zu.',
      },
      {
        type: 'fix',
        text: 'Dev C&C: Fehler-Box zeigt nur noch die eigentliche Fehlermeldung; rohe API-Antworten sind auf Wunsch einblendbar.',
      },
    ],
  },
  {
    version: 'v1.14.380',
    date: '30. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'Teaser: Die Grundstücksfläche wird neu mit dem eindeutigen Label «Grundstücksfläche» angezeigt statt dem generischen «Fläche» – keine Verwechslung mehr mit Wohn-/Nutzfläche.',
      },
    ],
  },
  {
    version: 'v1.14.379',
    date: '30. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'Pendenzenassistent: Das Detail-Panel (inkl. «Endgültig löschen») ist neu jederzeit über einen sichtbaren «Details / Bearbeiten»-Button im Kopfbereich erreichbar – auch wenn die Pendenz bereits mit Kontakt, Firma oder Objekt verknüpft ist.',
      },
    ],
  },
  {
    version: 'v1.14.378',
    date: '30. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'Pendenzen: Neue Aktion «Endgültig löschen» (nur für Admin oder Ersteller) entfernt eine Pendenz inkl. Chat, Verlauf, Wissens-Bausteinen und allen Sub-Pendenzen unwiderruflich – mit klarer Bestätigung und deutlich abgesetzt vom normalen (wiederherstellbaren) Löschen.',
      },
    ],
  },
  {
    version: 'v1.14.377',
    date: '30. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'Pendenzen-Assistent: Recherche-Ergebnisse (Firmen, Personen, allgemein) werden jetzt sauber mit Überschriften, Tabellen und anklickbaren Quellen-Links dargestellt – statt als zerrissener Rohtext ohne Formatierung. Die Firmen-Recherche zeigt zusätzlich zur Kompakt-Karte den vollständigen Bericht inkl. Quellen.',
      },
    ],
  },
  {
    version: 'v1.14.376',
    date: '26. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'Käufer-Teaser: Der «Zugang anfordern»-Link aus der Teaser-E-Mail funktioniert jetzt. Externe Empfänger ohne Konto öffnen über den persönlichen Link eine öffentliche, NDA-sichere Teaser-Ansicht (Region statt Adresse, keine Dokumente) – statt wie bisher auf einer leeren Seite zu landen.',
      },
    ],
  },
  {
    version: 'v1.14.375',
    date: '26. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'Käufer-Onboarding: Neue öffentliche Bestätigungsseite. Über den personalisierten Link aus der Onboarding-E-Mail können Käufer ihre Firmen- und Personendaten prüfen, bestätigen (und direkt zur Vertragsunterzeichnung weitergeleitet werden) oder eine Korrektur melden.',
      },
    ],
  },
  {
    version: 'v1.14.374',
    date: '25. Juni 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Wohnungsmix: Bei einer einzelnen Wohnungsfläche genügt neu ein Feld — der «bis»-Wert wird beim Speichern automatisch gleich «von» gesetzt. Eine echte Flächenspanne lässt sich weiterhin über «Spanne» erfassen.',
      },
    ],
  },
  {
    version: 'v1.14.373',
    date: '25. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'Mieterspiegel: Pro Position lässt sich neu die Nutzungsart wählen (Wohnung, Gewerbe, Parkplatz, Werbefläche, Antenne, Sonstige) — so werden auch Parkplätze und Sondereinnahmen sauber abgebildet.',
      },
    ],
  },
  {
    version: 'v1.14.372',
    date: '23. Juni 2026',
    changes: [
      {
        type: 'improvement',
        text: 'KI-Objekterfassung: Die Entscheidung «Bilder verarbeiten» wird bereits beim Upload mitgeschickt — bei ausgeschalteten Bildern entfällt die Bild-Pipeline und der Upload ist spürbar schneller.',
      },
    ],
  },
  {
    version: 'v1.14.371',
    date: '23. Juni 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Mikro-/Makrolage werden für Interessenten ohne NDA verschwommen mit «NDA erforderlich»-Hinweis dargestellt — kein Klartext und keine leere Lücke mehr (Teaser-Ansicht und Investoren-Detailseite).',
      },
    ],
  },
  {
    version: 'v1.14.370',
    date: '22. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'Organisationen: Neue natürlichsprachliche Suchleiste — freie Frage (z.B. «alle die Gewerbe in Zürich suchen») wird in editierbare «Verstanden als»-Chips übersetzt; jeder Treffer zeigt den Grund (🎯 Suchmandat / 📄 Zefix-Zweck).',
      },
    ],
  },
  {
    version: 'v1.14.369',
    date: '22. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'KI-Erfassung: Neue Checkbox steuert, ob aus Dokumenten extrahierte Bilder in die Objekt-Galerie übernommen werden (standardmässig aus).',
      },
    ],
  },
  {
    version: 'v1.14.368',
    date: '19. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'Investment Highlights: Fettschrift wird jetzt korrekt gerendert (Investor-Detail, Teaser, Vorschau) — keine sichtbaren Sternchen mehr im Text.',
      },
    ],
  },
  {
    version: 'v1.14.367',
    date: '18. Juni 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Teaser-Vorschau: Der Preis steht nun rechtsbündig in derselben Zeile wie die Adresse statt als Overlay auf dem Bild.',
      },
    ],
  },
  {
    version: 'v1.14.366',
    date: '17. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'Medien & Texte: Jedes Textfeld (Subheader, Highlights, Beschreibung, Makro- und Mikrolage) hat nun einen eigenen «Neu generieren»-Button. Optional kann eine Bemerkung (z.B. «kürzer halten», «mehr auf Rendite fokussieren») angegeben werden, dann wird nur dieses eine Feld neu generiert. Der Button für alle Texte bleibt erhalten.',
      },
    ],
  },
  {
    version: 'v1.14.365',
    date: '17. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'Teaser-Vorschau: Preis wird nicht mehr doppelt angezeigt. Er erscheint nur noch als Badge unten rechts auf dem Titelbild (zuvor zusätzlich als Block über dem Bild).',
      },
    ],
  },
  {
    version: 'v1.14.364',
    date: '17. Juni 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Quality Gate und Teaser-Detail nutzen jetzt ein geteiltes Objekt-Cockpit: gleiche Käufer-Verwaltung (Investorengruppen mit Mitglieder-Drilldown, KI-Vorschläge, manuelle Personen-Suche) und dieselbe Teaser-Vorschau überall.',
      },
      {
        type: 'feature',
        text: 'Teaser-Detail zeigt neu den Web/E-Mail-Newsletter-Umschalter der Vorschau, den Gruppen-Drilldown und den Zuordnungs-Status der Käufer (aktiv/Onboarding nötig).',
      },
    ],
  },
  {
    version: 'v1.14.363',
    date: '17. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'KI-Erfassung: Upload-Limit pro Datei auf 250 MB korrigiert (war versehentlich auf 50 MB gesenkt). Hinweis- und Fehlertexte zeigen jetzt 250 MB pro Datei.',
      },
    ],
  },
  {
    version: 'v1.14.362',
    date: '17. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'Käufer-Zuordnung (Quality Gate & Teaser-Detail): Die Personen-Suche findet jetzt auch Privatpersonen ohne Firmen-Verknüpfung — auch bei Suche nach exakter E-Mail-Adresse.',
      },
    ],
  },
  {
    version: 'v1.14.361',
    date: '17. Juni 2026',
    changes: [
      {
        type: 'improvement',
        text: 'KI-Erfassung: Upload-Limit pro Datei auf 250 MB angeglichen (entspricht dem Server-Limit), Anzahl Dateien unbegrenzt. Hinweis- und Fehlertexte zeigen jetzt das korrekte Limit.',
      },
      {
        type: 'fix',
        text: 'KI-Erfassung: Verzeichnis-Upload übernimmt jetzt dieselben Dateitypen wie der Einzeldatei-Upload (z. B. .msg/.eml/.heic werden nicht mehr still verworfen).',
      },
    ],
  },
  {
    version: 'v1.14.360',
    date: '12. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'Teaser-Detail im Quality-Gate-Layout: links die Teaser-Vorschau wie der Investor sie sieht, rechts die Käufer-Verwaltung (Investorengruppen, KI-Vorschläge, manuelle Personen-Suche — nachträglich ergänzbar) plus neue Statistik-Sektion mit Öffnungen, Datei-Ansichten und Downloads pro Investor.',
      },
      {
        type: 'feature',
        text: 'Quality Gate: Neue Personen-Suche — CRM-Kontakte oder Firmen manuell als Käufer zuordnen, mit sofort sichtbarer Klassifizierung (aktiv / Onboarding nötig).',
      },
    ],
  },
  {
    version: 'v1.14.359',
    date: '12. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'CRM Kontakt-Detail: Neuer Schalter «Für Vertragsunterzeichnung verifiziert» (Abschnitt Verifikation) mit Bestätigungsdialog und Status-Badge im Kontakt-Header — öffnet die Self-Service-Vertragsanforderung auch ohne Zefix-Zeichnungsberechtigung (nur Admin/Sales).',
      },
    ],
  },
  {
    version: 'v1.14.358',
    date: '11. Juni 2026',
    changes: [
      {
        type: 'feature',
        text: 'Teaser-Detail: Neue Sektion «Zugeordnete Käufer» zeigt nach der Quality-Gate-Freigabe alle Käufer mit aktivem Teaser-Zugriff sowie übersprungene Käufer ohne aktiven Vertrag (Zugriff folgt automatisch nach Vertragsabschluss).',
      },
      {
        type: 'improvement',
        text: 'Quality Gate: Die ausgewählten KI-Käufervorschläge werden atomar mit der Freigabe gesendet (ein Request) — keine separaten, teilweise fehlschlagenden Zugriffs-Calls mehr.',
      },
    ],
  },
  {
    version: 'v1.14.357',
    date: '11. Juni 2026',
    changes: [
      {
        type: 'fix',
        text: 'Bildverwaltung: Der Schalter «Teaser/Nur NDA» pro Foto wirkt jetzt sofort — die Teaser-Vorschau (Stufe «Käufer ohne NDA») zeigt nur noch Teaser-freigegebene Bilder und der Schalter-Zustand bleibt nach Tab-Wechsel erhalten.',
      },
    ],
  },
  {
    version: 'v1.14.356',
    date: '11. Juni 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Teaser-Ansicht: Kaufpreis als Badge direkt am Titelbild, Beschreibung neu direkt nach den Kennzahlen als einspaltiger Blocktext, technische Details immer ausgeklappt (keine einklappbaren Bereiche mehr).',
      },
    ],
  },
  {
    version: 'v1.14.355',
    date: '10. Juni 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Immobilien-Bearbeitung: Die Bereiche KI-Hinweise, KI-Extraktionsprotokoll und Wissen sind zu einem internen Tab «KI-Hinweise» zusammengefasst — mit Warn-Indikator am Tab bei ⚠️-Hinweisen; alte Links auf die bisherigen Tabs leiten automatisch um.',
      },
    ],
  },
  {
    version: 'v1.14.354',
    date: '10. Juni 2026',
    changes: [
      { type: 'fix', text: 'Applikationsweit: Zahlenfelder ändern ihren Wert nicht mehr durch Scrollen mit dem Mausrad — unbeabsichtigte Datenänderungen beim Durchscrollen von Formularen sind damit ausgeschlossen.' },
    ],
  },
  {
    version: 'v1.14.353',
    date: '10. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzen-Chat: Schwach gerankte Suchtreffer im Transparenz-Trace sind standardmässig ausgeblendet und per «X weitere Treffer anzeigen» einsehbar — die Assistenten-Antwort bleibt das prominenteste Element.' },
    ],
  },
  {
    version: 'v1.14.352',
    date: '10. Juni 2026',
    changes: [
      { type: 'feature', text: 'Objektansicht: KI-Hinweise aus der Extraktion (Eigentümer, Dienstbarkeiten, Grundpfand) als aufklappbares Panel sichtbar — Warnungen hervorgehoben.' },
      { type: 'feature', text: 'Pricing-Tab: Eigenkapitalrendite editierbar und Preis pro m² als berechnete Kennzahl angezeigt.' },
      { type: 'feature', text: 'Standort-Tab: Steuerfüsse für juristische Personen (Gewinn- und Kapitalsteuer, Gemeinde + Kanton) ergänzt.' },
    ],
  },
  {
    version: 'v1.14.351',
    date: '10. Juni 2026',
    changes: [
      { type: 'fix', text: 'Dev C&C: Verschobene Kacheln im Abhängigkeits-Graph bleiben stehen und werden gespeichert (localStorage); Abhängigkeits-Linien sind nicht mehr hinter den Kacheln verdeckt.' },
    ],
  },
  {
    version: 'v1.14.350',
    date: '10. Juni 2026',
    changes: [
      { type: 'fix', text: 'Dev C&C: Abhängigkeits-Graph im Deps-Tab wird wieder angezeigt (Container hatte Höhe 0).' },
    ],
  },
  {
    version: 'v1.14.349',
    date: '9. Juni 2026',
    changes: [
      { type: 'feature', text: 'Demo-Anfragen: Die Nachricht des Interessenten wird auf der Pendenz jetzt prominent als Post-it/Notiz angezeigt.' },
    ],
  },
  {
    version: 'v1.14.348',
    date: '9. Juni 2026',
    changes: [
      { type: 'feature', text: 'Investorengruppen-Vorschau: Mitglieder zeigen jetzt ihren Typ (Benutzer, Firma oder Kontakt).' },
    ],
  },
  {
    version: 'v1.14.347',
    date: '9. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Vertrags-Wizard: Vertragspartner-Schritt aufgeräumt — eine gemeinsame Suche über CRM (Kontakte/Firmen) und Zefix; gewählte Person ist direkt der Unterzeichner.' },
      { type: 'improvement', text: 'Vertrags-Wizard: Unterzeichner einer Firma werden in EINER Liste mit Quellen-Markierung (Im CRM / Zefix-neu / nicht mehr im Register) angezeigt; fehlende E-Mail inline ergänzbar und direkt im CRM gespeichert.' },
      { type: 'improvement', text: 'Vertrags-Wizard: Freie Manuell-Erfassung entfernt — Vertragspartner werden immer als sauberer CRM-Datensatz gewählt oder neu angelegt.' },
    ],
  },
  {
    version: 'v1.14.346',
    date: '9. Juni 2026',
    changes: [
      { type: 'feature', text: 'Partner und Unterzeichner einheitlich aus CRM/Zefix wählbar' },
      { type: 'feature', text: 'Neue E-Mail-Kontakte direkt inline zu CRM hinzufügbar' },
      { type: 'improvement', text: 'Contract Wizard Partner-Schritt aufgeräumt und vereinfacht' },
    ],
  },
  {
    version: 'v1.14.345',
    date: '9. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Vertrags-Wizard: Vertragspartner-Schritt aufgeräumt — eine gemeinsame Suche über CRM (Kontakte/Firmen) und Zefix; gewählte Person ist direkt der Unterzeichner.' },
      { type: 'improvement', text: 'Vertrags-Wizard: Unterzeichner einer Firma werden in EINER Liste mit Quellen-Markierung (Im CRM / Zefix-neu / nicht mehr im Register) angezeigt; fehlende E-Mail inline ergänzbar und direkt im CRM gespeichert.' },
      { type: 'improvement', text: 'Vertrags-Wizard: Freie Manuell-Erfassung entfernt — Vertragspartner werden immer als sauberer CRM-Datensatz gewählt oder neu angelegt.' },
    ],
  },
  {
    version: 'v1.14.344',
    date: '9. Juni 2026',
    changes: [
      { type: 'fix', text: 'AGB-Seite: Rechtsträger auf Uniplacement GmbH (Samstagernstrasse 55, 8832 Wollerau) korrigiert — «Immotron» bleibt Plattform-/Markenname.' },
    ],
  },
  {
    version: 'v1.14.343',
    date: '9. Juni 2026',
    changes: [
      { type: 'feature', text: 'CRM: Neuer Investorengruppen-Filter (Mehrfachauswahl) in Organisationen und Kontakten — filtert die Liste nach zugeordneten Gruppen.' },
    ],
  },
  {
    version: 'v1.14.342',
    date: '9. Juni 2026',
    changes: [
      { type: 'feature', text: 'Quality Gate: Investorengruppen lassen sich jetzt aufklappen und zeigen die konkreten Mitglieder (Namen) — nicht mehr nur die Anzahl.' },
    ],
  },
  {
    version: 'v1.14.341',
    date: '9. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Firmenadressen werden in Vertrag, Suchmandat und CRM-Detailansicht jetzt einheitlich aus der zentral berechneten Firmenadresse übernommen — auch wenn die Adresse nur als zusätzliche Adresse hinterlegt ist.' },
    ],
  },
  {
    version: 'v1.14.340',
    date: '8. Juni 2026',
    changes: [
      { type: 'feature', text: 'CRM Kontakt- und Firmen-Modal: Eingaben werden beim Tab-Wechsel automatisch gespeichert (Auto-Save/Entwurf), neue Firmen werden angelegt sobald der Name erfasst ist, und ein «Gespeichert»-Indikator sowie eine Warnung beim Schliessen mit ungespeicherten Änderungen verhindern Datenverlust.' },
    ],
  },
  {
    version: 'v1.14.339',
    date: '9. Juni 2026',
    changes: [
      { type: 'fix', text: 'Vertragserstellung: Die im CRM hinterlegte Firmenadresse wird jetzt zuverlässig in den Vertrag übernommen — auch wenn sie nur als zusätzliche Adresse und nicht in den klassischen Adressfeldern gespeichert ist.' },
    ],
  },
  {
    version: 'v1.14.338',
    date: '9. Juni 2026',
    changes: [
      { type: 'fix', text: 'Dokument-Upload funktioniert wieder überall — im Property-Dossier (Tab Medien) sowie bei CRM-Kontakten und -Organisationen. Der Fehler 422 beim Hochladen tritt nicht mehr auf.' },
    ],
  },
  {
    version: 'v1.14.337',
    date: '8. Juni 2026',
    changes: [
      { type: 'fix', text: 'E-Mail-Vorlagen: Die Vorlagenliste lädt wieder zuverlässig — der Fehler „Vorlagen konnten nicht geladen werden" tritt nicht mehr auf.' },
    ],
  },
  {
    version: 'v1.14.336',
    date: '8. Juni 2026',
    changes: [
      { type: 'feature', text: 'Spracheingabe: Aufnahme stoppt automatisch nach 5 Minuten und transkribiert. Der Timer warnt sichtbar (gelb) kurz vor dem Limit, und während der Transkription erscheint ein klarer „Transkribiere…"-Hinweis.' },
      { type: 'improvement', text: 'Spracheingabe: Längere Aufnahmen werden nicht mehr vorzeitig abgebrochen (Upload-Zeitlimit erhöht), und bei zu langen Aufnahmen erscheint eine verständliche Fehlermeldung statt eines stillen Fehlers.' },
    ],
  },
  {
    version: 'v1.14.335',
    date: '8. Juni 2026',
    changes: [
      { type: 'improvement', text: 'feat(voice): Aufnahme-Guardrails — Auto-Stop 5 Min, Timer-Warnung, \'Transkribiere…\'-Status, Upload-Timeout erhöhen' },
    ],
  },
  {
    version: 'v1.14.334',
    date: '8. Juni 2026',
    changes: [
      { type: 'feature', text: 'Spracheingabe: Aufnahme stoppt automatisch nach 5 Minuten und transkribiert. Der Timer warnt sichtbar (gelb) kurz vor dem Limit, und während der Transkription erscheint ein klarer „Transkribiere…"-Hinweis.' },
      { type: 'improvement', text: 'Spracheingabe: Längere Aufnahmen werden nicht mehr vorzeitig abgebrochen (Upload-Zeitlimit erhöht), und bei zu langen Aufnahmen erscheint eine verständliche Fehlermeldung statt eines stillen Fehlers.' },
    ],
  },
  {
    version: 'v1.14.333',
    date: '8. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Spracheingabe: Der Bildschirm bleibt während einer Sprachaufnahme wach (Screen Wake Lock), damit die Aufnahme nicht durch die automatische Bildschirmsperre unterbrochen wird.' },
    ],
  },
  {
    version: 'v1.14.332',
    date: '8. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenz per Spracheingabe: Audio-Upload schlug fehl (422). Der Upload sendet jetzt den korrekten Multipart-Header, sodass die Transkription wieder funktioniert — auch auf Mobile/PWA.' },
    ],
  },
  {
    version: 'v1.14.331',
    date: '8. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Teaser & Dossier: Beschreibung und Lage (Makro/Mikro) werden jetzt modern gerendert — mit Abschnitten, Stichpunkten und Hervorhebungen statt einem Fliesstext-Block. Lange Beschreibungen erscheinen auf grossen Bildschirmen zweispaltig.' },
    ],
  },
  {
    version: 'v1.14.330',
    date: '8. Juni 2026',
    changes: [
      { type: 'feature', text: 'TeaserProseMarkdown-Komponente für modern formatierte Beschreibungen mit Aufzählungen und Hervorhebungen' },
      { type: 'feature', text: 'Mehrspaltige Layouts und strukturierte Inhalte in Immobilien-Teasern' },
      { type: 'improvement', text: 'Unit-Tests für Markdown-Rendering-Komponente implementiert' },
    ],
  },
  {
    version: 'v1.14.329',
    date: '8. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Teaser & Dossier: Beschreibung und Lage (Makro/Mikro) werden jetzt modern gerendert — mit Abschnitten, Stichpunkten und Hervorhebungen statt einem Fliesstext-Block. Lange Beschreibungen erscheinen auf grossen Bildschirmen zweispaltig.' },
    ],
  },
  {
    version: 'v1.14.328',
    date: '8. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Vertrags-Wizard: Honorar-Schieber neu im Bereich 0–5 % (statt 0,5–10 %). Prozentsatz und Monatswerte lassen sich jetzt zusätzlich direkt als Zahl eintippen; der Schieber folgt und ungültige Werte werden auf den erlaubten Bereich begrenzt.' },
      { type: 'improvement', text: 'Vertrags-Wizard: Standard-Honorar für Käufer und Verkäufer auf 0,75 % angepasst.' },
    ],
  },
  {
    version: 'v1.14.327',
    date: '8. Juni 2026',
    changes: [
      { type: 'fix', text: 'Teaser-Vorschau: «Als Kampagne versenden» ist erst aktiv, wenn die Immobilie das Quality Gate bestanden hat (Status «Aktiv»). Vorher zeigt der deaktivierte Button den Hinweis «Erst nach Quality Gate verfügbar».' },
    ],
  },
  {
    version: 'v1.14.326',
    date: '8. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Vertrags- und Honorar-Bereich: Wording vereinheitlicht — «Provision» heisst neu durchgängig «Honorar» (Honorarmodelle, Vertragsdetails, AGB sowie EN/FR/IT analog).' },
    ],
  },
  {
    version: 'v1.14.325',
    date: '8. Juni 2026',
    changes: [
      { type: 'fix', text: 'Dossier-Dokumente: Upload von Word- und Excel-Dateien (.doc/.docx/.xls/.xlsx) funktioniert jetzt; zudem werden Dateien mit gültiger Endung auch dann akzeptiert, wenn der Browser keinen Dateityp meldet.' },
    ],
  },
  {
    version: 'v1.14.324',
    date: '8. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Teaser: Beim Gebäude-Typ wird jetzt nur der Haupttyp angezeigt — die ausgeschriebenen Subtypen entfallen für eine übersichtlichere Darstellung. In der internen Detail-Ansicht bleiben die Subtypen sichtbar.' },
    ],
  },
  {
    version: 'v1.14.323',
    date: '8. Juni 2026',
    changes: [
      { type: 'fix', text: 'Teaser-Vorschau: Die Lage-Karte stürzte beim Umschalten auf «Käufer mit NDA» und «Intern» ab. Sie wird jetzt als eingebettete Karte angezeigt — robust und ohne Fehler in allen Teaser- und Quality-Gate-Ansichten.' },
    ],
  },
  {
    version: 'v1.14.322',
    date: '7. Juni 2026',
    changes: [
      { type: 'fix', text: 'Web-Teaser: Der Kennzahlen-Block über den Bildern ist entfernt. Die prägnante Karten-Darstellung wird jetzt für die Investment-Highlights (nach den Bildern, im Tab «Medien & Texte» gepflegt) verwendet — eine einheitliche Teaser-Ansicht.' },
    ],
  },
  {
    version: 'v1.14.321',
    date: '6. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzen-Assistent: Lange Recherchen (über 60 Sekunden) zeigen jetzt einen Live-Fortschritt mit Sekundenzähler statt eines Zeitüberschreitungs-Fehlers — das Ergebnis erscheint automatisch, sobald es fertig ist. Der Abbrechen-Button stoppt die Generierung weiterhin sofort.' },
    ],
  },
  {
    version: 'v1.14.320',
    date: '6. Juni 2026',
    changes: [
      { type: 'fix', text: 'KI-Objekterfassung: Ein kurzer Unterbruch der Verbindung beim «Objekt erstellen» (z.B. während eines Server-Updates) zeigt keinen falschen Fehler mehr — das Frontend prüft jetzt, ob das Objekt trotzdem erstellt wurde, und leitet bei Erfolg weiter.' },
      { type: 'improvement', text: 'Immobilienliste: Bei einem kurzzeitigen Verbindungsproblem wird die Liste automatisch erneut geladen, statt dauerhaft «Immobilien konnten nicht geladen werden» anzuzeigen.' },
    ],
  },
  {
    version: 'v1.14.319',
    date: '6. Juni 2026',
    changes: [
      { type: 'fix', text: 'KI-Konfidenz wurde in der Erfassungs-Zusammenfassung und im Extraktionsprotokoll fälschlich als «9700 %» statt «97 %» angezeigt. Der Wert wird jetzt korrekt skaliert dargestellt.' },
    ],
  },
  {
    version: 'v1.14.318',
    date: '6. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Recherche läuft jetzt einheitlich nur noch über Sub-Pendenzen. Die Aktionen «Recherche intern/extern» auf gepinnten Notizen samt Bestätigungs-Overlay wurden entfernt — Recherche wird wie gewohnt im Chat bzw. als Sub-Pendenz gestartet.' },
    ],
  },
  {
    version: 'v1.14.317',
    date: '6. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Recherche-aus-PostIt-Funktion entfernt — einheitliches Recherche-Design' },
      { type: 'improvement', text: 'Redundante Recherche-Dialoge entfernt — klarere Taskbearbeitung' },
      { type: 'improvement', text: 'Fortan nur Sub-Pendenzen für Recherche — vereinfachtes Konzept' },
    ],
  },
  {
    version: 'v1.14.316',
    date: '6. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Recherche läuft jetzt einheitlich nur noch über Sub-Pendenzen. Die Aktionen «Recherche intern/extern» auf gepinnten Notizen samt Bestätigungs-Overlay wurden entfernt — Recherche wird wie gewohnt im Chat bzw. als Sub-Pendenz gestartet.' },
    ],
  },
  {
    version: 'v1.14.315',
    date: '6. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: E-Mail-Vorschau scrollt jetzt innerhalb ihres Bereichs — das Feld «Was soll ich anpassen?» überlappt die Signatur nicht mehr und sitzt sauber getrennt am unteren Rand.' },
    ],
  },
  {
    version: 'v1.14.314',
    date: '6. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Generierte E-Mails lassen sich direkt nachbearbeiten — Feld «Was soll ich anpassen?» mit «Anpassen» übernimmt nur das gewünschte Delta (Basistext bleibt wortgleich), «Erneuern» generiert die Mail frisch und berücksichtigt gepinnte Notizen. Vorherige Versionen bleiben im Verlauf.' },
    ],
  },
  {
    version: 'v1.14.313',
    date: '6. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Verschiebbare Trennlinie zwischen Chat und Kontext-Panel — Breite per Maus (oder Pfeiltasten) anpassbar, bleibt nach Reload erhalten (Desktop).' },
    ],
  },
  {
    version: 'v1.14.312',
    date: '5. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Investor-Teaser: Finanzkennzahlen jetzt zuoberst — Bruttorendite, Mietertrag, Nettorendite (mit ausgewiesener Annahme), Faktor, Leerstand und CHF/m² als hervorgehobener Snapshot above-the-fold.' },
      { type: 'improvement', text: 'Investor-Teaser: Echter, klickbarer Call-to-Action (Interesse bekunden / NDA unterzeichnen / Dossier öffnen) als sticky Leiste statt totem Status-Badge.' },
      { type: 'improvement', text: 'Investor-Teaser: NDA als Wert statt Mangel — geschützte Angaben als «Nach NDA» sichtbar, Standort als geblurte Region-Kachel, technische Details einklappbar, Skeleton-Ladezustand.' },
    ],
  },
  {
    version: 'v1.14.311',
    date: '5. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Investor-Teaser: Die Bildergalerie zeigt Fotos jetzt randlos im Editorial-Format (4:3 mobil, 16:9 Desktop) ohne graue Balken, mit glasiger Steuerung und Vollbild-Lightbox für Hochformat- und Grundrissbilder.' },
      { type: 'improvement', text: 'Investor-Teaser: Einheitliche Typografie (Satoshi) mit klarer 5-Stufen-Skala für Titel, Zwischentitel und Fliesstext — ruhigeres, hochwertigeres Erscheinungsbild.' },
    ],
  },
  {
    version: 'v1.14.310',
    date: '5. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Objekterfassung «Medien & Texte»: Die Marketing- und Übersetzungstexte sind neu in drei übersichtliche Karten gegliedert (Titel, Beschreibung, Lage), mit grösserer Schrift, mitwachsenden Textfeldern, Zeichenzähler und klarer Markierung von KI-Vorschlägen.' },
      { type: 'improvement', text: 'Übersetzungen: Aktiver Sprach-Reiter ist deutlicher hervorgehoben, nicht übersetzte Felder werden markiert und können per Klick aus der deutschen Originalfassung übernommen werden.' },
    ],
  },
  {
    version: 'v1.14.309',
    date: '5. Juni 2026',
    changes: [
      { type: 'feature', text: 'Medien-Tab mit 3-Karten-Layout für bessere Übersicht' },
      { type: 'feature', text: 'Auto-grow Textareas und KI-Vorschlag-Markierung' },
      { type: 'improvement', text: 'Text vergrößert und unnötige Komponente entfernt' },
    ],
  },
  {
    version: 'v1.14.308',
    date: '5. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Objekterfassung «Medien & Texte»: Die Marketing- und Übersetzungstexte sind neu in drei übersichtliche Karten gegliedert (Titel, Beschreibung, Lage), mit grösserer Schrift, mitwachsenden Textfeldern, Zeichenzähler und klarer Markierung von KI-Vorschlägen.' },
      { type: 'improvement', text: 'Übersetzungen: Aktiver Sprach-Reiter ist deutlicher hervorgehoben, nicht übersetzte Felder werden markiert und können per Klick aus der deutschen Originalfassung übernommen werden.' },
    ],
  },
  {
    version: 'v1.14.307',
    date: '5. Juni 2026',
    changes: [
      { type: 'feature', text: 'Teaser-Vorschau: Neuer Button «Als Kampagne versenden». Aus dem Teaser entsteht mit einem Klick eine E-Mail-Kampagne (identischer NDA-sicherer Inhalt wie für Investoren); danach übernimmt der bestehende Kampagnen-Flow mit Inhalt, Empfängern, Test-Versand und Versand.' },
      { type: 'feature', text: 'Empfänger können direkt aus den Investorengruppen des Objekts übernommen werden; alternativ steht der CSV-Upload bereit. Der Versand ist erst möglich, wenn das NDA-Gate grün ist.' },
    ],
  },
  {
    version: 'v1.14.306',
    date: '5. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Objekterfassung: Reiter neu sortiert — «Medien & Texte» liegt jetzt direkt nach «Objekt» (Objekt → Medien → Standort → Preise), passend zum Erfassungsablauf.' },
      { type: 'improvement', text: 'Reiter «Preise & Konditionen» heisst neu «Preise & Miete» und ist in zwei klare Bereiche gegliedert: «Preise & Konditionen» (Preis, Konditionen, Finanzkennzahlen) und «Mietsituation» (Wohnungsmix und Mietspiegel). Die Mietsituation klappt bei Objekten ohne Einheiten (z.B. EFH) automatisch zu.' },
    ],
  },
  {
    version: 'v1.14.305',
    date: '5. Juni 2026',
    changes: [
      { type: 'feature', text: 'Teaser-Vorschau: Neue Umschaltung «Web-Ansicht / E-Mail-Newsletter» im Quality Gate und in der Objekt-Teaser-Vorschau. Die Newsletter-Ansicht zeigt den Teaser exakt so, wie er als E-Mail versendet wird (identischer Renderer, Browser == E-Mail).' },
      { type: 'feature', text: 'Newsletter-Vorschau blendet den NDA-Status ein: Anzahl freigegebener Teaser-Bilder, erst nach NDA sichtbare Bilder sowie eine Bestätigung für standortkritische Bilder. Ist der Versand gesperrt, werden die Gründe rot angezeigt.' },
    ],
  },
  {
    version: 'v1.14.304',
    date: '5. Juni 2026',
    changes: [
      { type: 'feature', text: 'Bild-Editor (Tab Medien): Pro Bild lässt sich jetzt direkt umschalten, ob es im Teaser (vor NDA) oder nur nach NDA sichtbar ist. Standort-kritische Bilder (Fassade, Luftbild, Umgebung) sind mit einem Standort-Badge markiert und müssen bewusst bestätigt werden, bevor sie im Teaser erscheinen.' },
      { type: 'feature', text: 'Galerie-Kopf zeigt auf einen Blick, wie viele Bilder im Teaser, nur nach NDA bzw. mit Standort-Risiko sind — inklusive Teaser-Vorschau auf das anonyme Bild-Set, das Käufer ohne NDA sehen.' },
      { type: 'improvement', text: 'KI-Bildbeschreibung wird als Bildunterschrift angezeigt; der volle Text öffnet sich in einer Lightbox.' },
    ],
  },
  {
    version: 'v1.14.303',
    date: '5. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Spracheingabe app-weit vereinheitlicht: Alle Voice-Eingaben (Pendenz-Schnellerfassung, Pendenz-Beschreibung, Recherche, Assistenten-Chat) nutzen jetzt dieselbe Aufnahme (MediaRecorder + Whisper). Aufnahmen brechen bei Denkpausen nicht mehr ab — gestoppt wird nur durch erneuten Klick.' },
    ],
  },
  {
    version: 'v1.14.302',
    date: '4. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Assistent: Neuer «💡 Vorschläge»-Button im Chat — zeigt jederzeit (auch bei laufendem Verlauf) die gleichen ziel-getriebenen Vorschläge wie beim Start einer neuen Pendenz. Ein Klick auf einen Vorschlag löst die Aktion direkt aus.' },
    ],
  },
  {
    version: 'v1.14.301',
    date: '4. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzen-Assistent: Der E-Mail-Eingabebereich ist schlanker — Empfänger, Auftrag und «E-Mail erstellen» sind direkt sichtbar, die Feinsteuerung (Ton, Länge, Zweck, Sprache) und der Auto-Kontext liegen neu hinter einem «Optionen»-Schalter mit Kurzübersicht.' },
    ],
  },
  {
    version: 'v1.14.300',
    date: '4. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzen-Assistent: Einheitliche Sprache — die überlappenden Begriffe «PostIt», «Wissensbaustein» und «Baustein» heissen jetzt durchgängig «Notiz» (de/en/fr/it).' },
      { type: 'improvement', text: 'Pendenzen-Assistent: Die technischen Quellen-Badges (Claude/Perplexity) auf den Karten wurden entfernt — welches KI-Modell eine Karte erzeugt hat, ist für die tägliche Arbeit irrelevant.' },
    ],
  },
  {
    version: 'v1.14.299',
    date: '4. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzen-Assistent: Schlägt die KI-Käufer-Ermittlung ein Objekt vor, ohne dass eine Immobilie verknüpft ist, führt die Karte nicht mehr ins Leere — ein „Objekt verknüpfen"-Button öffnet direkt die Objekt-Auswahl.' },
      { type: 'improvement', text: 'Pendenzen-Assistent: Die rangierte KI-Käuferliste wird neu als eigene Karten-Variante mit Rang, Fit-Score, Empfehlung und Begründung dargestellt — klar von den Wissens-Post-its getrennt.' },
    ],
  },
  {
    version: 'v1.14.298',
    date: '4. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzen-Assistent: Im Trace „Was der Assistent gemacht hat" wird die Relevanz jedes Treffers jetzt als farbiger Balken statt als nackte Zahl angezeigt — auf einen Blick erkennbar, wie gut eine Quelle zur Frage passt.' },
    ],
  },
  {
    version: 'v1.14.297',
    date: '4. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Assistent: Neue, leere Pendenz startet jetzt mit einem Kontext-Anker und bis zu 4 ziel-getriebenen Quick-Start-Vorschlägen (verb-zuerst, mit konkreten Namen) statt eines leeren Chats.' },
      { type: 'feature', text: 'Pendenzen-Assistent: Markierter Text im Chat-Verlauf kann per schwebendem „📌 Als Post-it"-Button direkt als Wissens-Baustein gepinnt werden — der Mensch entscheidet, was Post-it wird.' },
      { type: 'improvement', text: 'Pendenzen-Assistent: Ist (noch) kein Kontakt/keine Firma/kein Objekt verknüpft, schlägt eine prominente Karte das Verknüpfen vor — keine ins Leere laufenden Vorschläge.' },
    ],
  },
  {
    version: 'v1.14.296',
    date: '4. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzen-Assistent: KI-Käufer-Ermittlung in die bestehende Oberfläche integriert — Auslöser ist jetzt ein Action-Chip in der Aktionsleiste statt eines separaten Buttons.' },
      { type: 'improvement', text: 'Pendenzen-Assistent: Käufer-Ergebnisse erscheinen als Post-It-Karten (mit Fit-Score, Empfehlung und Begründung) statt in einem eigenen Panel.' },
    ],
  },
  {
    version: 'v1.14.295',
    date: '4. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Assistent: Neuer Button «KI-Käufer ermitteln» — findet jederzeit passende Käufer aus dem CRM mit Fit-Score (Objekt-Auswahl, falls keines verknüpft ist).' },
      { type: 'feature', text: 'Pendenzen-Assistent: Vorgeschlagene Aktionskarte zur KI-Käufer-Ermittlung lässt sich per Klick bestätigen und ausführen.' },
    ],
  },
  {
    version: 'v1.14.294',
    date: '4. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Dev-Kommandozentrale: Tasks mit gesperrten Dateien (Sensitiv-Freigabe nötig) erscheinen jetzt sichtbar in der Rubrik «Wartet auf Antwort» mit Zähler — der Freigabe-Button ist so in ein bis zwei Klicks erreichbar.' },
    ],
  },
  {
    version: 'v1.14.293',
    date: '4. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Unter jeder Antwort lässt sich neu ein Trace «Was der Assistent gemacht hat» aufklappen — er zeigt Verknüpfung, semantische Such-Query mit Trefferzahl, genutzte Quellen mit Score, Portfolio-Status und eine Kurz-Begründung. So ist von aussen sichtbar, warum etwas (nicht) gefunden wurde.' },
    ],
  },
  {
    version: 'v1.14.292',
    date: '4. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Neue Wissens-Bausteine (Vertrag, App-Wissen, CRM- und Dokument-Profile, Amtsdaten) erscheinen jetzt mit eigenem Icon, eigener Farbe und passendem Label statt als generisches «Chat»-Post-it.' },
    ],
  },
  {
    version: 'v1.14.291',
    date: '3. Juni 2026',
    changes: [
      { type: 'fix', text: 'Quality Gate: Die KI-Käufervorschläge werden jetzt korrekt geladen und angezeigt (zuvor "Käufervorschläge konnten nicht geladen werden" wegen falschem API-Pfad).' },
    ],
  },
  {
    version: 'v1.14.290',
    date: '3. Juni 2026',
    changes: [
      { type: 'feature', text: 'CRM: Im Dokumente-Tab von Kontakten und Organisationen lassen sich jetzt bestehende Dokumente per Suche verknüpfen — ohne erneuten Upload.' },
    ],
  },
  {
    version: 'v1.14.289',
    date: '3. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzen: Die Firmenrecherche erscheint jetzt immer als Post-it — die alte rechte Recherche-Ansicht mit Firmakachel wurde entfernt. Auch bei fehlgeschlagener Datenextraktion wird der Roh-Recherche-Text als Post-it gespeichert.' },
    ],
  },
  {
    version: 'v1.14.288',
    date: '3. Juni 2026',
    changes: [
      { type: 'fix', text: 'Quality Gate: Die KI-Käufervorschläge werden wieder angezeigt. Das Genehmigungs-Panel las das Antwortfeld unter falschem Namen, wodurch die Vorschlagsliste leer blieb.' },
    ],
  },
  {
    version: 'v1.14.287',
    date: '3. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Objektkarten in Suche, Favoriten, Dashboard-Reihen, den Investor-Widgets und der Verkäufer-Liste nutzen jetzt eine einzige, einheitliche Karten-Komponente. Die NDA-Redaktion (Adresse/Standort ohne Freigabe verbergen) läuft überall nach derselben zentralen Regel.' },
    ],
  },
  {
    version: 'v1.14.286',
    date: '3. Juni 2026',
    changes: [
      { type: 'feature', text: 'PropertyCard vereinheitlicht für Search, Favoriten, Dashboard, Listen' },
      { type: 'improvement', text: 'PropertyTeaserCard konsolidiert — redundante Komponente entfernt' },
    ],
  },
  {
    version: 'v1.14.285',
    date: '3. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Objektkarten in Suche, Favoriten, Dashboard-Reihen, den Investor-Widgets und der Verkäufer-Liste nutzen jetzt eine einzige, einheitliche Karten-Komponente. Die NDA-Redaktion (Adresse/Standort ohne Freigabe verbergen) läuft überall nach derselben zentralen Regel.' },
    ],
  },
  {
    version: 'v1.14.284',
    date: '3. Juni 2026',
    changes: [
      { type: 'feature', text: 'Objekt-Tab «Medien & Texte»: Neuer Button «Texte neu generieren» erzeugt Subheader, Highlights, Beschreibung sowie Makro- und Mikrolage per KI neu und übernimmt sie direkt in die deutschen Texte. Behebt Objekte mit fehlgeschlagener KI-Generierung ohne Neuanlage.' },
    ],
  },
  {
    version: 'v1.14.283',
    date: '3. Juni 2026',
    changes: [
      { type: 'feature', text: 'Quality Gate: KI-Käufervorschläge mit Eignungs-Score und aufklappbarer Begründung. Mehrfachauswahl — die ausgewählten Käufer erhalten bei Freigabe automatisch Zugriff auf den Teaser.' },
    ],
  },
  {
    version: 'v1.14.282',
    date: '3. Juni 2026',
    changes: [
      { type: 'feature', text: 'Interner Teaser-Block (Stufe «Intern») zeigt nun die zugeordneten Investorengruppen samt ihren Mitgliedern. Im Quality Gate erscheinen die ausgewählten Gruppen live in der Vorschau — Käufer-Stufen bleiben unberührt.' },
    ],
  },
  {
    version: 'v1.14.281',
    date: '3. Juni 2026',
    changes: [
      { type: 'feature', text: 'Einheitliche Teaser-Ansicht mit «Vorschau als»-Umschalter (Käufer ohne NDA / Käufer mit NDA / Intern) in Käufer-Teaser, Quality Gate, Objekterfassung/-Bearbeitung und Listing-Teaser. Interne Nutzer sehen damit exakt die redigierte Käufer-Ansicht.' },
      { type: 'improvement', text: 'Quality Gate und Editor-Vorschau zeigen die echte Käufer-Sicht mit zentraler NDA-Redaktion (Adresse, Koordinaten, Kataster) — kein Daten-Leak interner Felder.' },
    ],
  },
  {
    version: 'v1.14.280',
    date: '3. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Wird aus einer gepinnten Karte eine Sub-Pendenz erstellt, verschwindet die Karte aus «Gepinnt» und erhält ein anklickbares Badge «→ Sub-Pendenz #X», das direkt zur neuen Sub-Pendenz springt.' },
      { type: 'feature', text: 'Pendenzenassistent: Beim Pinnen in einer Sub-Pendenz lässt sich nun wählen, ob der Wissensbaustein an die Sub-Pendenz oder an die Hauptpendenz angeheftet wird. Top-Level-Pendenzen pinnen unverändert ohne Rückfrage.' },
    ],
  },
  {
    version: 'v1.14.279',
    date: '3. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: In einer Sub-Pendenz getippte Chat-Nachrichten erschienen fälschlich auch im Chat der Hauptpendenz. Haupt- und Sub-Pendenz haben jetzt strikt getrennte Chatverläufe — beim Wechsel werden nur noch die Nachrichten der aktiven Pendenz angezeigt.' },
    ],
  },
  {
    version: 'v1.14.278',
    date: '2. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Die Übersicht zeigt die Info-Sektionen jetzt als Masonry-Pinnwand (2-spaltig ab Tablet, lückenlos verschachtelt) — mehr «Klebezettel an der Wand»-Look. Mobil bleibt es einspaltig.' },
    ],
  },
  {
    version: 'v1.14.277',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Gepinnte Post-it-Karten haben in der Übersicht jetzt exakt dieselbe Farbe wie im Chat (Strategie violett, Recherche blau, Fakt grün, Empfehlung gelb).' },
      { type: 'improvement', text: 'Pendenzenassistent: Datum und Funktionen einer Post-it-Karte sitzen nur noch in der Kopfzeile — der Karteninhalt nutzt darunter die volle Breite.' },
      { type: 'fix', text: 'Pendenzenassistent: Beim Start einer Recherche aus einem Post-it wird der vollständige Notiz-Text vorausgefüllt statt nach 200 Zeichen abgeschnitten.' },
    ],
  },
  {
    version: 'v1.14.276',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Entwickler-Dashboard: Der Phasen-Stepper im Task-Detail zeigt während der Implementierung nicht mehr fälschlich «Tests» als laufend. Der frühe Analyse-Aufruf wird nicht mehr mit der echten Implementierung verwechselt.' },
    ],
  },
  {
    version: 'v1.14.275',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Entwickler-Dashboard: Die Phasen-Übersicht im Task-Detail berücksichtigt jetzt den letzten Reset. Nach einem Reset zeigt sie den Reset-Stand statt des alten, am weitesten erreichten Standes — konsistent mit der kompakten Phasen-Status-Bar.' },
    ],
  },
  {
    version: 'v1.14.274',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Entwickler-Dashboard: Der Pipeline-Tab zeigt jetzt immer den aktuellen Lauf und aktualisiert sich live. Während ein Lauf aktiv ist wird im Sekundentakt nachgeladen (mit Live-Indikator), nach Abschluss stoppt das Polling. Neuer Status-Kopf mit Gesamtstatus, Zeitpunkt des letzten Abrufs und manuellem Neu-laden-Button; beim Zurückkehren ins Fenster wird sofort frisch geladen.' },
    ],
  },
  {
    version: 'v1.14.273',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Dev-Dashboard zeigt aktuellen Pipeline-Lauf jetzt live aktualisiert' },
      { type: 'improvement', text: 'Pipeline-Phasen-Logik refaktoriert für bessere Wartbarkeit' },
    ],
  },
  {
    version: 'v1.14.272',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Entwickler-Dashboard: Der Pipeline-Tab zeigt jetzt immer den aktuellen Lauf und aktualisiert sich live. Während ein Lauf aktiv ist wird im Sekundentakt nachgeladen (mit Live-Indikator), nach Abschluss stoppt das Polling. Neuer Status-Kopf mit Gesamtstatus, Zeitpunkt des letzten Abrufs und manuellem Neu-laden-Button; beim Zurückkehren ins Fenster wird sofort frisch geladen.' },
    ],
  },
  {
    version: 'v1.14.271',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Entwickler-Dashboard: Der Pipeline-Tab zeigt jetzt immer den aktuellen Lauf und aktualisiert sich live. Während ein Lauf aktiv ist wird im Sekundentakt nachgeladen (mit Live-Indikator), nach Abschluss stoppt das Polling. Neuer Status-Kopf mit Gesamtstatus, Zeitpunkt des letzten Abrufs und manuellem Neu-laden-Button; beim Zurückkehren ins Fenster wird sofort frisch geladen.' },
    ],
  },
  {
    version: 'v1.14.270',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Dev-Dashboard: Der Abhängigkeiten-Tab zeigt bei Tasks ohne Verknüpfung jetzt einen klaren Hinweis "Keine Abhängigkeiten" statt einer leeren Fläche und kein verwaister Einzel-Knoten mehr. Tasks mit depends_on stellen ihre vollständige Kette dar — Vorgänger (wartet auf) und nachgelagerte Tasks (blockiert) — passend zum Inline-Banner.' },
    ],
  },
  {
    version: 'v1.14.269',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Dev-Dashboard: Die Phasen-Status-Bar in Task-Liste und Archiv zeigt jetzt den aktuell laufenden Schritt statt dem am weitesten erreichten — sie läuft dem realen Fortschritt nicht mehr voraus und nutzt dieselbe Phasen-Quelle und -Nummerierung wie Prozess-Tab und Live-Status.' },
    ],
  },
  {
    version: 'v1.14.268',
    date: '2. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Entwickler-Dashboard: Der Events-Tab gruppiert die Roh-Events jetzt nach Pipeline-Phase (aus derselben kanonischen Phasen-Quelle wie der Stepper). Jede Phase ist ein- und ausklappbar, zeigt Dauer und Startzeit, Schlüssel-Events sind hervorgehoben und wiederkehrendes Rauschen (Reindex, geplante/übersprungene Hintergrund-Jobs, Token-Lifecycle) wird standardmässig zusammengefasst und ist auf Wunsch einblendbar.' },
    ],
  },
  {
    version: 'v1.14.267',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'PWA: Nach einem Deploy wird automatisch der neue Build geladen — der Cache-Name enthält jetzt die Build-Version, alte Caches werden beim Aktivieren entfernt und ein Update-Hinweis erscheint, ohne dass ein manueller Hard-Refresh nötig ist.' },
    ],
  },
  {
    version: 'v1.14.266',
    date: '2. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Entwickler-Dashboard: Task-Detail entrümpelt — Header, Abhängigkeiten, Phasen-Stepper und Aktionen bleiben als kompakter Kopfblock beim Scrollen sichtbar (sticky).' },
      { type: 'feature', text: 'Entwickler-Dashboard: Abhängigkeiten zeigen jetzt beidseitig "wartet auf" UND "blockiert" plus einen Deeplink zum Abhängigkeits-Graphen.' },
      { type: 'improvement', text: 'Entwickler-Dashboard: Live-Status bündelt Erstellt/Gestartet/Agent/Laufzeit in einem Panel; Sekundärfelder und Dauer-Aufschlüsselung in eingeklappte Bereiche bzw. den Prozess-Tab verlagert.' },
      { type: 'improvement', text: 'Entwickler-Dashboard: Fehler bei fehlgeschlagenen Tasks oben angezeigt; Live-Abfragen für abgeschlossene Tasks abgeschaltet (weniger Polling).' },
    ],
  },
  {
    version: 'v1.14.265',
    date: '2. Juni 2026',
    changes: [
      { type: 'feature', text: 'Entwickler-Dashboard: Horizontaler Phasen-Stepper in der Task-Detailansicht zeigt alle Ausführungsphasen mit Status, Dauer und Fortschritt above-the-fold.' },
      { type: 'improvement', text: 'Entwickler-Dashboard: Phasen-Definition vereinheitlicht (eine Quelle für Liste, Navigation und Detailansicht) — kein Auseinanderdriften mehr.' },
    ],
  },
  {
    version: 'v1.14.264',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Chat-Karten erscheinen nach dem Schliessen und erneuten Öffnen wieder im Verlauf.' },
    ],
  },
  {
    version: 'v1.14.262',
    date: '2. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Wissens-Bausteine in der Übersicht sind jetzt je nach Typ dezent unterschiedlich eingefärbt — konsistent zu den Chat-Karten.' },
    ],
  },
  {
    version: 'v1.14.261',
    date: '2. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Wissens-Bausteine in der Übersicht haben jetzt im «…»-Menü die Aktion «Sub-Pendenz erstellen» — wie bei den Chat-Karten.' },
    ],
  },
  {
    version: 'v1.14.260',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Wissens-Bausteine in der Übersicht zeigen jetzt ihren Titel wie im Chat — der Titel erscheint nicht mehr doppelt im Text.' },
    ],
  },
  {
    version: 'v1.14.259',
    date: '2. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Im E-Mail-Modus zeigt die Kontext-Anzeige jetzt die Anzahl der gepinnten Wissens-Bausteine — passend dazu, was die E-Mail-Erstellung tatsächlich nutzt.' },
    ],
  },
  {
    version: 'v1.14.258',
    date: '2. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Wissens-Bausteine in der Übersicht nutzen jetzt dieselbe Karten-Optik wie der Chat-Verlauf — einheitlicher Look aus einer Quelle.' },
      { type: 'improvement', text: 'Pendenzenassistent: Wissens-Bausteine werden im breiten Übersichts-Panel zweispaltig dargestellt (eine Spalte auf schmalen/mobilen Ansichten).' },
    ],
  },
  {
    version: 'v1.14.257',
    date: '2. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Im Recherche-Modus zeigt ein Chip den automatisch erkannten Recherche-Typ (Firma, Person, Produkt/API, Preise, Markt) — manuell übersteuerbar.' },
      { type: 'improvement', text: 'Pendenzenassistent: Während einer Recherche erscheint der Status «Tiefe Recherche läuft …», da die Deep-Research 30 Sekunden bis mehrere Minuten dauern kann.' },
    ],
  },
  {
    version: 'v1.14.256',
    date: '2. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Generierte E-Mails lassen sich jetzt verwalten — Entwürfe verwerfen (mit «Rückgängig»-Hinweis, ohne Nachfrage) und gesendete E-Mails archivieren. Archivierte E-Mails werden nicht gelöscht, sondern bleiben in einem eigenen Bereich auffindbar. Jede E-Mail-Karte zeigt klar ihren Status («Entwurf» / «Gesendet am …»).' },
    ],
  },
  {
    version: 'v1.14.255',
    date: '2. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Der E-Mail-Composer zeigt jetzt den automatisch berücksichtigten Basis-Kontext (Ziel, Wissens-Bausteine, Kontakt + E-Mail-Verlauf, Firma, Objekt) als Chips an. «0 Dokumente» wird nicht mehr als «kein Kontext» missverstanden — zusätzlich gewählte Elemente sind klar als «Zusätzlich gewählt» ausgewiesen.' },
    ],
  },
  {
    version: 'v1.14.254',
    date: '2. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Wissens-Bausteine in der Übersicht sehen jetzt aus wie die Chat-PostIts (Cremeton, Typ-Icon, Titel + 2 Zeilen, Klick zum Aufklappen) — übersichtlicher und kompakter.' },
      { type: 'improvement', text: 'Wissens-Bausteine: Übersichts-Aktionen (Bearbeiten, Verknüpfung entfernen, Löschen, Kontakt/Organisation erstellen, Recherche intern/extern) liegen jetzt in einem «…»-Menü; Pin und Anhängen bleiben direkt sichtbar.' },
    ],
  },
  {
    version: 'v1.14.253',
    date: '1. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent E-Mail-Modus: Ein Klick auf «E-Mail» öffnet ein kompaktes Panel mit vorausgefülltem Auftrag, Empfänger-Anzeige und sichtbaren Einstellungen (Ton/Länge/Zweck/Sprache). Der Button «✦ E-Mail erstellen» erzeugt den Entwurf direkt — Tippen ist optional, ein leeres Feld erzeugt die E-Mail rein aus dem Kontext.' },
    ],
  },
  {
    version: 'v1.14.252',
    date: '1. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Laufende Chat- und Recherche-Jobs lassen sich jetzt mit einem Stop-Button (oder Escape) abbrechen — die KI-Generierung wird serverseitig wirklich gestoppt.' },
    ],
  },
  {
    version: 'v1.14.251',
    date: '1. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Im Bearbeiten-Modus geänderter E-Mail-Text wird jetzt dauerhaft gespeichert und bleibt nach dem Schliessen und erneuten Öffnen der Pendenz erhalten.' },
    ],
  },
  {
    version: 'v1.14.250',
    date: '1. Juni 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Im Bearbeiten-Modus der E-Mail geänderter Text wird jetzt in die Vorschau übernommen und nicht mehr verworfen.' },
    ],
  },
  {
    version: 'v1.14.249',
    date: '1. Juni 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Wissens-Bausteine lassen sich per Drag & Drop zwischen Recherche und Ziel-Bereich arrangieren. Die Reihenfolge im Ziel bestimmt den E-Mail-Aufbau und wird persistent gespeichert (auf Mobile: Long-Press auf den Griff zum Verschieben).' },
    ],
  },
  {
    version: 'v1.14.248',
    date: '1. Juni 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Wissens-Bausteine zeigen nur noch explizit gepinnte PostIts. Automatisch gespeicherte Chat-Antworten sind hinter einem "Verlauf anzeigen"-Toggle versteckt und visuell gedimmt mit «Verlauf»-Badge gekennzeichnet.' },
      { type: 'fix', text: 'Pin-Button auf Chat-Antworten aktualisiert bestehende Auto-PostIts (UPDATE statt CREATE), sodass keine doppelten Wissens-Bausteine mehr entstehen.' },
    ],
  },
  {
    version: 'v1.14.247',
    date: '1. Juni 2026',
    changes: [
      { type: 'feature', text: 'Spracheingabe im Smart-Create-Dialog für neue Pendenzen und im Recherche-Popup ergänzt.' },
    ],
  },
  {
    version: 'v1.14.246',
    date: '29. Mai 2026',
    changes: [
      { type: 'feature', text: 'Media-Tab Archiv-Sicht: gelöschte Immobilienbilder werden über einen Toggle eingeblendet und können per Klick wiederhergestellt werden.' },
    ],
  },
  {
    version: 'v1.14.245',
    date: '29. Mai 2026',
    changes: [
      { type: 'feature', text: 'Media-Tab zeigt jetzt gelöschte Bilder in separater Archiv-Sicht' },
      { type: 'feature', text: 'Restore-Button ermöglicht Wiederherstellen gelöschter Bilder' },
      { type: 'improvement', text: 'Lokalisierung für neue Media-Archiv-Funktionen in alle 4 Sprachen' },
    ],
  },
  {
    version: 'v1.14.244',
    date: '29. Mai 2026',
    changes: [
      { type: 'feature', text: 'Media-Tab Archiv-Sicht: gelöschte Immobilienbilder werden über einen Toggle eingeblendet und können per Klick wiederhergestellt werden.' },
    ],
  },
  {
    version: 'v1.14.243',
    date: '29. Mai 2026',
    changes: [
      { type: 'feature', text: 'Quarantäne-Anzeige für Immobilien mit fehlgeschlagener KI-Standortermittlung: rotes Badge «Standort fehlt» in der Immobilienliste und Warnbanner in der Detailansicht mit Hinweis auf fehlende Adressdaten.' },
    ],
  },
  {
    version: 'v1.14.242',
    date: '18. Mai 2026',
    changes: [
      { type: 'feature', text: 'Post-Its an Kontakt, Firma oder Immobilie anhängen — neuer Picker mit Tabs, Suche und Vorschlägen aus dem Pendenz-Kontext; neuer «Wissen»-Tab in der Immobilien-Bearbeitung zeigt alle angehefteten Post-Its.' },
    ],
  },
  {
    version: 'v1.14.241',
    date: '18. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenz-Chat: Strategie-Karten am Pendenz-Start werden als Post-It-Karten gerendert (einheitliches PostIt-Format). Klick auf Sub-Pendenz erstellt sofort eine Sub-Pendenz und springt direkt hinein.' },
      { type: 'improvement', text: 'Strategie-Karten erscheinen nur 1× beim Erstellen einer Pendenz; mitten im Gespräch gibt es Beratungs-Karten via Chat, keine frischen Strategy-Cards mehr.' },
    ],
  },
  {
    version: 'v1.14.240',
    date: '18. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenz-Chat: KI-Antworten als strukturierte Post-It-Karten mit Pin / Sub-Pendenz / Anhängen — Quelle (Claude/Perplexity) und Link sichtbar pro Karte.' },
    ],
  },
  {
    version: 'v1.14.239',
    date: '18. Mai 2026',
    changes: [
      { type: 'improvement', text: 'feat(web): Strategie wiederherstellen + ALLE Antworten als Post-Its mit Pin/Sub/Anhaengen' },
    ],
  },
  {
    version: 'v1.14.238',
    date: '18. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Pendenz-Assistent: Block «Empfohlene nächste Schritte» (Strategie-Karten) entfernt — steht nicht im Wiki v11. Onboarding-Vorschläge laufen neu rein über die Chat-Eingabe und die zwei Skill-Chips (Recherche / E-Mail).' },
    ],
  },
  {
    version: 'v1.14.237',
    date: '18. Mai 2026',
    changes: [
      { type: 'fix', text: 'Pendenz-Chat: ChatSkill-TypeScript-Type 1:1 mit Backend (STORY-604) synchronisiert und Recherche-Button sendet jetzt den unifizierten Wiki-v11 Wert «research» — verhindert 422-Validierungsfehler beim Klick auf Recherche-intern/extern.' },
    ],
  },
  {
    version: 'v1.14.236',
    date: '18. Mai 2026',
    changes: [
      { type: 'fix', text: 'Pendenz-Chat: 422-Validierungsfehler vom Backend werden nicht mehr als Roh-Objekt in den Chat geschrieben (React-Crash / weisse Tasks-Page). Pydantic-Detail-Arrays werden zu einer lesbaren Meldung zusammengefasst.' },
    ],
  },
  {
    version: 'v1.14.235',
    date: '18. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenz: Recherche direkt aus einem PostIt starten — neue Buttons «Recherche intern» und «Recherche extern» pro PostIt öffnen ein Modal mit vorbefülltem Suchbegriff. Das Ergebnis landet als neuer PostIt, verknüpft mit der Quell-Notiz (parent_postit_id).' },
    ],
  },
  {
    version: 'v1.14.234',
    date: '18. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenz-Recherche: Firmen- und Kontakt-Ergebnisse erscheinen jetzt direkt als PostIts in der Wissens-Liste — pro Firma ein «Firma»-PostIt, pro Kontakt ein «Person»-PostIt. Das separate Recherche-Panel rechts entfaellt.' },
    ],
  },
  {
    version: 'v1.14.233',
    date: '18. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenz-Chat: KI-Antworten können jetzt mit «Als PostIt speichern» direkt als Wissensbaustein ans Ziel gepinnt werden. Bereits gepinnte Antworten werden markiert.' },
    ],
  },
  {
    version: 'v1.14.232',
    date: '18. Mai 2026',
    changes: [
      { type: 'feature', text: 'Strategie-Karten: Beim Klick auf eine Recherche- oder E-Mail-Karte erscheint jetzt ein Bestätigungsdialog mit editierbarem Ziel-Feld, das beim Erstellen der Sub-Pendenz als Kontext gespeichert wird.' },
      { type: 'improvement', text: 'Sub-Pendenz-Erstellung intern auf /api/tasks/batch migriert — der alte Skill-Endpoint /api/tasks/{id}/skills/create-subtask wird nicht mehr verwendet.' },
    ],
  },
  {
    version: 'v1.14.231',
    date: '18. Mai 2026',
    changes: [
      { type: 'feature', text: 'E-Mail-Timeline — gesendete und empfangene E-Mails erscheinen in der Timeline auf Firma- und Kontakt-Detail mit Betreff, Empfänger/Absender, Datum und Vorschau-Text. Richtungs-Icon (Gesendet/Empfangen) und Anhang-Hinweis für schnelle Orientierung.' },
    ],
  },
  {
    version: 'v1.14.230',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Multi-Entity PostIts — PostIt-Karten in Pendenzen haben jetzt Quick-Buttons «An Firma/Kontakt/Immobilie heften». Firma- und Kontakt-Detail zeigen einen neuen Tab «Wissen» mit allen gehefteten PostIts. Badges auf der Karte zeigen, an welche Entities ein PostIt verknuepft ist.' },
    ],
  },
  {
    version: 'v1.14.229',
    date: '17. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Dead Code entfernt — nicht mehr verwendete ActionChips-Komponente, Cockpit-Quick-Action im GoalBanner sowie ungenutztes suggested_actions Metadata-Feld bereinigt.' },
    ],
  },
  {
    version: 'v1.14.228',
    date: '17. Mai 2026',
    changes: [
      { type: 'fix', text: 'Sub-Pendenz Tabs lassen sich jetzt schliessen — X-Button auf jedem Sub-Tab, leerer Tab schliesst sofort, Tab mit Chat-Inhalt mit Bestaetigung. Hauptpendenz bleibt fix.' },
    ],
  },
  {
    version: 'v1.14.227',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Layout — Ziel als editierbares PostIt im rechten Kontext-Panel (statt horizontalem Balken), Quick Actions unter dem Chat entfernt. Kontakt, Firma, Notizen und Recherche-Bausteine sind jetzt durchgehend als PostIts gestapelt.' },
    ],
  },
  {
    version: 'v1.14.226',
    date: '17. Mai 2026',
    changes: [
      { type: 'fix', text: 'Sub-Pendenz aus Strategie-Kachel übernimmt jetzt Titel und Beschreibung und erbt Kontakt/Firma der Hauptpendenz.' },
      { type: 'fix', text: '"Neue Sub-Pendenz"-Button in der Übersicht erstellt direkt einen Task statt einen Chat-Befehl zu schreiben.' },
    ],
  },
  {
    version: 'v1.14.225',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenz-Onboarding — neue Pendenzen zeigen anklickbare Strategie-Karten (Recherche, Notiz, E-Mail) im Chat. Klick auf eine Karte erstellt direkt einen gepinnten PostIt oder eine Sub-Pendenz und springt in den passenden Sub-Chat.' },
    ],
  },
  {
    version: 'v1.14.224',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'E-Mail-Kontext bei Pendenzen — eingehende E-Mails werden als kompakte, aufklappbare Karte (Betreff, Absender, Datum) im Kontext-Panel angezeigt. Body, To/CC und Inhalt stehen ohne Wechsel in den Mail-Client direkt zur Verfügung.' },
    ],
  },
  {
    version: 'v1.14.223',
    date: '17. Mai 2026',
    changes: [
      { type: 'improvement', text: 'feat: E-Mail-Kontext bei Pendenzen — Body, CRM-Kontakte, Ziel-Formulierung' },
    ],
  },
  {
    version: 'v1.14.222',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'E-Mail-Kontext bei Pendenzen — eingehende E-Mails werden als kompakte, aufklappbare Karte (Betreff, Absender, Datum) im Kontext-Panel angezeigt. Body, To/CC und Inhalt stehen ohne Wechsel in den Mail-Client direkt zur Verfügung.' },
    ],
  },
  {
    version: 'v1.14.221',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'E-Mail-Kontext bei Pendenzen — eingehende E-Mails werden als kompakte, aufklappbare Karte (Betreff, Absender, Datum) im Kontext-Panel angezeigt. Body, To/CC und Inhalt stehen ohne Wechsel in den Mail-Client direkt zur Verfügung.' },
    ],
  },
  {
    version: 'v1.14.220',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Chat-Antworten userfreundlicher — Markdown-Tabellen werden als Mini-Cards gerendert (eine Karte pro Zeile mit Label/Value-Paaren), kein roher Markdown-Dump mehr.' },
    ],
  },
  {
    version: 'v1.14.219',
    date: '17. Mai 2026',
    changes: [
      { type: 'improvement', text: 'feat: Chat-Antworten im Pendenzenassistenten userfreundlicher gestalten [Retry von #734]' },
    ],
  },
  {
    version: 'v1.14.218',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Chat-Antworten userfreundlicher — Markdown-Tabellen werden als Mini-Cards gerendert (eine Karte pro Zeile mit Label/Value-Paaren), kein roher Markdown-Dump mehr.' },
    ],
  },
  {
    version: 'v1.14.217',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Chat-Antworten userfreundlicher — Markdown-Tabellen werden als Mini-Cards gerendert (eine Karte pro Zeile mit Label/Value-Paaren), kein roher Markdown-Dump mehr.' },
    ],
  },
  {
    version: 'v1.14.216',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Sub-Pendenzen als Tabs — neue Tab-Leiste direkt unter dem Ziel. Erster Tab = Hauptpendenz, weitere Tabs für jede Sub-Pendenz. Klick wechselt den Chat, "+"-Button erstellt eine neue Sub-Pendenz und öffnet sie direkt.' },
    ],
  },
  {
    version: 'v1.14.215',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Manuelle Notizen an Pendenzen — Schnell-Eingabefeld im Kontext-Panel, PostIt-Karten mit Inline-Bearbeitung. Notizen werden automatisch im KI-Kontext berücksichtigt (z.B. «kein Videocall», «formal schreiben»).' },
    ],
  },
  {
    version: 'v1.14.214',
    date: '17. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Altlasten entfernt — Vorschlagsliste für Unteraufgaben (SubtaskSuggestionList) sowie zugehörige Action-Types (suggest_subtasks, research_organization) abgebaut. Vorbereitung für das vereinfachte v11-Redesign mit Tabs.' },
    ],
  },
  {
    version: 'v1.14.213',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Ziel-Banner ist jetzt sticky on top über die volle Breite — bleibt beim Scrollen sichtbar, zeigt Anzahl Wissens-Bausteine und gepinnte Notizen, Klick aufs Ziel öffnet den Inline-Editor, Quick-Action öffnet das Cockpit-Panel.' },
    ],
  },
  {
    version: 'v1.14.212',
    date: '17. Mai 2026',
    changes: [
      { type: 'fix', text: 'Firmenrecherche: Schlüsselpersonen-Block entfernt — Bold-Text wie «Management» oder «Property» wird nicht mehr fälschlich als Person mit Avatar dargestellt. Kontakte erscheinen stattdessen als separate Karten aus der Recherche.' },
    ],
  },
  {
    version: 'v1.14.211',
    date: '17. Mai 2026',
    changes: [
      { type: 'improvement', text: 'fix: Schluesselpersonen-Block entfernen — Bold-Text wird falsch als Person geparst' },
    ],
  },
  {
    version: 'v1.14.210',
    date: '17. Mai 2026',
    changes: [
      { type: 'fix', text: 'Firmenrecherche: Schlüsselpersonen-Block entfernt — Bold-Text wie «Management» oder «Property» wird nicht mehr fälschlich als Person mit Avatar dargestellt. Kontakte erscheinen stattdessen als separate Karten aus der Recherche.' },
    ],
  },
  {
    version: 'v1.14.209',
    date: '17. Mai 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Fettgedruckter Text in Chat-Antworten wird jetzt tatsächlich fett dargestellt (vorher nur leicht hervorgehoben).' },
    ],
  },
  {
    version: 'v1.14.208',
    date: '17. Mai 2026',
    changes: [
      { type: 'improvement', text: 'feat: Smart Research Router — internes Wissen zuerst, Perplexity nur extern' },
    ],
  },
  {
    version: 'v1.14.207',
    date: '17. Mai 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Fettgedruckter Text in Chat-Antworten wird jetzt tatsächlich fett dargestellt (vorher nur leicht hervorgehoben).' },
    ],
  },
  {
    version: 'v1.14.206',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Wissens-Bausteine per Drag & Drop zwischen Recherche und Ziel arrangieren — neue Ansicht unter /tasks/knowledge-board, accessibility-konform via @dnd-kit, Reihenfolge bleibt nach Reload erhalten.' },
    ],
  },
  {
    version: 'v1.14.205',
    date: '17. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Chat-Antworten kompakter und actionable — «Nächste Schritte» / «Empfehlungen» werden automatisch erkannt und als eigene Karte mit Check-Icons hervorgehoben.' },
    ],
  },
  {
    version: 'v1.14.204',
    date: '17. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Pendenzenassistent: Skill-Chips auf zwei Buttons reduziert (Recherche + E-Mail) — alles Weitere erkennt die KI automatisch aus dem Chat-Kontext.' },
    ],
  },
  {
    version: 'v1.14.203',
    date: '17. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Recherche-Historie im Panel — frühere Recherchen bleiben als aufklappbare Sektionen erhalten (neueste zuoberst), Header zeigt Suchbegriff + Anzahl Ergebnisse.' },
    ],
  },
  {
    version: 'v1.14.202',
    date: '16. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Wissensbausteine direkt ins CRM übernehmen — neue Buttons «Kontakt», «Organisation» und «Anhängen» pro Karte; mit automatischem Dedup-Check (Zusammenführen, Trotzdem neu, Als Notiz anhängen).' },
    ],
  },
  {
    version: 'v1.14.201',
    date: '16. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Recherche-Ergebnisse werden im Extern-Tab als einzelne, kompakte Karten dargestellt (max. 4 Zeilen, mit «Mehr anzeigen»). Jede Karte hat Aktions-Platzhalter für Pin, Kontakt anlegen und Organisation anlegen.' },
    ],
  },
  {
    version: 'v1.14.200',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Ziel-Kontext nutzt wieder die AI-reformulierte Beschreibung (proposal.description), nicht nur den rohen Benutzertext — STORY-726-Verbesserung wiederhergestellt.' },
    ],
  },
  {
    version: 'v1.14.199',
    date: '16. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Recherche-Panel hat jetzt zwei klar getrennte Tabs — Intern (bestehende CRM-Treffer) und Extern (Perplexity-Recherche). Nach einer Recherche wechselt der Tab automatisch zum passenden Bereich.' },
    ],
  },
  {
    version: 'v1.14.198',
    date: '16. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzenassistent: Wissensbausteine können per Pin als relevant markiert werden — gepinnte Bausteine erscheinen in der neuen Sektion «Gepinnt» und werden vom CEO-Agent als bevorzugter Kontext genutzt.' },
    ],
  },
  {
    version: 'v1.14.197',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent: Smart-Create speichert die AI-Ziel-Reformulierung als Kontext — der GoalBanner zeigt das strukturierte, professionelle Ziel statt des Roh-Freitextes.' },
    ],
  },
  {
    version: 'v1.14.196',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Pendenzenassistent setzt das Ziel-Feld (context) beim Erstellen — GoalBanner zeigt das eingegebene Ziel sofort statt des Platzhalters.' },
    ],
  },
  {
    version: 'v1.14.195',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Bilder-Rotation bleibt nach Seiten-Neuladen erhalten — Cache-Buster verhindert dass der Browser das alte (ungedrehte) Bild aus dem Cache zeigt.' },
    ],
  },
  {
    version: 'v1.14.194',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Bild-Rotation in der Immobilien-Bearbeitung bleibt nach Seiten-Neuladen sichtbar — Cache-Buster anhand des updated_at-Zeitstempels verhindert, dass der Browser das alte ungedrehte Bild aus dem HTTP-Cache lädt.' },
    ],
  },
  {
    version: 'v1.14.193',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Confirm sendet job_id — Extraktionsprotokoll-Tab findet Jobs zuverlässig über die Job-ID statt nur über property_id.' },
    ],
  },
  {
    version: 'v1.14.192',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Teaser-Vorschau vereinheitlicht — Investor-Listing-Teaser nutzt jetzt das gleiche Design wie Property-Teaser, Quality Gate und Unified-Process-Vorschau. Eine einzige Komponente (PropertyTeaserContent) rendert Titel, Bilder, Key Facts, Details, Lage und Beschreibung systemweit.' },
    ],
  },
  {
    version: 'v1.14.191',
    date: '16. Mai 2026',
    changes: [
      { type: 'improvement', text: 'fix: Teaser-Vorschau — ein einziges Design überall (PropertyTeaserContent als Single Source)' },
    ],
  },
  {
    version: 'v1.14.190',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Standort-Tab: Duplikat-Eintrag "Nutzungszone" unter "Steuern & Recht" entfernt — die Zonen-Informationen erscheinen jetzt nur noch einmal unter den Standort-Eckdaten.' },
      { type: 'feature', text: 'Standort-Tab: Neue Felder aus API #499 — Bauzonen-Kategorie (z. B. "Arbeitszonen") mit Erklärung, ÖV-Güteklasse als Farb-Badge (A grün bis E rot) und Strassenlärm Tag in dB mit Farb-Badge.' },
      { type: 'improvement', text: 'Standort-Tab: Solar-Klasse wird ohne Markdown-Reste (##) dargestellt.' },
    ],
  },
  {
    version: 'v1.14.189',
    date: '16. Mai 2026',
    changes: [
      { type: 'improvement', text: 'fix: Standort-Tab — Duplikat Zonen entfernen + neue API-Felder anzeigen (OeV, Laerm, Steuer, Bauzone)' },
    ],
  },
  {
    version: 'v1.14.188',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'KI-Extraktionsprotokoll: Echtes userfreundliches Protokoll statt nur Job-Status/Pipeline. Zeigt Zusammenfassung (Datum, Dauer, Anzahl Dokumente/Bilder/Felder, Konfidenz als farbiges Badge), erkannte Standort-Daten (Adresse/PLZ/Ort/Gemeinde/Kanton/Land/Koordinaten als grüne oder graue Chips), Dokumente mit Typ + Confidence, erkannte Felder als Tags, Bilder als Thumbnail-Grid mit Kategorie-Badge, Datenabdeckung und Warnungen prominent. Technische Pipeline-Details sind eingeklappt verfügbar. Kein rohes JSON sichtbar.' },
    ],
  },
  {
    version: 'v1.14.187',
    date: '16. Mai 2026',
    changes: [
      { type: 'fix', text: 'Teaser-Vorschau: Preis wird wieder angezeigt — die Komponente las das veraltete Feld `minimum_price`, die API liefert jedoch nur noch das kanonische `price`-Feld (Issue #357). Betrifft Hero-Preis und Key-Facts-Kachel sowie Brutto-/Nettorendite-Berechnung.' },
      { type: 'improvement', text: 'Teaser-Vorschau: Schriftgrössen vereinheitlicht — statt sechs verschiedener Grössen jetzt drei konsistente Stufen (Titel, Sektionsüberschrift, Body). Wirkt professioneller und ruhiger.' },
    ],
  },
  {
    version: 'v1.14.186',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'CEO-Agent E-Mail-Einstellungen: Wenn der Skill "E-Mail entwerfen" aktiv ist, kann der User VOR der Generierung Ton (Visionär/Pragmatisch/Formell), Länge (Kurz/Mittel/Ausführlich), Zweck (Erstkontakt/Follow-Up/Angebot/Weiterleitung) und Sprache (DE/EN/FR/IT) wählen. Die Einstellungen werden dem Prompt vorangestellt und steuern den AI-Entwurf gezielt.' },
    ],
  },
  {
    version: 'v1.14.185',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Panel: Wissensbausteine werden jetzt nach Geltungsbereich gruppiert — lokale Bausteine (nur diese Pendenz) und globale Bausteine (an Immobilie/Kontakt/Organisation angehängt) erscheinen unter eigenen Überschriften. Lokale Chunks verschwinden mit der Pendenz, globale bleiben an der Entity für andere Pendenzen verfügbar.' },
    ],
  },
  {
    version: 'v1.14.184',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Panel: Dossier-Dokumente und Bilder der verknüpften Immobilie werden direkt im Übersichts-Panel angezeigt. Dokumente lassen sich per Klick als E-Mail-Anhang übernehmen — der Composer öffnet sich automatisch mit dem ausgewählten Anhang.' },
    ],
  },
  {
    version: 'v1.14.183',
    date: '15. Mai 2026',
    changes: [
      { type: 'fix', text: 'Standort-Tab: Verständliche Bezeichnungen statt Abkürzungen — Lärmempfindlichkeit, Naturgefahren, Erdbebenzone, Grundwasserschutz und CO₂-Emissionen erscheinen jetzt mit lesbarem Label (z. B. "Wohnzone" statt "ES II") und Tooltip mit Erklärung der Konsequenzen für die Immobilie.' },
      { type: 'improvement', text: 'Standort-Tab Tooltips übersetzt — Französisch und Italienisch ergänzt (vorher nur DE/EN).' },
    ],
  },
  {
    version: 'v1.14.182',
    date: '15. Mai 2026',
    changes: [
      { type: 'improvement', text: 'fix: Standort-Tab Werte muessen verstaendlich sein — Farben + Erklaerungen statt Abkuerzungen' },
    ],
  },
  {
    version: 'v1.14.181',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Übersichtspanel: Neue Immobilien-Mini-Card zeigt Adresse, Typ, Wohn-/Gewerbefläche, Etagen, Baujahr, Preis und Primärbild der verknüpften Liegenschaft — der CEO-Agent zeigt damit auf einen Blick, was er über die Immobilie weiss.' },
      { type: 'feature', text: 'Pendenzen Compact Header: Entity-Chips (Kontakt, Firma, Immobilie) sind jetzt bearbeitbar — X-Button entfernt die Verknüpfung, Klick auf Chip scrollt zur Detailkarte. Wenn kein Entity verknüpft ist, erscheint ein "+ Kontakt / Firma / Immobilie"-Button mit Autocomplete-Suche zum direkten Verknüpfen.' },
    ],
  },
  {
    version: 'v1.14.180',
    date: '15. Mai 2026',
    changes: [
      { type: 'fix', text: 'Standort-Tab überarbeitet: Zonenvorschriften werden jetzt formatiert dargestellt (kein rohes JSON mehr, Dokumente als Liste mit Links). Duplikat "Bauzone" entfernt — Nutzungszone erscheint nur noch einmal (kommunal + kantonal kombiniert im Abschnitt Steuern & Recht).' },
      { type: 'improvement', text: 'Standort-Tab Umwelt & Restriktionen: Farbige Ampel-Badges (grün/gelb/rot) für Naturgefahren, Hochwasser-Risiko, Erdbebenzone und Lärmempfindlichkeit — visuell sofort erkennbar statt einheitlich grau.' },
    ],
  },
  {
    version: 'v1.14.179',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Chat: Vorlesen von Agent-Antworten via Browser Sprachsynthese. Neben jeder Antwort erscheint ein Lautsprecher-Icon (Vorlesen starten/stoppen). Sprache wird automatisch an die UI-Sprache angepasst (de-CH/en-US/fr-FR/it-IT). Markdown wird vor dem Vorlesen entfernt für sauberen Sprachfluss. Ergänzt die bestehende Mikrofon-Aufnahme (STT) zu einer freihändigen Bedienung — unterwegs oder am Desktop.' },
      { type: 'improvement', text: 'Mikrofon-Button im Pendenzen-Chat: Tooltips, ARIA-Labels und Fehlermeldungen sind jetzt vollständig übersetzt (de/en/fr/it) — vorher waren Aufnahme-Hinweise auf Deutsch fixiert.' },
    ],
  },
  {
    version: 'v1.14.178',
    date: '15. Mai 2026',
    changes: [
      { type: 'fix', text: 'KI-Upload (Immobilien-Erfassung): Fehlende Dateitypen ergänzt — E-Mail (.eml/.msg), PowerPoint (.pptx/.ppt), CSV/TSV, OpenDocument (.odt/.ods) sowie zusätzliche Bildformate (BMP, TIFF, HEIC/HEIF) können jetzt hochgeladen werden. Zudem ist die maximale Dateigrösse von 50 MB auf 200 MB angehoben, sodass grosse Immobilien-Dossiers (Pläne, Fotos) nicht mehr abgewiesen werden.' },
    ],
  },
  {
    version: 'v1.14.177',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Pendenzen-Chat: Skill-Chips unter dem Eingabefeld erlauben die direkte Auswahl der Agent-Funktion (Firma recherchieren, Person recherchieren, Text generieren, Kontext recherchieren, E-Mail entwerfen, Notiz erstellen, Sub-Pendenz, Zusammenfassen). Ein aktiver Chip fokussiert den Agent — ohne Skill bleibt der Chat im freien Modus wie bisher. Mobile: horizontal scrollbar, Tastatur-Navigation und ARIA-Rollen unterstuetzt.' },
    ],
  },
  {
    version: 'v1.14.176',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Immobilien-Bearbeitung: Sticky Header ist jetzt zweizeilig. Zeile 1 enthält Navigation, Status-Badge und alle Action-Buttons (Auftraggeber, Teaser, Einreichen, Löschen). Zeile 2 zeigt den Titel der Immobilie (bold) und Adresse als Untertitel (Strasse, PLZ, Stadt) — immer sichtbar, auch bei langen Titeln, sodass beim Scrollen klar ist, welche Immobilie gerade bearbeitet wird.' },
    ],
  },
  {
    version: 'v1.14.175',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Standort-Tab erweitert: Neue Sektion "Energie & Umwelt" mit CO₂-Emissionen (Ampel-Badge gruen/gelb/rot + Heizungstyp), Minergie-Zertifikat (farbiger Badge nach Standard) und Solarpotenzial Dach. Neue Sektion "Steuern & Recht" mit Steuerfuss Gemeinde + Kanton (Einkommen/Vermoegen %) und Nutzungszone (kommunal + kantonal). Jede Sektion verlinkt zur offiziellen Quelle (geo.admin.ch, minergie.ch, ESTV, geodienste.ch).' },
    ],
  },
  {
    version: 'v1.14.174',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Dev Command Center: Deployed Tasks werden jetzt zeitbasiert gefiltert. Die aktive Queue zeigt nur noch frisch deployte Tasks (< 4h). Ältere deployte Tasks (4h-48h) wandern in den Erledigt-Tab unter "Kürzlich erledigt". Nach 48h werden sie automatisch archiviert. So bleibt die aktive Queue übersichtlich und kein Task geht verloren.' },
    ],
  },
  {
    version: 'v1.14.173',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Dev Command Center: Task-Detailansicht zeigt Abhaengigkeiten (depends_on) jetzt prominent als farbige Info-Box. Amber wenn der Parent-Task noch laeuft, gruen wenn die Abhaengigkeit erfuellt ist. Bei verketteten Abhaengigkeiten wird die komplette Kette dargestellt — jeder Eintrag ist klickbar und navigiert direkt zum jeweiligen Task.' },
    ],
  },
  {
    version: 'v1.14.172',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Immobilien-Bearbeitung: Der KI-Erfassungs-Tab wird nach der Erstellung ausgeblendet — Objektdaten ist neu der erste Tab. Am Ende der Tab-Leiste erscheint stattdessen ein neuer Tab "KI-Extraktionsprotokoll" (nur intern) mit Pipeline-Status, Dokument- und Bild-Klassifizierung, Datenabdeckung und Konfidenz-Scores pro KI-Job.' },
    ],
  },
  {
    version: 'v1.14.171',
    date: '15. Mai 2026',
    changes: [
      { type: 'fix', text: 'PWA-Update beschleunigt: Service Worker precached nicht mehr alle 265 Build-Dateien (~24 MB) bei jedem Deploy. JS/CSS, Fonts und Bilder werden neu zur Laufzeit gecached. Statt sofortigem Hard-Reload erscheint ein Toast "Neue Version verfügbar" mit Aktualisieren-Button.' },
    ],
  },
  {
    version: 'v1.14.170',
    date: '15. Mai 2026',
    changes: [
      { type: 'improvement', text: 'fix: PWA Precache — 265 Dateien / 24 MB bei jedem Update verursacht 5min Ladezeit' },
    ],
  },
  {
    version: 'v1.14.169',
    date: '15. Mai 2026',
    changes: [
      { type: 'fix', text: 'Datenschutz: Property-Titel im Bearbeitungs-Header anonymisiert — Strassenname + Hausnummer werden entfernt (z.B. "Mehrfamilienhaus Kasernenstrasse 42, 8004 Zürich" wird zu "Mehrfamilienhaus, 8004 Zürich"). Verhindert dass die genaue Adresse vor NDA-Unterzeichnung an Investoren leakt.' },
    ],
  },
  {
    version: 'v1.14.168',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Dev C&C Task-Detailansicht: Löschen-Button hinzugefügt. Tasks können jetzt direkt aus der Detailansicht entfernt werden (sichtbar bei allen Status ausser in_progress und deployed) mit Bestätigungs-Dialog.' },
    ],
  },
  {
    version: 'v1.14.167',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Dev C&C Dashboard: Delete-Button (Trash-Icon) zum endgültigen Löschen fehlerhaft erstellter Tasks (Duplikate, Müll-Tasks). Sichtbar im Hover für Status done, failed, pending_approval, pending und waiting — mit Browser-Confirm-Dialog vor dem Löschen.' },
    ],
  },
  {
    version: 'v1.14.166',
    date: '15. Mai 2026',
    changes: [
      { type: 'feature', text: 'Neuer Standort-Tab in der Immobilien-Erfassung — zeigt Karte, GWR-Daten (EGID/EGRID/Baujahr) und Umwelt-Restriktionen (Lärm, Grundwasser, Naturgefahren, Hochwasser, Erdbeben, Altlasten, Denkmalschutz) mit klickbaren Links zu den offiziellen Quellen (Google Maps, map.geo.admin.ch, housing-stat.ch). Felder Einwohnerzahl, Koordinaten, EGID und EGRID wurden vom Objekt-Tab in den neuen Standort-Tab verschoben (read-only).' },
    ],
  },
  {
    version: 'v1.14.165',
    date: '15. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Seller-Sicht vereinheitlicht: Die separate Verkäufer-Detailseite wurde entfernt — Verkäufer und Vermarkter nutzen nun denselben Unified-Process. Tab "Auftrag & Vermarktung" zeigt Investoren-Engagement-Statistiken (NDA/NBO/BO) und die Deal-Timeline, sobald die Immobilie aktiv ist. Der aktuelle Status wird als Badge im Sticky-Header angezeigt.' },
    ],
  },
  {
    version: 'v1.14.164',
    date: '13. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Dev C&C: Navigation-Labels klarer benannt — "MR / Pipeline" → "Pipeline läuft", "Deploy / Merged" → "Staging Deploy".' },
    ],
  },
  {
    version: 'v1.14.163',
    date: '13. Mai 2026',
    changes: [
      { type: 'feature', text: 'Gewerbe-Subtypen erweitert: Neue Subtypen Werkstatt, Produktionsfläche, Showroom, Gemischte Gewerbefläche, Rechenzentrum, KFZ-Werkstatt, Tankstelle, Arztpraxis und Fitnesscenter stehen bei der Objekterfassung (Gewerbe/Industrie/Wohn-Gewerbe) zur Auswahl — übersetzt in DE/EN/FR/IT.' },
    ],
  },
  {
    version: 'v1.14.162',
    date: '13. Mai 2026',
    changes: [
      { type: 'fix', text: 'KI-Objekterfassung: Doppelter Datei-Dialog beim Klick auf "Verzeichnis wählen" behoben — der programmatische Klick auf den versteckten Input wurde bisher zum Drop-Zone-Container hochgereicht und öffnete zusätzlich den Datei-Dialog.' },
      { type: 'fix', text: 'KI-Objekterfassung: Roter "KI-Extraktion starten"-Button verschwand beim Hover, weil eine nicht definierte CSS-Variable (--v2-brand-dark) verwendet wurde. Jetzt wird korrekt --v2-brand-hover benutzt.' },
    ],
  },
  {
    version: 'v1.14.161',
    date: '13. Mai 2026',
    changes: [
      { type: 'fix', text: 'KI-Erfassung (PropertyAITab): Button "Verzeichnis wählen" hinzugefügt. Ein zweiter File-Input mit webkitdirectory-Attribut ermöglicht jetzt den Ordner-Upload neben dem klassischen "Dateien wählen" — unterstützte Dateien werden beim Ordner-Pick automatisch herausgefiltert.' },
    ],
  },
  {
    version: 'v1.14.160',
    date: '13. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Ordner-Upload Bug Fix in PropertyAITab' },
    ],
  },
  {
    version: 'v1.14.159',
    date: '13. Mai 2026',
    changes: [
      { type: 'feature', text: 'Bilder-Galerie (Immobilie bearbeiten → Medien): Bilder werden automatisch nach Medium gruppiert (Visualisierungen → Fotos → Pläne → Karten). Neue Filter-Buttons (Alle/Visualisierungen/Fotos/Pläne/Karten) und ein Badge auf jedem Bild zeigt Medium und Bildinhalt (z.B. „Foto · Fassade", „3D · Wohnraum"). Manuelles Sortieren via Drag & Drop bleibt innerhalb jeder Gruppe erhalten.' },
    ],
  },
  {
    version: 'v1.14.157',
    date: '12. Mai 2026',
    changes: [
      { type: 'fix', text: 'Quality Gate Pruef-Checkliste: "Mindestpreis festlegen" durch "Preis hinterlegt" ersetzt. Pruefung akzeptiert nun jedes hinterlegte Preisfeld (price/minimum_price/desired_price > 0), da der Mindestpreis optional ist.' },
    ],
  },
  {
    version: 'v1.14.155',
    date: '12. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Test-Tooling Major Update: Vitest 2.1.1 auf 4.x angehoben (zusammen mit @vitest/coverage-v8 und @vitest/ui auf 4.1.6). Voraussetzung Vite >= 6 erfuellt (aktuell Vite 8). Alle 154 Test-Dateien mit 2865 Tests gruen, Build gruen.' },
    ],
  },
  {
    version: 'v1.14.154',
    date: '12. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Build-Tooling aktualisiert: Vite 7 auf 8.x angehoben (Rolldown ersetzt Rollup/esbuild). build.rollupOptions auf rolldownOptions migriert, manualChunks-Objekt auf advancedChunks.groups umgestellt. Parallel Vitest auf 4.x, @vitejs/plugin-react auf 6.x und vite-plugin-pwa auf 1.3 gehoben. Build und 2846 Tests gruen.' },
    ],
  },
  {
    version: 'v1.14.153',
    date: '11. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Build-Tooling aktualisiert: Vite 6.4 auf 7.x angehoben. Keine Code-Anpassungen nötig (kein Sass, kein splitVendorChunkPlugin). Build und Tests laufen unverändert.' },
    ],
  },
  {
    version: 'v1.14.152',
    date: '11. Mai 2026',
    changes: [
      { type: 'feature', text: 'Dev Cockpit: Neuer Button "Sensitive Dateien freigeben" in der TaskDetailView. Wenn ein Task fehlschlägt, weil der Agent geschützte Dateien (z.B. .gitlab-ci.yml) angefasst hat, kann die Änderung jetzt direkt im UI freigegeben werden — der Task wird automatisch neu eingereiht (#569).' },
    ],
  },
  {
    version: 'v1.14.149',
    date: '11. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Build-Tooling aktualisiert: Vite 5.4 auf 6.4 sowie Vitest 2.1 auf 3.2 angehoben (inkl. @vitejs/plugin-react und Coverage-/UI-Pakete). Build, Tests und Dev-Server laufen unverändert.' },
    ],
  },
  {
    version: 'v1.14.148',
    date: '11. Mai 2026',
    changes: [
      { type: 'feature', text: 'Quality Gate: Preis-Bezeichnung wechselt dynamisch zwischen "Richtpreis" (Bieterverfahren) und "Verkaufspreis" (Festpreis). Festpreis-Inserate haben jetzt zudem ein frei wählbares Enddatum mit Schnellauswahl-Buttons für 30/60/90/120 Tage statt fixer 90-Tage-Laufzeit (#544).' },
      { type: 'improvement', text: 'Property-Edit: Preisfeld umbenannt von "Mindestpreis" zu "Preisvorstellung", da der finale Charakter (Richt- bzw. Verkaufspreis) erst im Quality Gate entschieden wird.' },
    ],
  },
  {
    version: 'v1.14.147',
    date: '11. Mai 2026',
    changes: [
      { type: 'fix', text: 'Verkäufer-Flow: "Zur Prüfung einreichen" Button ist jetzt auf der Bearbeitungsmaske (Tab "Auftrag & Vermarktung") sichtbar. Der Submit-Button auf der Detailseite reicht die Immobilie direkt ein, ohne den vorherigen Umweg über die Investor-Ansicht (zweiter Klick entfällt). Race Condition im Auto-Save behoben, damit Submit während einer laufenden Speicherung nicht mit veraltetem Stand ausgeführt wird (#545).' },
    ],
  },
  {
    version: 'v1.14.146',
    date: '11. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Zefix-Handelsregister-Sektion im Quality Gate entfernt (nicht benoetigt).' },
    ],
  },
  {
    version: 'v1.14.145',
    date: '11. Mai 2026',
    changes: [
      { type: 'fix', text: 'Auftraggeber-Suche im Property-Edit Tab "Auftrag & Vermarktung" verwendet jetzt dieselbe Vertragssuche wie das Quality Gate (nur Firmen/Kontakte mit aktivem Plattformvertrag). Die "Aktuelle Verknüpfung" im Such-Dialog zeigt zudem den echten Firmen- bzw. Kontaktnamen statt eines generischen Platzhalters (#543).' },
    ],
  },
  {
    version: 'v1.14.144',
    date: '10. Mai 2026',
    changes: [
      { type: 'feature', text: 'Deploy-Button im Dev C&C: Production-Deploy direkt aus dem Deploys-Tab ausloesen mit Live-Log und Status-Anzeige.' },
    ],
  },
  {
    version: 'v1.14.143',
    date: '10. Mai 2026',
    changes: [
      { type: 'fix', text: 'Auftraggeber-Drawer im Property-Header zeigt nicht mehr den internen Verantwortlichen als Auftraggeber-Kontakt. Wenn kein externer Auftraggeber-Kontakt verknüpft ist, erscheint klar "Keine Kontaktdaten hinterlegt"; der Verantwortliche Berater bleibt separat in der Sales-Konfiguration (#541).' },
    ],
  },
  {
    version: 'v1.14.142',
    date: '10. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Tote Detailseiten (PropertyDetailPage, UnifiedPropertyDetailPage) entfernt. Geteilte Property-Links (z.B. aus Benachrichtigungen) leiten jetzt rollenbasiert auf den Unified Property Process bzw. die Investor-Detailseite weiter (#539).' },
    ],
  },
  {
    version: 'v1.14.141',
    date: '10. Mai 2026',
    changes: [
      { type: 'fix', text: 'Vertrags-/Auftraggeber-Suche jetzt auch im Quality Gate und im Unified Property Process verfügbar (vorher nur auf der Immobilien-Detailseite). Admin/Sales können den verknüpften Verkäufer-Vertrag direkt während Bearbeitung und Quality-Gate-Prüfung suchen oder ändern (#537).' },
    ],
  },
  {
    version: 'v1.14.140',
    date: '10. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Auftraggeber-Suche auf der Immobilien-Detailseite: Jeder Treffer zeigt nun explizit ein "Vertrag aktiv"-Badge an, damit klar ist dass die Liste API-seitig auf Firmen und Kontakte mit unterzeichnetem Plattformvertrag (SELLER_FRAMEWORK) gefiltert ist. UX-Verbesserung zu #535 — der Filter selbst wurde bereits in v1.14.139 nach API #409 (seller_crm_company_id) korrigiert.' },
    ],
  },
  {
    version: 'v1.14.139',
    date: '10. Mai 2026',
    changes: [
      { type: 'fix', text: 'Auftraggeber-Suche auf der Immobilien-Detailseite: Es werden nur noch Firmen und Kontakte mit aktivem Plattformvertrag (unterzeichneter SELLER_FRAMEWORK) angezeigt. Aufruf wieder auf den existierenden Backend-Endpoint /principal/search angepasst (Filter erfolgt API-seitig).' },
    ],
  },
  {
    version: 'v1.14.136',
    date: '10. Mai 2026',
    changes: [
      { type: 'feature', text: 'Auftraggeber-Feld auf der Immobilien-Detailseite: Neuer Such-Dialog erlaubt das Verknüpfen einer Firma oder eines Kontakts aus dem CRM via Live-Suche. Ergebnisse zeigen Typ-Badge (Firma/Kontakt) und Details. Aktive Verknüpfungen lassen sich direkt entfernen.' },
    ],
  },
  {
    version: 'v1.14.135',
    date: '10. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Vertragsgenerierung: Legacy v1-Endpunkt /api/sales/contracts/generate entfernt. Vertragserstellung erfolgt nun ausschliesslich über den V3-Wizard (ContractWizardV3Unified) mit dem unified Platform-Agreement-Endpunkt.' },
    ],
  },
  {
    version: 'v1.14.134',
    date: '10. Mai 2026',
    changes: [
      { type: 'fix', text: 'm²-Zeichen wird in Flächen-Spalten und -Filtern korrekt angezeigt (vorher fälschlich als "m\\u00B2" sichtbar). Betrifft Mieterspiegel, Wohnungsmix und Listing-Spaltenüberschriften in allen 4 Sprachen.' },
    ],
  },
  {
    version: 'v1.14.133',
    date: '10. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Tenant/Company Cleanup: Vertrags-Antwortfelder im Frontend nutzen die kanonische Namensgebung gemäss Backend STORY-385 (our_company_id → tenant_id, buyer/partner_company_id → buyer/partner_crm_company_id, *_contact_id → *_crm_contact_id). Das nicht mehr verwendete Feld principal_company_id wurde entfernt. Vertrags-Detailansicht zeigt weiterhin alle Parteien wie gewohnt.' },
    ],
  },
  {
    version: 'v1.14.132',
    date: '10. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Tenant/Company Cleanup: Pendenzen-Typen im Frontend nutzen das kanonische Feld crm_company_id (CRM-Verknüpfung) gemäss Backend STORY-384. Konsistent mit der neuen Namensgebung. Keine sichtbare Änderung für Nutzer; bereinigt die interne API-Vertragsoberfläche.' },
    ],
  },
  {
    version: 'v1.14.131',
    date: '10. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Tenant/Company Cleanup: Kampagnen-Typen und -Aufrufe im Frontend nutzen das kanonische Feld tenant_id (Mandant) gemäss Backend STORY-383. Der List-Filter verwendet jetzt tenant_ids. Keine sichtbare Änderung für Nutzer.' },
    ],
  },
  {
    version: 'v1.14.130',
    date: '10. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Tenant/Company Cleanup: Listing-Typen im Frontend nutzen das kanonische Feld tenant_id (Mandant) gemäss Backend STORY-382. Der bestehende Filter-Parameter company_ids bleibt rückwärtskompatibel erhalten. Keine sichtbare Änderung für Nutzer.' },
    ],
  },
  {
    version: 'v1.14.129',
    date: '9. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Tenant/Company Cleanup: Property-Datenfelder im Frontend auf neue CRM-Namensgebung umgestellt (company_id → crm_company_id, principal_contact_id → crm_contact_id). Konsistent mit Backend STORY-381. Keine sichtbare Änderung für Nutzer; bereinigt die interne API-Vertragsoberfläche.' },
    ],
  },
  {
    version: 'v1.14.128',
    date: '8. Mai 2026',
    changes: [
      { type: 'feature', text: 'Quality Gate: Neue Verkäufer-Vertrag-Sektion im rechten Panel zeigt direkt aus den Review-Daten den Vertragsstatus, Vertragsnummer, Ablaufdatum, alle Unterzeichner mit Unterschriftstyp sowie einen Direktlink zum Vertrag. Bei fehlendem Plattformvertrag erscheint eine deutliche rote Warnung.' },
    ],
  },
  {
    version: 'v1.14.127',
    date: '7. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Quality Gate: Der Zefix-Bereich zeigt jetzt nur noch Verkäufer mit aktivem Plattformvertrag — inklusive Vertragsstatus-Badge ("Vertrag aktiv seit DD.MM.YYYY"), Vertragsnummer, Ablaufdatum, den Unterzeichnern des Vertrags und einem Direktlink zur Vertragsdetail-Seite. Die Zeichnungsberechtigten aus dem Handelsregister werden weiterhin automatisch geladen.' },
    ],
  },
  {
    version: 'v1.14.126',
    date: '7. Mai 2026',
    changes: [
      { type: 'feature', text: 'Quality Gate: Im Genehmigungs-Panel können jetzt eine oder mehrere Investorengruppen ausgewählt werden, die bei Freigabe automatisch Zugriff auf den Teaser erhalten. Pro Gruppe werden Name und Mitgliederanzahl angezeigt; eine Zusammenfassung listet die ausgewählten Gruppen mit Gesamtzahl der Investoren.' },
    ],
  },
  {
    version: 'v1.14.125',
    date: '7. Mai 2026',
    changes: [
      { type: 'feature', text: 'CRM: Verkäufer-Vertrag visuell sichtbar — neuer "Verträge"-Tab im Kontakt-Modal listet alle Verträge in denen der Kontakt Signatar ist; neuer "Immobilien"-Tab in CRM-Organisationen zeigt alle Immobilien einer Firma mit Vertragsstatus-Badge (aktiv/abgelaufen/fehlend); im Auftraggeber-Drawer auf der Property-Detailseite gibt es einen neuen "Vertrag öffnen"-Link.' },
    ],
  },
  {
    version: 'v1.14.124',
    date: '7. Mai 2026',
    changes: [
      { type: 'fix', text: 'Teaser-Vorschau Button (Auge-Symbol) im Unified Property Process wieder verfügbar — öffnet die Investor-Vorschau als Overlay und zeigt die aktuell bearbeiteten (noch nicht gespeicherten) Highlights und Übersetzungen an.' },
    ],
  },
  {
    version: 'v1.14.123',
    date: '7. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Property Wizard Finanzkennzahlen: Mieteinnahmen, Marktwert sowie Nettomiete und Nebenkosten im Mieterspiegel werden jetzt mit CHF-Tausender-Apostroph formatiert (z.B. CHF 200\'004 statt 200004.00).' },
    ],
  },
  {
    version: 'v1.14.122',
    date: '7. Mai 2026',
    changes: [
      { type: 'feature', text: 'Quality Gate: Vollständige Verkaufskonfiguration bei Genehmigung — Richtpreis und Mindestpreis (CHF-Apostroph), 4 Phasen-Inputs (NBO, NBO-Review, BO, BO-Review), farbige Timeline-Visualisierung mit berechneten Daten und Gesamtdauer, Checkbox "Tiefstes NBO ausschliessen", sowie Festpreis-Modus ohne Phasen.' },
    ],
  },
  {
    version: 'v1.14.121',
    date: '7. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Property Wizard: Statt zwei Feldern (Mindestpreis und Wunschpreis) gibt es jetzt ein einziges Feld "Preis" mit CHF-Apostroph-Formatierung. Bestehende Immobilien werden weiterhin korrekt angezeigt.' },
    ],
  },
  {
    version: 'v1.14.120',
    date: '7. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Einheitliche CHF-Formatierung in der gesamten App: alle Preisanzeigen (Immobilien-Listen, Detail-Seiten, Angebote, Rechnungen, Karten, Dashboard) nutzen jetzt die zentrale formatCHF()-Utility mit Schweizer Apostroph-Tausendertrennzeichen.' },
    ],
  },
  {
    version: 'v1.14.119',
    date: '7. Mai 2026',
    changes: [
      { type: 'feature', text: 'KI-Upload Status: Pro Dokument werden jetzt PDF-Qualitäts-Badges (Text-PDF / Scan-PDF / Verschlüsselt), Seitenzahl und extrahierte Zeichen angezeigt — inkl. Warnhinweis bei Scans und verschlüsselten PDFs.' },
    ],
  },
  {
    version: 'v1.14.118',
    date: '7. Mai 2026',
    changes: [
      { type: 'fix', text: 'Quality Gate: Link "Immobilie ansehen" öffnet die Detail-Seite jetzt im gleichen Tab via SPA-Navigation statt in einem neuen Tab mit vollem Reload.' },
    ],
  },
  {
    version: 'v1.14.117',
    date: '7. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Neue formatCHF()-Utility und CurrencyInput-Komponente: einheitliche Darstellung von CHF-Beträgen (Schweizer Apostroph-Tausendertrennzeichen) in der gesamten App, mit accessibler Eingabe-Komponente für Preisfelder.' },
    ],
  },
  {
    version: 'v1.14.116',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Vertragswizard: Rollen-Kacheln (Käufer / Verkäufer / Vermittler) werden jetzt vertikal untereinander dargestellt — bessere Lesbarkeit der Beschreibungen und konsistentes Layout auf Mobile und Desktop.' },
    ],
  },
  {
    version: 'v1.14.115',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'CompanySearch: CRM-First-Reihenfolge — eigene CRM-Organisationen werden jetzt zuoberst angezeigt, danach Privatpersonen (CRM-Kontakte ohne Firma) als neue Kategorie mit eigenem Badge, und Zefix erscheint nur noch als Fallback, wenn weniger als 5 CRM-Treffer vorhanden sind.' },
    ],
  },
  {
    version: 'v1.14.114',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'V3 Wizard: AI Email Research bei Partner-Email-Eingabe — automatische E-Mail-Vorschläge (mit MX-Validierung) auch bei Zefix- und CRM-Signatory-Feldern, inkl. Übergabe der lokalen Firmen-ID für präzisere Domain-Erkennung.' },
    ],
  },
  {
    version: 'v1.14.113',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Property-Detail UI: Deal-State-Feld aus dem Objekt-Tab entfernt, alle Sektionen sind per Default aufgeklappt, und die Sektion "Rechtliches & Baurecht" wurde aufgelöst — E-GRID, Baurechts-Schalter und Ablaufdatum erscheinen jetzt am Ende der Sektion "Grundstück".' },
    ],
  },
  {
    version: 'v1.14.112',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Quality Gate v2: Sidebar entfernt, Pill-Leiste oben (mit automatischem Dropdown-Fallback ab >5 Reviews), Hauptbereich auf 2/3 Vorschau + 1/3 Checkliste umgestellt. Title/Price-Overlap im PropertyTeaserContent durch späteren Flex-Row-Breakpoint und flex-wrap behoben.' },
    ],
  },
  {
    version: 'v1.14.111',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'V3 Vertrags-Wizard: CRM-Kontakte als Partner-Unterzeichner integriert. Zefix- und CRM-Zeichnungsberechtigte werden jetzt parallel geladen und in zwei separaten Sektionen mit Quellen-Badge angezeigt; Auswahl, E-Mail-Override und Validierung der Kollektivunterschrift funktionieren über beide Quellen hinweg.' },
    ],
  },
  {
    version: 'v1.14.110',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'Unified Teaser: Neues Prop viewMode (investor/seller/admin) im PropertyTeaserContent. Seller-Modus blendet den Sales-Kontakt aus (rohe Vorschau ohne interne Daten); Admin-Modus zeigt die volle Ansicht (Quality Gate Preview); Investor bleibt Standard.' },
    ],
  },
  {
    version: 'v1.14.109',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Soft-Delete für Immobilien: Gelöschte Immobilien werden nur noch markiert (deleted_at/deleted_by_id) statt physisch entfernt. Standardansichten zeigen sie nicht mehr.' },
    ],
  },
  {
    version: 'v1.14.108',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Alte Vertragstypen (v1/v2) komplett aus dem Frontend entfernt: AGB, BUYER_AGREEMENT, SELLER_MANDATE, PARTNER_AGREEMENT, NDA_TRIPARTITE, LEGITIMATION_*, ESG_AML_MODULE sowie SELLER_FRAMEWORK/BUYER_PLATFORM. Der Vertragsgenerator zeigt nur noch die drei aktiven Typen: PLATFORM_AGREEMENT, BROKER_AGREEMENT und SEARCH_MANDATE. Anhang-B/Appendix-Komponenten und V2-Service-Endpoints entfernt, Übersetzungen in DE/EN/FR/IT bereinigt.' },
    ],
  },
  {
    version: 'v1.14.107',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Vertragstypen v1/v2 aus Frontend entfernen (nur 3 aktive behalten)' },
    ],
  },
  {
    version: 'v1.14.106',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'Quality Gate Redesign: Split-View mit Investor-Vorschau (PropertyTeaserContent) links und automatischer Pruef-Checkliste plus Listing-Konfiguration rechts. Alle Pruefpunkte (Pflichtfelder, Bilder ≥3, Beschreibung, Preis, Dokumente, Highlights, Lage) werden automatisch aus den Immobilien-Daten ermittelt — keine manuellen Checkboxen mehr. Vertrag-signiert ist optional und nicht blockierend.' },
    ],
  },
  {
    version: 'v1.14.105',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Quality Gate Redesign: Split-View mit Teaser-Preview' },
    ],
  },
  {
    version: 'v1.14.104',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'Sticky Header in der Immobilien-Erfassung: "Zur Prüfung einreichen" (grün, nur DRAFT/abgelehnt) und "Löschen" (rot, mit Bestätigungs-Dialog) jetzt direkt oben im Header erreichbar. Der Einreichen-Button bleibt zusätzlich unten im Tab "Auftrag & Vermarktung".' },
    ],
  },
  {
    version: 'v1.14.103',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Sticky Header: Einreichen + Löschen Buttons oben' },
    ],
  },
  {
    version: 'v1.14.102',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'Bilder-Rotation im Medien-Tab: Pro Bild ein Rotate-Button (90° im Uhrzeigersinn) im Hover-Overlay. Optimistische CSS-Drehung mit Cache-Busting nach Server-Bestätigung; Revert bei Fehler.' },
    ],
  },
  {
    version: 'v1.14.101',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Bilder-Rotation Button im Medien-Tab' },
    ],
  },
  {
    version: 'v1.14.100',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Vermarktungsauftrag-Typ (Alleinauftrag/Mehrfachauftrag/Reine Schätzung) komplett aus der Immobilien-Erfassung entfernt: Radio-Buttons im Tab "Auftrag & Vermarktung" entfernt, zugehörige Typen (MandateType, MANDATE_TYPES) und Felder (mandate_type) aus PropertyDetail/Create/Update entfernt, Übersetzungen in DE/EN/FR/IT bereinigt.' },
    ],
  },
  {
    version: 'v1.14.99',
    date: '6. Mai 2026',
    changes: [
      { type: 'fix', text: 'Sticky Header Fix: overflow-x von hidden auf clip geändert (html + body). hidden erzeugte implizit overflow-y: auto, was position: sticky brach.' },
    ],
  },
  {
    version: 'v1.14.98',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Top-Bar Sticky: Verifikation der Sticky-Position im aktiven DashboardLayout (sticky top-0 z-30, kein overflow-x-hidden auf den Wrapper-Divs). DashboardLayoutV2 verbleibt im Archiv (_archive). Topbar bleibt beim Scrollen verlässlich oben kleben.' },
    ],
  },
  {
    version: 'v1.14.97',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Top-Bar im aktiven Layout systemweit sticky: Firmenselektor, Benachrichtigungen, Hilfe, Sprache und Benutzermenü bleiben beim Scrollen immer sichtbar — mit dezentem Schatten beim Scrollen. DashboardLayoutV2 ins Archiv (_archive) verschoben.' },
    ],
  },
  {
    version: 'v1.14.96',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Top-Bar systemweit sticky: Firmenselektor, Benachrichtigungen und Sprachumschalter bleiben beim Scrollen immer sichtbar — mit dezentem Schatten beim Scrollen.' },
    ],
  },
  {
    version: 'v1.14.95',
    date: '6. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Top-Bar systemweit sticky machen SIZE: S Projekt: immotron-web ISSUE: NEU: Top' },
    ],
  },
  {
    version: 'v1.14.94',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'Unified Property Process Tab 1 ist jetzt die KI-Erfassung: Beim Erstellen einer neuen Immobilie können Verkaufsdokumentation und Bilder direkt hochgeladen werden — die KI extrahiert automatisch Felder, anschliessend werden die Daten übernommen und der Wizard springt auf "Objekt". Im Bearbeitungs-Modus zeigt der Tab schreibgeschützt die KI-Erfassungs-Historie der Immobilie. Der frühere "Grunddaten"-Tab entfällt, weil seine Inhalte bereits im "Objekt"-Tab konsolidiert sind.' },
    ],
  },
  {
    version: 'v1.14.93',
    date: '6. Mai 2026',
    changes: [
      { type: 'feature', text: 'Unified Property Process Tab 2 "Objektdaten": Klassifizierung, Adresse mit Google-Places-Autocomplete und Geocoding, Grundstück, Gebäude, Energie & Zustand sowie Rechtliches/Baurecht in sechs zusammenklappbaren Sektionen — die alten Tabs Basisdaten, Objektmerkmale und Rechtliches sind im einheitlichen Bearbeitungs-Workflow konsolidiert.' },
    ],
  },
  {
    version: 'v1.14.92',
    date: '5. Mai 2026',
    changes: [
      { type: 'feature', text: 'Unified Property Process: Routing umgestellt — Erfassung und Bearbeitung von Immobilien laufen nun durchgängig über `/sales/properties/new` und `/sales/properties/:id` (sowie `/seller/properties/...` für Verkäufer). Die alten Seiten PropertyEditPage, UnifiedPropertyWizard und AIPropertyUploadPage wurden entfernt; alte Links (z.B. /wizard, /ai-upload, /:id/edit) leiten automatisch auf die neuen Routen weiter.' },
    ],
  },
  {
    version: 'v1.14.91',
    date: '5. Mai 2026',
    changes: [
      { type: 'feature', text: 'Unified Property Process Tab 5 "Auftrag & Vermarktung": Auftraggeber-Picker mit Mandatstyp (Allein-/Mehrfachauftrag/Schätzung), Sales-Konfiguration mit verantwortlichem Berater und Mandatslaufzeit, Listing-Einstellungen (privates Listing, Ablaufdatum) sowie Quality-Gate-Einreichung mit Status-Badge und Ablehnungsgrund — Verkäufer sehen die Auftraggeber-Daten neu read-only und können ihre Immobilie direkt zur Prüfung einreichen.' },
    ],
  },
  {
    version: 'v1.14.90',
    date: '5. Mai 2026',
    changes: [
      { type: 'feature', text: 'Unified Property Process Tab 4 "Medien & Texte": Bilder-Upload mit Drag & Drop und Hauptbild-Markierung, Dossier-Dokumente, Immotron-Dokumente (intern), Highlights-Verwaltung, VR-Tour-Link und mehrsprachige Beschreibungen mit Auto-Übersetzung sind jetzt im einheitlichen Bearbeitungs-Workflow integriert.' },
    ],
  },
  {
    version: 'v1.14.89',
    date: '5. Mai 2026',
    changes: [
      { type: 'feature', text: 'Unified Property Process: Tab 3 "Preise & Konditionen" mit Sektionen Preise, Finanzkennzahlen (intern), Wohnungsmix und Mietspiegel — Verkäufer können den Mietspiegel ihrer eigenen Immobilie nun direkt im neuen Bearbeitungsprozess pflegen.' },
    ],
  },
  {
    version: 'v1.14.88',
    date: '5. Mai 2026',
    changes: [
      { type: 'feature', text: 'Unified Property Process: Grundgerüst der neuen Immobilien-Bearbeitungsseite mit fünf Tabs (Grunddaten, Objekt, Preis, Medien, Übersicht), Rollen-Erkennung (Verkäufer sieht eigene Tab-Auswahl) und Auto-Save unter /sales/properties/:id/unified.' },
      { type: 'improvement', text: 'Neue Hooks usePropertyForm und usePropertyAutoSave als Basis für die Vereinheitlichung von Erfassung und Bearbeitung — Feldnamen (latitude/longitude als Zahl, usable_area_sqm) sind jetzt deckungsgleich mit dem Backend-Schema.' },
    ],
  },
  {
    version: 'v1.14.87',
    date: '5. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Immobilien-Bearbeitungsseite: Sticky-Header neu sortiert — Auftraggeber-Pill steht neu links bei Titel und KI-Badge, Speichern und "Zur Prüfung einreichen" sind rechts gruppiert mit klarer Hierarchie (Einreichen ist primär blau im DRAFT-Status, sonst ist Speichern primär).' },
      { type: 'feature', text: 'KI-Extraktions-Banner ist neu einklappbar: kompakte Kopfzeile mit Konfidenz-Badge und Anzahl Hinweise, ausklappbar per Klick — Hinweise sind standardmässig ausgeblendet, um den Fokus auf die Bearbeitung zu lenken.' },
    ],
  },
  {
    version: 'v1.14.86',
    date: '5. Mai 2026',
    changes: [
      { type: 'improvement', text: 'AUFTRAG Implementation: Issue erstellen + Implementation starten --- Vollständi' },
    ],
  },
  {
    version: 'v1.14.85',
    date: '5. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Konzept-Dokument für das Exposé-Redesign (Crowdhouse-Style als Referenz) ergänzt: Sitemap, Layout-Skizze (Desktop + Mobile), Komponentenkarte, Bildmaterial-Plan und i18n-Schlüsselplan unter docs/expose-redesign-concept.md zur Freigabe.' },
    ],
  },
  {
    version: 'v1.14.84',
    date: '4. Mai 2026',
    changes: [
      { type: 'feature', text: 'Auftraggeber-Sticky-Header neu auch in der Immobilien-Bearbeitungsseite (Admin/Sales): Firma und Status-Badge sind jederzeit sichtbar, ein Klick öffnet den Auftraggeber-Drawer mit allen Details.' },
      { type: 'improvement', text: 'Auftraggeber im Immobilien-Wizard ist neu optional: Sie können den Eintrag jederzeit über den Sticky Header der Bearbeitungsseite nachpflegen — die Folge-Schritte sind nicht mehr gesperrt.' },
    ],
  },
  {
    version: 'v1.14.83',
    date: '4. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Vereinheitlichung der Immobilien-Erfassung (Wizard ↔ Master): Highlights werden jetzt im selben JSON-Chip-Format gespeichert — Daten aus dem Wizard erscheinen verlustfrei im Master-Editor und auf dem Teaser. Defaults und maximale Anzahl (5) sind in einer gemeinsamen Konstante definiert.' },
      { type: 'feature', text: 'Wizard (Admin/Sales): Neuer Bereich "Interne Angaben" im Schritt Vermarktung mit Sales-Verantwortlicher (Dropdown), Ablaufdatum des Inserats und Erinnerung vor Ablauf — gleiche Felder wie im Admin-Bearbeiten.' },
      { type: 'fix', text: 'Wizard speichert beim Erstellen nur noch die Master-Sprache (Deutsch) statt denselben Text in alle vier Sprachen zu klonen. Echte Übersetzungen werden danach im Side-by-Side-Editor des Master-Bearbeiten erfasst.' },
      { type: 'improvement', text: 'Admin/Sales-Bearbeiten: KI-Extraktions-Banner (Konfidenz + Hinweise) wird oberhalb der Grunddaten angezeigt, wenn Werte aus der KI-Erfassung stammen.' },
    ],
  },
  {
    version: 'v1.14.82',
    date: '4. Mai 2026',
    changes: [
      { type: 'improvement', text: 'Gap-Analyse Property-Prozesse (docs/property-flows-gap-analysis.md): Dokumentiert die Unterschiede zwischen Admin-Bearbeiten (Master), Wizard und Verkäufer-Sichten — inkl. fehlender Felder (Finanzkennzahlen, Mieterspiegel, Wohnungsmix, Übersetzungs-Editor) und Folge-Issues für die UX/Entwicklung.' },
    ],
  },
  {
    version: 'v1.14.81',
    date: '4. Mai 2026',
    changes: [
      { type: 'feature', text: 'Neuer Pflicht-Schritt "Auftraggeber" am Anfang des Immobilien-Wizards: CRM-Kontakt auswählen oder neu erfassen, Vermarktungsauftrag-Typ wählen (Alleinauftrag / Mehrfachauftrag / Schätzung). Weitere Schritte sind erst freigeschaltet, wenn beide Felder erfasst sind.' },
    ],
  },
  {
    version: 'v1.14.80',
    date: '4. Mai 2026',
    changes: [
      { type: 'feature', text: 'Auftraggeber-Sticky-Header in der Immobilien-Detailseite: Auftraggeber-Name und Status-Badge (grün/gelb/rot) sind auf jedem Tab sichtbar' },
      { type: 'feature', text: 'Auftraggeber-Drawer zeigt jetzt Firma, Hauptkontakt, Vertragsdetails (Nummer, Typ, Unterzeichnung, Ablauf) und Provision' },
      { type: 'improvement', text: 'Quick-Actions Anrufen und E-Mail im Auftraggeber-Drawer sind klickbar wenn Telefon/E-Mail vorhanden' },
    ],
  },
  {
    version: 'v1.14.79',
    date: '4. Mai 2026',
    changes: [
      { type: 'feature', text: 'Auftraggeber-Drawer in der Immobilien-Detailseite: Klick auf "Auftraggeber" im Sticky-Header öffnet einen Slide-In mit Kontakt, Vermarktungsauftrag, Verkäufer-Wünschen und Quick-Actions' },
    ],
  },
  {
    version: 'v1.14.78',
    date: '30. April 2026',
    changes: [
      { type: 'fix', text: 'Immobilien-Übersicht: KPI-Kacheln zeigen jetzt dieselben konfigurierbaren Highlights wie der Investor-Teaser (eine gemeinsame Komponente statt zwei separate)' },
      { type: 'fix', text: 'Stockwerke-Kachel zeigte fälschlicherweise Anzahl Gebäude statt Anzahl Etagen' },
      { type: 'fix', text: 'Marktwert wird als Gesamtpreis übernommen wenn kein Kaufpreis vorhanden' },
    ],
  },
  {
    version: 'v1.14.77',
    date: '30. April 2026',
    changes: [
      { type: 'fix', text: 'GWR Gebäuderegister-Link korrigiert: Verwendet jetzt EGID (Gebäude-ID) statt EGRID (Grundstück-ID) und die korrekte URL' },
      { type: 'improvement', text: 'KI-Objekterfassung: Grundriss-Beschreibungen (Untergeschoss, Erdgeschoss etc.) werden jetzt prominenter angezeigt' },
    ],
  },
  {
    version: 'v1.14.75',
    date: '30. April 2026',
    changes: [
      { type: 'fix', text: 'KI-Objekterfassung: Bildvorschau funktioniert jetzt korrekt — Nginx-Proxy-Priorität für /api/ Routen behoben, sodass Bild-URLs nicht mehr als statische Dateien behandelt werden' },
    ],
  },
  {
    version: 'v1.14.74',
    date: '30. April 2026',
    changes: [
      { type: 'feature', text: 'Property-Wizard «Objektmerkmale» / «Erweiterte Angaben»: Feld «Ausnützungsziffer (AZ)» ergänzt — Verkäufer können diesen Wert nun direkt im Wizard erfassen, identisch zur Admin-Ansicht. Damit gehen beim Abgleich/Übernahme keine Daten mehr verloren.' },
    ],
  },
  {
    version: 'v1.14.73',
    date: '30. April 2026',
    changes: [
      { type: 'fix', text: 'Property-Wizard «Objektmerkmale»: Verkäufer sehen jetzt denselben Block «Erweiterte Angaben» (Etagen, Aufzug, Parkplätze, Energieklassen) wie Admin/Sales — gleiche Felder, gleiche Validierung, keine Daten gehen beim Abgleich mehr verloren.' },
    ],
  },
  {
    version: 'v1.14.72',
    date: '30. April 2026',
    changes: [
      { type: 'fix', text: 'Property-Wizard: Tab-Status zeigt nicht mehr fälschlich Haken ohne Eingaben. Optionale Schritte (KI-Upload, Rechtliches, Medien) werden als «Optional» (leerer Kreis) markiert, solange keine Daten erfasst sind; Pflicht-Schritte (Basisdaten, Objektmerkmale, Preis) bleiben echte Vollständigkeits-Indikatoren.' },
    ],
  },
  {
    version: 'v1.14.71',
    date: '30. April 2026',
    changes: [
      { type: 'improvement', text: 'Property-Wizard «Preis & Vermarktung»: Validierung vereinheitlicht — für alle Rollen (Admin, Sales, Verkäufer) ist nur noch der Angebotspreis Pflicht; der Wunschpreis ist überall optional. Identische Interaktion zwischen Admin- und Verkäufer-Ansicht.' },
    ],
  },
  {
    version: 'v1.14.70',
    date: '30. April 2026',
    changes: [
      { type: 'improvement', text: 'Property-Wizard «Preis & Vermarktung»: Wunschpreis, Timeline und Anmerkungen sind jetzt für alle Rollen sichtbar und werden einheitlich gespeichert — Admin- und Verkäufer-Ansicht zeigen identische Felder.' },
    ],
  },
  {
    version: 'v1.14.69',
    date: '30. April 2026',
    changes: [
      { type: 'improvement', text: 'Wizard-Navigation (Immobilie erfassen): Aktiver Schritt wird durch zusätzlichen Ring und leichte Vergrösserung deutlicher hervorgehoben — Reihenfolge und aktueller Schritt jederzeit klar erkennbar.' },
    ],
  },
  {
    version: 'v1.14.68',
    date: '30. April 2026',
    changes: [
      { type: 'improvement', text: 'Wizard-Navigation (Immobilie erfassen): Tabs umbrechen jetzt auf zwei Zeilen statt horizontal zu scrollen — alle 7 Schritte sind sichtbar, Labels werden auch auf Mobile angezeigt.' },
    ],
  },
  {
    version: 'v1.14.67',
    date: '30. April 2026',
    changes: [
      { type: 'fix', text: 'KI-Objekterfassung: Wenn die Extraktion aller Dokumente fehlschlägt, wird der Job nicht mehr fälschlich als erfolgreich angezeigt — der Wizard zeigt jetzt eine Fehlermeldung mit Erneut-versuchen-Button.' },
    ],
  },
  {
    version: 'v1.14.66',
    date: '30. April 2026',
    changes: [
      { type: 'improvement', text: 'Dev C&C: Granulare Step-Progress und Live-Logs direkt in der Task-Liste und Task-Detail-Ansicht (Test-Fortschritt, Claude-Activity, ETA)' },
    ],
  },
  {
    version: 'v1.14.65',
    date: '30. April 2026',
    changes: [
      { type: 'feature', text: 'Verkäufer-Ansicht: Erweiterte Angaben (Anzahl Etagen, Energieklasse Hülle/Gesamt) sowie "Auftrag verloren"-Kennzeichnung sind jetzt sichtbar und editierbar' },
    ],
  },
  {
    version: 'v1.14.64',
    date: '30. April 2026',
    changes: [
      { type: 'feature', text: 'Wizard zur Objekterfassung: Tabs sind jederzeit frei klickbar; pro Tab zeigt ein grüner Haken bzw. ein graues X die Vollständigkeit an. Speichern wird erst freigegeben, wenn alle Pflichtfelder gesetzt sind.' },
    ],
  },
  {
    version: 'v1.14.63',
    date: '29. April 2026',
    changes: [
      { type: 'feature', text: 'KI-Objekterfassung: Upload akzeptiert nun zusätzlich Bilder (JPEG/PNG/WebP/GIF), Excel (.xlsx/.xls) und Word (.docx/.doc) neben PDF und E-Mail' },
    ],
  },
  {
    version: 'v1.14.62',
    date: '29. April 2026',
    changes: [
      { type: 'improvement', text: '[Split-WEB] KI-Objekterfassung: Upload-Allow-List in API + Frontend erweitern um Bilder (JPE' },
    ],
  },
  {
    version: 'v1.14.61',
    date: '29. April 2026',
    changes: [
      { type: 'feature', text: 'KI-Jobs: Pro Job aufklappbarer Pipeline-Stepper (Upload, Klassifizierung, Extraktion, Validierung, Persistierung) plus Live-Log-Konsole mit Auto-Scroll (2s-Polling)' },
    ],
  },
  {
    version: 'v1.14.60',
    date: '29. April 2026',
    changes: [
      { type: 'improvement', text: 'CRM-Organisationen: Filterleiste auf 3-Zeilen-Layout umgestellt (analog Kontakte) mit aktiven Filter-Chips und Reset-Link' },
    ],
  },
  {
    version: 'v1.14.59',
    date: '29. April 2026',
    changes: [
      { type: 'fix', text: 'Kampagnen-Liste: Trailing-Slash ergaenzt, verhindert 307-Redirect mit Auth-Header-Verlust' },
    ],
  },
  {
    version: 'v1.14.58',
    date: '29. April 2026',
    changes: [
      { type: 'improvement', text: 'Unified Property Wizard: PropertyEditPage Refactoring' },
    ],
  },
  {
    version: 'v1.14.57',
    date: '29. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Dev C&C: Task-Fortschritt zeigt nach Reset korrekt den neuen Stand statt den alten (14/14) — Reset-Filter erkennt task_reset und task_reset_boundary in TaskListPanel und ArchivePanel.',
      },
    ],
  },
  {
    version: 'v1.14.56',
    date: '29. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Property-Wizard Cleanup: Alte Sales- und Seller-Wizard-Dateien entfernt — der Unified Property Wizard ist nun die einzige Wizard-Implementierung.',
      },
    ],
  },
  {
    version: 'v1.14.55',
    date: '29. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Unified Property Wizard: Neuer 7-Step Wizard ersetzt Sales- und Seller-Wizard mit rollenbasierter Feldanzeige (KI-Upload, Basisdaten, Objektmerkmale, Rechtliches, Preis & Vermarktung, Medien, Zusammenfassung).',
      },
    ],
  },
  {
    version: 'v1.14.54',
    date: '29. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Property-Wizard: Frontend-Types und Konstanten an die bereinigte API angeglichen — Ghost-Felder (fee_percentage, due_diligence_period_days, earnest_money_deposit) entfernt, Condition-Werte (EXCELLENT, RENOVATION_NEEDED, GUTTED) und Status-Werte (IN_REVIEW, APPROVED) aktualisiert, sellerNotes hinzugefügt.',
      },
    ],
  },
  {
    version: 'v1.14.52',
    date: '27. April 2026',
    changes: [
      {
        type: 'improvement',
        text: '@types/node auf Version 25.6.0 aktualisiert (Major-Update, dev-only Typdefinitionen für Node 25 — keine Runtime-Änderung).',
      },
    ],
  },
  {
    version: 'v1.14.51',
    date: '27. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'KI-Objekterfassung: 422-Fehler beim Datei-Upload behoben — Multipart-Uploads (Datei-Staging, Property-Upload, Batch-Upload, Supplement) senden jetzt den korrekten Content-Type, sodass FastAPI die Dateien wieder einlesen kann.',
      },
    ],
  },
  {
    version: 'v1.14.50',
    date: '27. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'KI-Objekterfassung: Weisse Seite beim Ordner-Upload behoben — FastAPI-Validierungsfehler (422) werden jetzt als lesbare Texte angezeigt statt React zum Absturz zu bringen.',
      },
    ],
  },
  {
    version: 'v1.14.49',
    date: '27. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Node-Dependencies aktualisiert (Minor-Updates): @playwright/test, @tanstack/react-query, @types/google.maps, @typescript-eslint, @vis.gl/react-google-maps, autoprefixer, axios, eslint-plugin-react-refresh, i18next-browser-languagedetector, postcss, prettier, react-hook-form, react-hot-toast, recharts, workbox-window.',
      },
    ],
  },
  {
    version: 'v1.14.48',
    date: '27. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Build: vite-plugin-pwa auf v1.2.0 aktualisiert (Major-Update) — verbesserte Service-Worker-Generierung und aktualisierte Workbox-Integration.',
      },
    ],
  },
  {
    version: 'v1.14.47',
    date: '27. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'KI-Objekterfassung: Fehler in der Seite führen nicht mehr zu einer weissen Seite — eine ErrorBoundary umschliesst die Seite und zeigt eine Fehlermeldung mit "Erneut versuchen"-Button.',
      },
    ],
  },
  {
    version: 'v1.14.46',
    date: '27. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dashboard: KPI-Kacheln (Verkäufer/Investor) sind jetzt klickbar — Deep-Link in die jeweilige Objektliste mit vorbelegtem Status-Filter (Im Verkauf / Verkauft / In Vorbereitung).',
      },
    ],
  },
  {
    version: 'v1.14.45',
    date: '27. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'KI-Upload: Endlos-Schleife bei Verzeichnis-Upload behoben — fehlgeschlagene Dateien werden nicht mehr automatisch wiederholt; Dateien werden in Chunks von 25 hochgeladen.',
      },
    ],
  },
  {
    version: 'v1.14.44',
    date: '27. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev C&C: depends_on sichtbar — Amber Badge "wartet auf #X" bei pending Tasks + gestrichelte Abhängigkeitskante im Dependency-Graph.',
      },
    ],
  },
  {
    version: 'v1.14.43',
    date: '26. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev C&C: Agent-Steuerung — Agenten einzeln aktivieren/deaktivieren mit Power-Button und visuellem Status.',
      },
    ],
  },
  {
    version: 'v1.14.42',
    date: '26. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Wohnungsmix & Mieterspiegel: PUT-Endpoints auf korrekte Backend-Pfade /unit-mix und /rent-roll korrigiert (vorher mit /bulk-Suffix).',
      },
    ],
  },
  {
    version: 'v1.14.41',
    date: '26. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Investor-Favoriten: Notification-Update-Endpoint auf korrekte HTTP-Methode PUT korrigiert (vorher PATCH /api/investor/favorites/{id}/notifications → 405 Method Not Allowed).',
      },
    ],
  },
  {
    version: 'v1.14.40',
    date: '26. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Investor-Dossier-Download: Endpoint auf korrekten Backend-Pfad /dossier-pdf korrigiert (vorher /dossier/download → 404).',
      },
    ],
  },
  {
    version: 'v1.14.39',
    date: '26. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Firmenbeziehungen: PATCH/DELETE-Endpoints auf korrekten Backend-Pfad /relations/{id} korrigiert (vorher /links/{id} → 404).',
      },
    ],
  },
  {
    version: 'v1.14.38',
    date: '26. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev C&C: Housekeeping-Tab UX Overhaul — Expandierbare Task-Karten mit Beschreibung, Live-Log bei laufendem Agent, Report-Drill-Down mit Severity-Farben, neue Infrastructure-Kategorie.',
      },
    ],
  },
  {
    version: 'v1.14.37',
    date: '26. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev C&C: Housekeeping-Dashboard — Task-Registry mit Run/Toggle, aufklappbare Reports mit farbkodierten Findings, automatischer Dispatch bei leerer Queue.',
      },
    ],
  },
  {
    version: 'v1.14.36',
    date: '26. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev C&C: Sortierung in Haupt-TaskList und Archiv — Toggle zwischen gruppierter und chronologischer Ansicht, sortierbar nach Zeit/ID/Status.',
      },
      {
        type: 'feature',
        text: 'Dev C&C: Kill-Button mit Dropdown — 3 Optionen: Abbrechen (cancelled), Pausieren (waiting), Stoppen (failed/retry-fähig).',
      },
    ],
  },
  {
    version: 'v1.14.35',
    date: '26. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev C&C: Konsistenz-Check mit Opus — Fragen werden jetzt mit Opus 4.7 generiert und automatisch im Hintergrund beantwortet. Neuer "Neu prüfen"-Button.',
      },
      {
        type: 'feature',
        text: 'Dev C&C: Interaktive Prozess-Dokumentation — Jeder Schritt im ProcessOverview zeigt Details, I/O, Modell und Beispiele.',
      },
      {
        type: 'feature',
        text: 'Dev C&C: Visual Review Tab — Screenshots und GIFs aus Reviews direkt im Task-Detail sichtbar.',
      },
    ],
  },
  {
    version: 'v1.14.34',
    date: '26. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Objektdetailseite: Highlights-Grid rendert nur noch Kacheln mit echten Werten — keine grauen Platzhalter (—) mehr. Bei nur einem Wert wird die Kachel über die volle Breite angezeigt.',
      },
    ],
  },
  {
    version: 'v1.14.33',
    date: '26. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev C&C: Daemon-Management — Daemons können jetzt einzeln gestartet, gestoppt und neugestartet werden. Log-Viewer mit Auto-Refresh direkt im Dashboard.',
      },
    ],
  },
  {
    version: 'v1.14.32',
    date: '25. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Einstellungen → Benachrichtigungen: Verwaiste Übersetzung "Demnächst verfügbar" entfernt — die vollständige Benachrichtigungs-Verwaltung (E-Mail/Push pro Kategorie, Geräte-Registrierung) ist bereits aktiv.',
      },
    ],
  },
  {
    version: 'v1.14.31',
    date: '25. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Verkäufer-Wizard: Preis-Slider für Mindest- und Wunschpreis ergänzt (CHF 100\u2019000 bis CHF 10\u2019000\u2019000, Schrittweite CHF 50\u2019000) — feinere und schnellere Preiseingabe direkt per Slider.',
      },
      {
        type: 'improvement',
        text: 'Provisions-Slider im Vertrags-Wizard: Schrittweite von 0.05% auf 0.01% reduziert — präzisere Werteinstellung für Käufer-, Verkäufer- und Makler-Provision.',
      },
    ],
  },
  {
    version: 'v1.14.30',
    date: '25. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Changelog-Tests von hardcoded Versionen befreit — Tests brechen nicht mehr bei jedem Version-Bump.',
      },
    ],
  },
  {
    version: 'v1.14.29',
    date: '25. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Objektdetailseite: Highlights-Sektion ist jetzt responsiv mit auto-fit-Layout — die Kacheln verteilen sich automatisch über die verfügbare Breite und passen sich an Desktop- und Mobile-Ansichten an.',
      },
    ],
  },
  {
    version: 'v1.14.28',
    date: '25. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Investor-Ansicht (Käufer/Verkäufer): Aktivität "Neue Anfrage erhalten" (INQUIRY_RECEIVED) wird jetzt mit lokalisiertem Text und Glocken-Icon angezeigt — passend zum API-Fix, der Investoren mit eigenen Objekten auch Verkäufer-Aktivitäten ausliefert.',
      },
    ],
  },
  {
    version: 'v1.14.27',
    date: '25. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev Dashboard: Update & Restart Button in der StatusBar — Autopilot mit einem Klick aktualisieren und neu starten.',
      },
    ],
  },
  {
    version: 'v1.14.26',
    date: '24. April 2026',
    changes: [
      { type: 'improvement', text: '[Split-WEB] CRM Kontakte Geschlecht-Anzeige verifizieren: Logik für Speicherung und Anzeige' },
    ],
  },
  {
    version: 'v1.14.25',
    date: '24. April 2026',
    changes: [
      { type: 'fix', text: 'Passwort-Reset in den Profileinstellungen: Fehlermeldung "Investor Settings Profile..." durch saubere Übersetzungen ersetzt (currentPasswordWrong, hidePassword, showPassword, weakPassword) in allen vier Sprachen.' },
    ],
  },
  {
    version: 'v1.14.24',
    date: '24. April 2026',
    changes: [
      { type: 'fix', text: 'Dossier-Download in Firefox: Abbruch behoben durch verzögertes Freigeben der Blob-URL und rel="noopener" beim Download-Link' },
    ],
  },
  {
    version: 'v1.14.23',
    date: '24. April 2026',
    changes: [
      { type: 'improvement', text: '[Split-WEB] Bug / UX-Konzeptfehler: Push-Benachrichtigungen ohne hinterlegte Zustellmöglich' },
    ],
  },
  {
    version: 'v1.14.22',
    date: '24. April 2026',
    changes: [
      { type: 'improvement', text: '[Split-WEB] Bug Report / Auftrag: Passwort ändern in Einstellungen funktioniert fehlerhaft' },
    ],
  },
  {
    version: 'v1.14.21',
    date: '24. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Meine Aktivitäten: Aktivitätstypen (z. B. Immobilie erstellt, Angebot erstellt) werden nun in allen Sprachen korrekt übersetzt angezeigt statt als technischer Schlüssel.',
      },
      {
        type: 'improvement',
        text: 'Meine Aktivitäten: Neue Aktivitätstypen vorbereitet (Favorit, Angebot, Dokumente, Besichtigung, Vertrag, Profil, Nachricht) inkl. passender Icons pro Typ.',
      },
    ],
  },
  {
    version: 'v1.14.20',
    date: '24. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Meine Aktivitäten: Immobilien-Einträge im Aktivitätsfeed sind nun klickbar und führen direkt zur Detailseite der Immobilie bzw. des Listings.',
      },
    ],
  },
  {
    version: 'v1.14.19',
    date: '24. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'CRM-Organisationen: Branche als Mehrfach-Auswahl mit Chips. Im Recherche-Tab können nun mehrere Branchen ausgewählt oder frei erfasst werden, inkl. Vorschlagsliste bereits verwendeter Branchen. In der Übersicht ist zusätzlich ein Mehrfach-Filter auf Branchen verfügbar.',
      },
    ],
  },
  {
    version: 'v1.14.18',
    date: '24. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Deal-Timeline: Fehlende Übersetzungen ergänzt. «timeline.daysLeft» wurde bisher als Schlüssel statt als Text angezeigt; zudem waren die Aktivitäten-Überschrift und die Phasen-Labels in EN/FR/IT nicht übersetzt.',
      },
    ],
  },
  {
    version: 'v1.14.17',
    date: '24. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Deal-Timeline: Fehlende Übersetzungen ergänzt (Countdown-Badge, Aktivitäten-Zähler, Dringlichkeits-Banner) — keine rohen Keys und hardcoded deutschen Texte mehr.',
      },
    ],
  },
  {
    version: 'v1.14.16',
    date: '24. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Firmen-Verknüpfungen: Fehler beim Speichern behoben. Die Sektion «Verknüpfte Organisationen» nutzt jetzt den korrekten API-Endpunkt; die Art der Verknüpfung wird neu im Feld «Notiz» (Freitext, z. B. Tochterfirma, Schwesterfirma) erfasst.',
      },
    ],
  },
  {
    version: 'v1.14.15',
    date: '24. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'CRM-Organisationen: Neue Sektion «Verknüpfte Organisationen» auf der Firmen-Detailseite. Firmen lassen sich informativ verknüpfen (Mutter/Tochter, Partner, Schwestergesellschaft usw.), mit optionaler Notiz und Anzeige der gemeinsamen Kontakte pro Verknüpfung.',
      },
    ],
  },
  {
    version: 'v1.14.14',
    date: '24. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Kontakt-Detail → Firmen: «Firma hinzufügen» nutzt jetzt eine Suche statt eines Dropdowns. Bestehende Firmen lassen sich schneller finden und neue Organisationen können direkt per Zefix-Suche angelegt und dem Kontakt als Organisationen zugeordnet werden.',
      },
    ],
  },
  {
    version: 'v1.14.13',
    date: '24. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Kontakt-Recherche: Service-Layer für das Aktualisieren und Löschen einzelner Recherche-Findings (Personen, E-Mails, Telefonnummern) ergänzt — Vorbereitung für die UI-Bearbeitung von Treffern.',
      },
    ],
  },
  {
    version: 'v1.14.12',
    date: '24. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Einstellungen → Aktivitäten: Leerer Zustand und Fehlerzustand sind jetzt klar getrennt. Bei Fehler kann mit «Erneut laden» neu versucht werden, der leere Zustand zeigt einen Hinweis zum gewählten Zeitraum.',
      },
    ],
  },
  {
    version: 'v1.14.11',
    date: '23. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Verkäufer-Detailseite: Fehlermeldung zeigt jetzt HTTP-Status und Backend-Detail statt nur generischem Text – hilft beim Debuggen, warum eine Immobilie nicht geladen werden kann.',
      },
    ],
  },
  {
    version: 'v1.14.10',
    date: '23. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev Dashboard: Manuelle MRs (ohne Task) erscheinen in der MR/Pipeline-Navigation',
      },
      {
        type: 'improvement',
        text: 'Pre-Push Hook prueft Changelog-Format (korrekte Types, Felder, Versionsreihenfolge)',
      },
    ],
  },
  {
    version: 'v1.14.9',
    date: '23. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Passwort ändern: Präzise Fehlermeldungen unterscheiden jetzt zwischen "Aktuelles Passwort falsch" und "Neues Passwort zu schwach". Zusätzlich zeigt eine Passwort-Stärke-Anzeige live die Qualität des neuen Passworts an.',
        detail: 'Bisher wurde bei jedem Fehler beim Passwort ändern nur eine generische Fehlermeldung angezeigt. Jetzt wird klar unterschieden: Aktuelles Passwort falsch vs. neues Passwort zu schwach. Die Passwort-Stärke-Anzeige hilft zusätzlich, ein sicheres neues Passwort zu wählen.',
        translations: {
          en: {
            text: 'Change password: precise error messages now distinguish between "current password wrong" and "new password too weak". A password strength indicator shows the quality of the new password in real time.',
            detail: 'Previously every password change error showed a generic message. Now we clearly separate current password wrong vs. new password too weak. The password strength indicator also helps choose a secure new password.',
          },
          fr: {
            text: 'Changer le mot de passe : des messages d\'erreur précis distinguent désormais « mot de passe actuel incorrect » et « nouveau mot de passe trop faible ». Un indicateur de force du mot de passe affiche la qualité en temps réel.',
            detail: 'Auparavant, toutes les erreurs de changement de mot de passe affichaient un message générique. Maintenant nous distinguons clairement : mot de passe actuel incorrect vs. nouveau mot de passe trop faible. L\'indicateur de force aide à choisir un nouveau mot de passe sûr.',
          },
          it: {
            text: 'Cambia password: messaggi di errore precisi distinguono ora tra "password attuale errata" e "nuova password troppo debole". Un indicatore di forza della password mostra la qualità in tempo reale.',
            detail: 'In precedenza ogni errore nel cambio password mostrava un messaggio generico. Ora distinguiamo chiaramente: password attuale errata vs. nuova password troppo debole. L\'indicatore di forza aiuta a scegliere una nuova password sicura.',
          },
        },
      },
      {
        type: 'improvement',
        text: 'Zusätzliche /password-setup/:token Route als Alias für Passwort-Reset-Links, damit sowohl /auth/set-password/:token als auch /password-setup/:token konsistent funktionieren.',
        detail: 'Die Passwort-Reset-Links werden jetzt über beide Routen unterstützt. So bleiben ältere E-Mail-Links mit /password-setup/:token gültig und neue Links über /auth/set-password/:token funktionieren ebenfalls.',
        translations: {
          en: {
            text: 'Additional /password-setup/:token route as alias for password reset links, so both /auth/set-password/:token and /password-setup/:token work consistently.',
            detail: 'Password reset links are now supported via both routes. Older email links with /password-setup/:token remain valid while new links via /auth/set-password/:token also work.',
          },
          fr: {
            text: 'Route supplémentaire /password-setup/:token comme alias pour les liens de réinitialisation du mot de passe, afin que /auth/set-password/:token et /password-setup/:token fonctionnent tous les deux.',
            detail: 'Les liens de réinitialisation de mot de passe sont désormais pris en charge via les deux routes. Les anciens liens e-mail avec /password-setup/:token restent valides, tandis que les nouveaux liens via /auth/set-password/:token fonctionnent également.',
          },
          it: {
            text: 'Route aggiuntiva /password-setup/:token come alias per i link di reset password, in modo che sia /auth/set-password/:token sia /password-setup/:token funzionino.',
            detail: 'I link di reset password sono ora supportati tramite entrambe le route. I vecchi link e-mail con /password-setup/:token rimangono validi, mentre i nuovi link tramite /auth/set-password/:token funzionano anch\'essi.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.8',
    date: '23. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev Dashboard: MR-Tab zeigt offene Merge Requests mit Pipeline-Status',
      },
      {
        type: 'improvement',
        text: 'Dev Dashboard: Retry-Analyse zeigt jetzt Fehlerdetails pro Versuch',
      },
    ],
  },
  {
    version: 'v1.14.7',
    date: '23. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev Dashboard: Task-Konfiguration, Dependencies-Graph und Answer-Agent Anzeige',
        detail: 'Task-Parameter (Retries, Turns) direkt im Dashboard konfigurierbar. Dependencies-Graph funktioniert jetzt korrekt. Answer-Agents werden in der Statusleiste als "Frage" angezeigt.',
        translations: {
          en: {
            text: 'Dev Dashboard: task configuration, dependencies graph and answer agent display',
          },
          fr: {
            text: 'Dev Dashboard : configuration des taches, graphe des dependances et affichage des agents',
          },
          it: {
            text: 'Dev Dashboard: configurazione task, grafico dipendenze e visualizzazione agenti',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.6',
    date: '23. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev Dashboard: Live-Timer, Telegram-Kontext und Claude-Step-Anzeige',
        detail: 'Das Dev Command Center zeigt jetzt einen Live-Timer für laufende Tasks, den Telegram-Kontext (Reply-To + Konversation) und die aktuell laufende Claude-Aktivität (nicht nur die letzte abgeschlossene).',
        translations: {
          en: {
            text: 'Dev Dashboard: live timer, Telegram context and Claude step display',
            detail: 'The Dev Command Center now shows a live timer for running tasks, Telegram context (reply-to + conversation), and the currently running Claude activity (not just the last completed one).',
          },
          fr: {
            text: 'Dev Dashboard : minuteur en direct, contexte Telegram et affichage des etapes Claude',
            detail: 'Le Dev Command Center affiche desormais un minuteur en direct pour les taches en cours, le contexte Telegram (reponse + conversation) et l\'activite Claude en cours (pas seulement la derniere terminee).',
          },
          it: {
            text: 'Dev Dashboard: timer live, contesto Telegram e visualizzazione step Claude',
            detail: 'Il Dev Command Center mostra ora un timer live per i task in corso, il contesto Telegram (risposta + conversazione) e l\'attivita Claude attualmente in corso (non solo l\'ultima completata).',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.5',
    date: '23. April 2026',
    changes: [
      {
        type: 'fix',
        text: 'Verkäufer-KI-Upload: Fehlermeldung zeigt jetzt Status + Backend-Detail',
        detail: 'Im Verkäufer-Wizard wird bei einem fehlgeschlagenen KI-Upload jetzt der HTTP-Status und die Backend-Fehlermeldung (detail) im Toast angezeigt — damit werden Auth-/Format-Fehler sofort sichtbar statt nur generisch "fehlgeschlagen".',
        translations: {
          en: {
            text: 'Seller AI upload: error toast now shows status + backend detail',
            detail: 'In the seller wizard a failed AI upload now shows the HTTP status and backend detail message in the toast — so auth/format errors are immediately visible instead of a generic "failed".',
          },
          fr: {
            text: 'Upload IA vendeur : le toast d\'erreur affiche maintenant le statut + détail backend',
            detail: 'Dans l\'assistant vendeur, un échec de téléchargement IA affiche désormais le statut HTTP et le message détaillé du backend dans le toast — les erreurs d\'authentification/de format sont donc immédiatement visibles.',
          },
          it: {
            text: 'Upload IA venditore: il toast di errore ora mostra stato + dettaglio backend',
            detail: 'Nella procedura guidata venditore, in caso di fallimento del caricamento IA vengono ora mostrati lo stato HTTP e il messaggio di dettaglio del backend nel toast — gli errori di autenticazione/formato sono quindi immediatamente visibili.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.4',
    date: '23. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev C&C: Retry-Nummern, Claude-Tab, State Machine, UX',
        detail: 'Retry-Loop zeigt korrekte Nummern und Fehlerdetails. Claude-Tab mit Session-Kosten. State Machine zeigt Eingangsprozess (Frage, Aufgabe, Info). Graph-Positionen bleiben nach Verschieben erhalten.',
        translations: {
          en: {
            text: 'Dev C&C: Retry numbers, Claude tab, state machine, UX',
            detail: 'Retry loop shows correct numbering and error details. Claude tab with session costs. State machine shows input process (question, task, info). Graph positions persist after dragging.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.3',
    date: '23. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev C&C: Restart-Dropdown mit 3 Modi + Self-Review Retry',
        detail: 'Task-Neustart mit Split-Button: Resume ab Checkpoint, ab Implementation (Step 7) oder komplett neu. Self-Review (Step 10) hat jetzt 3 Versuche für Lint/Test-Fixes.',
        translations: {
          en: {
            text: 'Dev C&C: Restart dropdown with 3 modes + self-review retry',
            detail: 'Task restart with split button: Resume from checkpoint, from implementation (Step 7), or fresh start. Self-review (Step 10) now has 3 attempts for lint/test fixes.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.2',
    date: '23. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Property-Wizard: Preise-Schritt mit Slidern für Mindest- und Wunschpreis',
        detail: 'Mindestpreis und neuer Wunschpreis werden jetzt per Slider (CHF 0 bis 250+ Mio) eingegeben. Bei "Jährliche Mieteinnahmen" ist der Suffix "/a" entfernt, die Überschrift zeigt nun "(CHF)".',
        translations: {
          en: {
            text: 'Property wizard: pricing step with sliders for minimum and desired price',
            detail: 'Minimum price and a new desired price are now entered via sliders (CHF 0 to 250+ million). On "Annual rental income" the "/a" suffix was removed and the label now shows "(CHF)".',
          },
          fr: {
            text: 'Assistant de bien : étape des prix avec curseurs pour le prix minimum et le prix souhaité',
            detail: 'Le prix minimum et un nouveau prix souhaité se règlent désormais via des curseurs (de CHF 0 à 250+ millions). Sur « Revenus locatifs annuels », le suffixe « /a » a été supprimé et l\'intitulé affiche désormais « (CHF) ».',
          },
          it: {
            text: 'Wizard immobile: passo dei prezzi con slider per prezzo minimo e desiderato',
            detail: 'Il prezzo minimo e il nuovo prezzo desiderato vengono ora impostati tramite slider (da CHF 0 a oltre 250 milioni). In "Reddito locativo annuo" il suffisso "/a" è stato rimosso e l\'etichetta ora mostra "(CHF)".',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.1',
    date: '23. April 2026',
    changes: [
      {
        type: 'improvement',
        text: 'Dev C&C: Gruppierte Task-Liste, Diff-View, Prompt-Manager, Prozess-Übersicht',
        detail: 'Task-Liste nach Status gruppiert mit Fortschrittsbalken. Diff-View mit Syntax-Highlighting. Prompt-Templates editierbar. 29 Prozesse in Übersicht. Step-Hints für Pipeline. 15+ UX-Verbesserungen.',
        translations: {
          en: {
            text: 'Dev C&C: Grouped task list, diff view, prompt manager, process overview',
            detail: 'Task list grouped by status with progress bars. Diff view with syntax highlighting. Editable prompt templates. 29 processes in overview. Step hints for pipeline. 15+ UX improvements.',
          },
        },
      },
    ],
  },
  {
    version: 'v1.14.0',
    date: '22. April 2026',
    changes: [
      {
        type: 'feature',
        text: 'Dev Command & Control: Autopilot-Steuerungszentrale für Admins',
        detail: '3-Panel Layout mit Task-Liste, Detail-Tabs und System-Panel. 14-Schritte Pipeline mit expandierbarem Detail (Scripts, Events, Prompt-Archive). Retry-Loop Analyse, Universal Linking, SSE Real-time Events, Statistiken.',
        translations: {
          en: {
            text: 'Dev Command & Control: Autopilot control center for admins',
            detail: '3-panel layout with task list, detail tabs, and system panel. 14-step pipeline with expandable detail (scripts, events, prompt archives). Retry-loop analysis, universal linking, SSE real-time events, statistics.',
          },
        },
      },
    ],
  },
]
